import { brandedQrSvg, parseMLink } from "../qr";
import { checkFreshness, liveVersion } from "../freshness"; // v1.0.12
import { openScanner } from "../scanner";
import type { WalletAdapter } from "../adapters/WalletAdapter";
import type { WalletName } from "../api";
import { clearCredential } from "../credentialStore";
import { cardAbove, clearCard, goToEnrol, readCard, setCardAbove } from "../card"; // 1.5.42; 1.5.50 setCardAbove (synced)
import { IC_CARD, IC_RELEASE_DELAY, IC_SHIELD_CHECK, IC_TIMELOCK } from "../icons"; // 1.5.42; 1.5.59 — the Timelock's glyphs; 1.5.60 its shield
import { countdown, onTimelock, phaseOf, setChoice, setPosture, setTlTestFigures, setWords, TL_DEFAULT, TL_OPTIONS, tlAmountLabel, tlHoursLabel, tlNow, tlOnHere, tlPrimary, tlRefresh, tlStart, tlTestFigures, tlView, type TlSetUp } from "../timelock"; // 1.5.59; 1.5.60 the owner's choices
import { cancelTimelockChange, setTimelockPolicy } from "../api"; // 1.5.59
import { tellPasskeyName } from "../passkeyName";
import { refreshSparePrompt, sparePromptDue, sparePromptShown } from "../sparePrompt";
import { clearFlag, WELCOME_KEY } from "../deviceFlag";
import { titleCase } from "../money";
import { renderSendReceive } from "../sendReceiveFlow";
import { PENCIL, alsoNames, clearAlso, contactLabel, listNicks, openNameEditor, readNick, setNick } from "../nickname";
import { readRecent } from "../recentStore";
import { cachedProfile, rememberProfile, warmProfiles } from "../profileCache";
import { MONEY_QUESTION, greetingFor } from "../greeting";
import type { SendReceiveHandle, SendPrefill } from "../sendReceiveFlow";
import { getThemePref, setTheme, type ThemePref } from "../theme";
import { playDone } from "../sound";
import {
  BASE_CURRENCY,
  currency,
  activeCurrency,
  defaultCurrency,
  liveCurrencies,
  setSessionCurrency,
  enabledCurrencies,
  setDefaultCurrency,
  setCurrencyEnabled,
  notePocketUse,
  takeOpenPocket,
  CURRENCIES,
  formatFx,
  makePlaceholderFxSource,
  type FxSource,
} from "../fx";
import { renderSwapScreen, type SwapScreenHandle } from "../swapScreen";
import { MFxSource } from "../adapters/MFxSource";
import { IC_X, IC_SWAP_UD, IC_ADD, IC_COPY, IC_GLOBE, IC_CHEVRON_DOWN as IC_CHEV, IC_SCAN_M as IC_SCAN, IC_LOCK_M } from "../icons";
import { lastErrors, lastMeasures, recordError } from "../diag";
import { maskBalance, setMaskBalance, setPusher } from "../settings";

import { getEmoji, setEmoji, EMOJI_SET } from "../emoji";
import { ASK_KEY, REVERT_KEY, type PrevWallet } from "../walletSwitch";
import {
  deleteWallet,
  getFunding,
  getMyInvites,
  getWallet,
  markInviteShared,
  mintInvites,
  requestFunding,
  resolveName,
} from "../api";
import { dropSharePayload, shareOrCopy } from "../share";
import {
  formatCents,
  placeholderEarnSource,
  renderEarnSheet,
  type EarnSource,
} from "../earn";
import { TellerEarnSource } from "../adapters/TellerEarnSource";
import { isNewLabel, labelFor, listHeld, removeHeld, secretFor } from "../heldStore";
import { demoToken, dropCancelled, dropRest, dropUrl, getActivity, getDemoConfig, getDropConfig, getMyDrops, getPendingTx, getProfile, setProfile, type ActivityItem, type DropView, type PendingTx } from "../api";
import { emojiFor, getEmojiOverride } from "../emoji";
import { encodeFunctionData, stringToHex } from "viem";
import { formatDayNoYear } from "../money";
import { listSent, looksUntaken, noteLink, noteSource, reconcileSent, sentState, type SentState } from "../notes/notes";
import { formatCode as formatNoteCode } from "../notes/code";
import { formatSerial, parseSerial } from "../notes/serial";
import { formatUsdShort } from "../money";
import { LINK_ORIGIN } from "../linkOrigin";

// ---- inline icons -------------------------------------------------------
// Rising trend line with an arrow head — drawn to the SF grid convention
// (squared viewBox, ~10% padding, currentColor). v0.30 "Earning".
// SF Symbols arrow.down.left.arrow.up.right — the two-way transfer glyph.
const IC_ARROWS = `<svg viewBox="-24.9 -24.9 298.8 298.8" fill="currentColor" aria-hidden="true"><path d="M124.51,249.02c-17.17,0-33.29-3.23-48.34-9.71-15.06-6.47-28.28-15.4-39.67-26.79-11.39-11.39-20.32-24.62-26.79-39.67C3.24,157.8,0,141.68,0,124.51s3.24-33.28,9.7-48.34c6.47-15.05,15.4-28.3,26.79-39.73,11.39-11.43,24.62-20.36,39.67-26.79C91.23,3.22,107.34,0,124.51,0s33.28,3.22,48.34,9.64c15.05,6.43,28.28,15.36,39.67,26.79,11.39,11.43,20.32,24.68,26.79,39.73,6.47,15.06,9.7,31.17,9.7,48.34s-3.24,33.29-9.7,48.34c-6.47,15.06-15.4,28.28-26.79,39.67-11.39,11.39-24.62,20.33-39.67,26.79-15.06,6.47-31.17,9.71-48.34,9.71ZM124.51,228.27c14.32,0,27.75-2.69,40.28-8.06,12.53-5.37,23.56-12.82,33.08-22.34s16.97-20.55,22.34-33.08c5.37-12.53,8.06-25.96,8.06-40.28s-2.69-27.77-8.06-40.34-12.82-23.6-22.34-33.08c-9.52-9.48-20.55-16.91-33.08-22.28-12.53-5.37-25.96-8.06-40.28-8.06s-27.75,2.69-40.28,8.06c-12.53,5.37-23.56,12.8-33.08,22.28-9.52,9.48-16.97,20.51-22.34,33.08s-8.06,26.02-8.06,40.34,2.69,27.75,8.06,40.28c5.37,12.53,12.82,23.56,22.34,33.08s20.55,16.97,33.08,22.34c12.53,5.37,25.96,8.06,40.28,8.06ZM110.96,140.38c-1.46,1.63-3.34,2.44-5.62,2.44l-42.85.37c-2.28.08-4.27-.71-5.98-2.38-1.71-1.67-2.52-3.64-2.44-5.92l.61-42.85c0-.98.18-2.01.55-3.11.37-1.1.92-1.97,1.65-2.62,1.63-1.71,3.48-2.56,5.55-2.56s3.89.85,5.43,2.56c1.71,1.55,2.48,3.38,2.32,5.49l-.24,18.19-1.59,18.43,19.17-1.1,17.94-.24c2.28,0,4.15.77,5.62,2.32,1.63,1.63,2.42,3.46,2.38,5.49-.04,2.04-.88,3.87-2.5,5.49ZM140.62,56.52c1.63,1.55,2.46,3.4,2.5,5.55.04,2.16-.75,4.01-2.38,5.55l-56.03,56.15-9.77,8.91c-1.46,1.46-3.24,2.2-5.31,2.2s-3.85-.73-5.31-2.2-2.18-3.23-2.14-5.31.75-3.85,2.14-5.31l9.28-9.64,55.91-55.91c1.63-1.63,3.5-2.44,5.62-2.44s3.95.81,5.49,2.44ZM107.91,192.5c-1.55-1.54-2.34-3.38-2.38-5.49-.04-2.12.75-3.99,2.38-5.62l55.91-56.03,9.77-9.28c1.46-1.38,3.25-2.09,5.37-2.14,2.12-.04,3.91.67,5.37,2.14s2.18,3.26,2.14,5.37c-.04,2.12-.75,3.95-2.14,5.49l-9.16,9.64-56.03,56.03c-1.63,1.63-3.5,2.42-5.62,2.38-2.12-.04-3.99-.87-5.62-2.5ZM191.89,162.84c-1.63,1.63-3.46,2.46-5.49,2.5-2.04.04-3.91-.75-5.62-2.38-1.46-1.63-2.2-3.5-2.2-5.62l.37-18.07,1.1-19.04-18.68,1.59-17.94.24c-2.12.16-3.99-.57-5.62-2.2-1.63-1.71-2.44-3.58-2.44-5.62s.81-3.86,2.44-5.49c1.54-1.54,3.5-2.32,5.86-2.32l42.72-.49c2.2,0,4.15.84,5.86,2.5,1.71,1.67,2.56,3.6,2.56,5.8l-.49,42.97c0,2.12-.81,3.99-2.44,5.62Z"/></svg>`;
// 1.4.91 (Alexander, 23 Oct) — Wallet back to the original (the open wallet; 1.4.90's squircle wallet dropped), Grow the bars; both drawn as wide as Home's frame (the $ square, 227.91 units in its 267.91 box) and centred in the same box, so the three glyphs measure the same across.
const IC_WALLET = `<svg viewBox="-26.11 -48.33 349.70 349.70" fill="currentColor" aria-hidden="true"><path d="M38.94,253.02c-12.7,0-22.36-3.28-28.99-9.83S0,227.1,0,214.57v-126.59c0-10.42,2.28-18.68,6.84-24.78,4.56-6.1,11.64-10.66,21.24-13.67L182.37,3.15c10.25-3.09,19.27-3.91,27.04-2.44s13.83,5.11,18.19,10.93c4.35,5.82,6.53,13.69,6.53,23.62v24.05l-23.44,1.34v-27.34c0-5.13-1.49-8.69-4.46-10.68-2.97-1.99-7.18-2.18-12.63-.55l-87.4,24.41c-1.3.33-1.91.9-1.83,1.71.08.81.73,1.22,1.95,1.22h129.76c12.78,0,22.44,3.28,28.99,9.83,6.55,6.55,9.83,16.13,9.83,28.75v28.69h-24.05v-26.61c0-5.53-1.4-9.66-4.21-12.39-2.81-2.73-6.82-4.09-12.02-4.09H40.41c-5.29,0-9.32,1.36-12.08,4.09-2.77,2.73-4.15,6.86-4.15,12.39v122.31c0,5.53,1.38,9.66,4.15,12.39,2.77,2.73,6.79,4.09,12.08,4.09h194.21c5.21,0,9.22-1.36,12.02-4.09,2.81-2.73,4.21-6.86,4.21-12.39v-30.88h24.05v33.08c0,12.53-3.28,22.07-9.83,28.62s-16.22,9.83-28.99,9.83H38.94ZM224.98,191.99c-13.18,0-23.82-3.44-31.92-10.31-8.1-6.88-12.15-17.07-12.15-30.58s4.05-23.68,12.15-30.52,18.74-10.25,31.92-10.25h46.02c8.3,0,14.79,2.5,19.47,7.51s7.02,11.33,7.02,18.98v28.56c0,7.73-2.34,14.1-7.02,19.1s-11.17,7.51-19.47,7.51h-46.02ZM225.71,173.19h40.53c4.15,0,7.26-1.08,9.34-3.23,2.08-2.16,3.11-5.23,3.11-9.22v-19.29c0-3.99-1.04-7.04-3.11-9.16-2.07-2.12-5.19-3.17-9.34-3.17h-40.53c-8.22,0-14.45,1.93-18.68,5.8-4.23,3.87-6.35,9.26-6.35,16.17s2.12,12.43,6.35,16.3c4.23,3.87,10.46,5.8,18.68,5.8ZM225.71,163.91c-3.58,0-6.63-1.26-9.16-3.78-2.52-2.52-3.78-5.57-3.78-9.16s1.26-6.61,3.78-9.09c2.52-2.48,5.57-3.72,9.16-3.72s6.51,1.24,9.03,3.72c2.52,2.48,3.78,5.51,3.78,9.09s-1.26,6.63-3.78,9.16-5.54,3.78-9.03,3.78Z"/></svg>`;
// 1.4.85 — the Settings list's Appearance row: the half-filled circle the theme picker's Auto uses.
const IC_APPEARANCE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none"/></svg>`;
const themeLabel = (t: string): string => (t === "dark" ? "Midnight" : t === "auto" ? "Auto" : "Pure");
// 1.4.86 (Alexander, 22 Oct) — Settings in the dock: his Setting_new.svg, drawn on the same 249-unit circle as Grow's
// glyph and given Grow's view box, so at the dock's 33 px the two circles are the same size (27.5 px). (1.4.85: a plain gear.)
const IC_GEAR = `<svg viewBox="-24.9 -24.9 298.82 298.82" fill="currentColor" aria-hidden="true"><path d="M124.51,249.02c-17.17,0-33.29-3.23-48.34-9.71-15.06-6.47-28.28-15.4-39.67-26.79-11.39-11.39-20.32-24.62-26.79-39.67C3.24,157.8,0,141.68,0,124.51s3.24-33.28,9.7-48.34c6.47-15.05,15.4-28.3,26.79-39.73,11.39-11.43,24.62-20.36,39.67-26.79C91.23,3.22,107.34,0,124.51,0s33.28,3.22,48.34,9.64c15.05,6.43,28.28,15.36,39.67,26.79,11.39,11.43,20.32,24.68,26.79,39.73,6.47,15.06,9.7,31.17,9.7,48.34s-3.24,33.29-9.7,48.34c-6.47,15.06-15.4,28.28-26.79,39.67-11.39,11.39-24.62,20.33-39.67,26.79-15.06,6.47-31.17,9.71-48.34,9.71ZM124.51,228.27c14.32,0,27.75-2.69,40.28-8.06,12.53-5.37,23.56-12.82,33.08-22.34s16.97-20.55,22.34-33.08c5.37-12.53,8.06-25.96,8.06-40.28s-2.69-27.77-8.06-40.34-12.82-23.6-22.34-33.08c-9.52-9.48-20.55-16.91-33.08-22.28-12.53-5.37-25.96-8.06-40.28-8.06s-27.75,2.69-40.28,8.06c-12.53,5.37-23.56,12.8-33.08,22.28-9.52,9.48-16.97,20.51-22.34,33.08s-8.06,26.02-8.06,40.34,2.69,27.75,8.06,40.28c5.37,12.53,12.82,23.56,22.34,33.08s20.55,16.97,33.08,22.34c12.53,5.37,25.96,8.06,40.28,8.06ZM124.39,192.99h-2.32c-.73,0-1.46-.04-2.2-.12l-3.3,6.71c-1.38,2.28-3.42,3.22-6.1,2.81-2.61-.57-4.07-2.16-4.39-4.76l-.98-7.32c-1.46-.41-2.89-.85-4.27-1.34-1.38-.49-2.77-1.06-4.15-1.71l-5.37,5c-1.79,1.79-4.03,2.04-6.71.73-2.28-1.38-3.09-3.38-2.44-5.98l1.71-7.2c-1.22-.89-2.42-1.83-3.6-2.81s-2.3-1.99-3.36-3.05l-6.71,2.93c-2.36.98-4.56.41-6.59-1.71-1.71-2.03-1.83-4.15-.37-6.35l4.15-6.23c-.81-1.22-1.61-2.5-2.38-3.85-.77-1.34-1.49-2.7-2.14-4.09l-7.32.61c-2.44.16-4.31-1.14-5.62-3.91-.9-2.36-.24-4.39,1.95-6.1l5.86-4.52c-.33-1.38-.61-2.81-.85-4.27s-.45-2.93-.61-4.39l-7.2-2.32c-2.52-.81-3.78-2.56-3.78-5.25s1.26-4.52,3.78-5.25l7.2-2.32c.32-3.01.81-5.94,1.46-8.79l-5.86-4.39c-2.12-1.63-2.73-3.74-1.83-6.35.89-2.52,2.73-3.74,5.49-3.66l7.32.37c.65-1.3,1.36-2.6,2.14-3.91.77-1.3,1.57-2.56,2.38-3.78l-4.03-6.23c-1.55-2.2-1.38-4.39.49-6.59,1.71-2.03,3.82-2.52,6.35-1.46l6.84,2.81c1.06-1.06,2.16-2.05,3.3-2.99,1.14-.93,2.32-1.85,3.54-2.75l-1.71-7.2c-.57-2.52.37-4.56,2.81-6.1,2.36-1.3,4.47-1.06,6.35.73l5.37,5.13c1.38-.65,2.79-1.22,4.21-1.71,1.42-.49,2.87-.98,4.33-1.46l.85-7.2c.32-2.69,1.87-4.27,4.64-4.76,2.6-.41,4.56.53,5.86,2.81l3.3,6.71c.73-.08,1.46-.14,2.2-.18.73-.04,1.5-.06,2.32-.06.73,0,1.48.02,2.26.06.77.04,1.53.1,2.26.18l3.3-6.71c.65-1.14,1.48-1.93,2.5-2.38,1.02-.45,2.18-.59,3.48-.43,2.44.49,3.99,2.08,4.64,4.76l.73,7.2c1.46.41,2.91.9,4.33,1.46,1.42.57,2.83,1.14,4.21,1.71l5.25-5.13c.98-.89,2.05-1.36,3.23-1.4,1.18-.04,2.34.23,3.48.79,2.12,1.3,3.01,3.34,2.69,6.1l-1.83,7.08c1.22.9,2.4,1.83,3.54,2.81,1.14.98,2.28,2,3.42,3.05l6.71-3.05c1.22-.41,2.4-.47,3.54-.18,1.14.29,2.12.92,2.93,1.89,1.54,2.04,1.71,4.23.49,6.59l-4.15,6.1c.81,1.3,1.61,2.58,2.38,3.85.77,1.26,1.48,2.58,2.14,3.97l7.32-.61c1.3,0,2.44.37,3.42,1.1s1.67,1.67,2.08,2.81c.73,2.44.16,4.56-1.71,6.35l-6.1,4.39c.73,2.69,1.26,5.58,1.59,8.67l7.08,2.32c2.6.73,3.91,2.48,3.91,5.25s-1.3,4.44-3.91,5.25l-7.08,2.32c-.33,3.01-.85,5.9-1.59,8.67l5.98,4.52c2.2,1.71,2.77,3.83,1.71,6.35-.9,2.52-2.69,3.74-5.37,3.66l-7.32-.49c-.65,1.38-1.36,2.73-2.14,4.03-.77,1.3-1.61,2.56-2.5,3.78l4.15,6.23c1.54,2.28,1.38,4.48-.49,6.59-1.71,2.04-3.83,2.52-6.35,1.46l-6.84-2.81c-1.06,1.06-2.18,2.06-3.36,2.99-1.18.94-2.38,1.85-3.6,2.75l1.83,7.2c.57,2.61-.37,4.6-2.81,5.98-2.36,1.38-4.48,1.18-6.35-.61l-5.49-5.13c-1.38.65-2.79,1.22-4.21,1.71-1.42.49-2.87.94-4.33,1.34l-.73,7.32c-.33,2.69-1.87,4.27-4.64,4.76-2.61.41-4.56-.53-5.86-2.81l-3.42-6.71c-.73.08-1.46.12-2.2.12h-2.2ZM124.39,179.44c10.09,0,19.31-2.46,27.65-7.39,8.34-4.92,14.97-11.55,19.9-19.9,4.92-8.34,7.38-17.56,7.38-27.65s-2.46-19.41-7.38-27.71c-4.92-8.3-11.56-14.93-19.9-19.9-8.34-4.96-17.56-7.45-27.65-7.45s-19.41,2.48-27.71,7.45c-8.3,4.96-14.93,11.6-19.9,19.9-4.96,8.3-7.45,17.54-7.45,27.71s2.48,19.31,7.45,27.65c4.96,8.34,11.6,14.97,19.9,19.9,8.3,4.92,17.54,7.39,27.71,7.39ZM121.7,141.36l-25.39,41.26-10.5-6.35,25.27-41.14,10.62,6.23ZM110.96,113.89l-24.41-41.63,10.74-5.98,24.41,41.63-10.74,5.98ZM124.39,144.04c-5.37,0-9.97-1.91-13.79-5.74-3.83-3.82-5.74-8.42-5.74-13.79s1.91-9.97,5.74-13.79,8.42-5.74,13.79-5.74,9.97,1.91,13.79,5.74c3.82,3.83,5.74,8.42,5.74,13.79s-1.91,9.97-5.74,13.79-8.42,5.74-13.79,5.74ZM124.39,131.59c1.95,0,3.62-.69,5-2.08s2.08-3.05,2.08-5-.69-3.62-2.08-5c-1.38-1.38-3.05-2.08-5-2.08s-3.62.69-5,2.08-2.08,3.05-2.08,5,.69,3.62,2.08,5c1.38,1.38,3.05,2.08,5,2.08ZM134.28,118.41h54.08v12.08h-54.08v-12.08Z"/></svg>`;
// 1.4.85 — Circle's logo (his CRCL.svg), top right of the Wallet sheet: the wallet on Circle Arc. Its own gradients, ids made unique.
const IC_CIRCLE = `<svg viewBox="0 0 1509 1498" aria-hidden="true"><defs><linearGradient id="crclG1" x2="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(1054.384,-1061.014,532.835,529.505,488.611,1545.699)"><stop offset="0" stop-color="#b090f5"/><stop offset="1" stop-color="#5fbfff"/></linearGradient><linearGradient id="crclG2" x2="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(1271.271,0,0,1194.158,0,597.005)"><stop offset="0" stop-color="#68d7fa"/><stop offset="1" stop-color="#7ef1b3"/></linearGradient></defs><path fill="url(#crclG1)" d="m1409.3 377.5l-32.7-56.7c-2.6-4.4-6.1-8.2-10.4-11.1-4.3-2.8-9.1-4.7-14.3-5.3-5.1-0.7-10.3-0.2-15.1 1.4-4.9 1.7-9.3 4.4-13 8l-74.7 74.2c-5.5 5.4-8.9 12.6-9.6 20.3-0.8 7.7 1.1 15.4 5.4 21.8 27.3 41.4 49.2 86.1 65.1 133 29.6 87.6 37.9 181 24.1 272.4-13.9 91.4-49.4 178.2-103.7 253.3-54.3 75-125.8 136.2-208.6 178.4-82.8 42.2-174.5 64.3-267.6 64.4-95.8 0.2-190.3-23-274.9-67.8l126.6-125.7c63.4 23.8 131.7 32 199 23.9 67.3-8.2 131.6-32.4 187.4-70.7 55.8-38.2 101.4-89.4 132.9-149 31.5-59.6 47.9-125.9 47.9-193.3q0-21.6-2.4-43c-4.9-48.1-18.3-94.9-39.5-138.3-2.3-4.7-5.8-8.8-10.1-12-4.2-3.1-9.2-5.2-14.4-6.1-5.2-0.8-10.6-0.5-15.7 1.1-5 1.6-9.6 4.4-13.4 8.1l-75.8 75.3c-4.1 3.9-7 8.9-8.6 14.3-1.5 5.5-1.7 11.2-0.4 16.8l6.4 27c11.2 47.3 8.2 96.8-8.5 142.5-16.8 45.6-46.6 85.5-85.8 114.6-39.2 29-86.1 46.1-134.9 49.1-48.9 3-97.5-8.3-140-32.4l-33.4-18.9c-6.3-3.7-13.7-5.1-21-4.2-7.3 1-14.1 4.3-19.4 9.4l-309.2 306.8c-3.4 3.3-6 7.4-7.7 11.8-1.7 4.4-2.4 9.2-2 13.9 0.3 4.7 1.6 9.3 3.8 13.5 2.3 4.2 5.4 7.8 9.2 10.7l45.5 34.7c131.4 100.5 292.6 154.8 458.4 154.3 415.9 0 754.2-336 754.2-749 0.1-130.3-34.1-258.3-99.1-371.5z"/><path fill="url(#crclG2)" d="m1212.6 154.3c-131.3-100.5-292.6-154.8-458.4-154.3-415.9 0-754.2 336-754.2 749 0 130.3 34.2 258.4 99.2 371.5l32.7 56.7c2.5 4.5 6.1 8.4 10.4 11.2 4.3 2.9 9.2 4.8 14.3 5.5 5.2 0.6 10.4 0.1 15.3-1.6 5-1.6 9.4-4.4 13-8.1l74.7-74.1c5.5-5.5 8.9-12.7 9.7-20.4 0.7-7.7-1.2-15.4-5.5-21.8-27.3-41.4-49.2-86.1-65.1-132.9-29.6-87.6-37.9-181-24.1-272.4 13.8-91.4 49.3-178.2 103.6-253.3 54.3-75 125.7-136.2 208.5-178.5 82.8-42.2 174.5-64.3 267.5-64.4 95.9-0.2 190.3 23.1 275 67.8l-126.8 125.7c-63.4-23.8-131.7-32-199-23.9-67.3 8.2-131.6 32.4-187.4 70.7-55.8 38.3-101.4 89.4-132.9 149-31.5 59.7-47.9 126-47.9 193.3 0 6.9 1.9 38.5 2.5 43.9 5.1 47.7 18.4 94.2 39.4 137.4 2.4 4.8 5.8 9 10.1 12.2 4.3 3.2 9.3 5.3 14.6 6.2 5.3 0.9 10.7 0.5 15.8-1.1 5.1-1.6 9.7-4.4 13.5-8.2l75.8-75.2c4.1-4 7-9 8.6-14.5 1.6-5.5 1.7-11.3 0.3-16.8l-6.2-26.8c-4.4-18.7-6.7-37.9-6.7-57.1 0-43.7 11.6-86.6 33.5-124.4 22-37.9 53.5-69.4 91.6-91.3 38-21.9 81.2-33.6 125.2-33.7 44-0.2 87.3 11.2 125.5 32.8l33.4 19c6.3 3.6 13.7 5.1 21 4.2 7.3-1 14.1-4.3 19.3-9.5l309-306.9c3.4-3.3 6-7.4 7.7-11.8 1.7-4.4 2.4-9.2 2.1-13.9-0.3-4.7-1.6-9.3-3.9-13.5-2.2-4.2-5.4-7.9-9.1-10.8z"/></svg>`;
const IC_LOGOUT = `<svg viewBox="-39.45 -25.84 310.1 310.1" fill="currentColor" aria-hidden="true"><path d="M156.86,161.74v64.94c0,9.52-3.05,17.19-9.16,23.01s-14.12,8.73-24.05,8.73H33.2c-9.93,0-17.94-2.91-24.05-8.73S0,236.21,0,226.68V31.74C0,22.22,3.05,14.55,9.16,8.73,15.26,2.91,23.27,0,33.2,0h90.45c9.93,0,17.94,2.91,24.05,8.73,6.1,5.82,9.16,13.49,9.16,23.01v64.82h-19.65v-61.65c0-4.88-1.38-8.65-4.15-11.29-2.77-2.64-6.71-3.97-11.84-3.97H35.77c-5.21,0-9.2,1.32-11.96,3.97-2.77,2.65-4.15,6.41-4.15,11.29v188.6c0,4.88,1.38,8.65,4.15,11.29,2.77,2.64,6.75,3.97,11.96,3.97h85.45c5.13,0,9.07-1.32,11.84-3.97,2.77-2.64,4.15-6.41,4.15-11.29v-61.77h19.65ZM78.74,129.15c0-2.6.93-4.88,2.81-6.84,1.87-1.95,4.11-2.93,6.71-2.93h97.9l32.84,1.59c2.28.08,4.21.9,5.8,2.44,1.59,1.55,2.38,3.46,2.38,5.74s-.79,4.09-2.38,5.68c-1.59,1.59-3.52,2.42-5.8,2.5l-32.84,1.46h-97.9c-2.61,0-4.84-.96-6.71-2.87-1.87-1.91-2.81-4.17-2.81-6.77ZM174.07,87.77c0-2.6.81-4.72,2.44-6.35s3.7-2.44,6.23-2.44c2.28,0,4.39.94,6.35,2.81l38.82,40.28c1.22,1.22,2.08,2.4,2.56,3.54.49,1.14.73,2.32.73,3.54s-.24,2.4-.73,3.54c-.49,1.14-1.34,2.32-2.56,3.54l-38.82,40.16c-1.95,1.95-4.07,2.93-6.35,2.93-2.52,0-4.6-.85-6.23-2.56-1.63-1.71-2.44-3.82-2.44-6.35,0-2.69.98-4.88,2.93-6.59l20.14-18.92,16.85-15.75-16.85-15.87-20.14-18.8c-1.95-1.71-2.93-3.95-2.93-6.71Z"/></svg>`;
// v0.70.12 — calendar (SF Symbol Light, supplied 13 Sep)
const IC_CALENDAR = `<svg viewBox="0 0 241.7 220.95" fill="currentColor" aria-hidden="true"><path d="M35.89,220.95c-11.88,0-20.83-2.99-26.86-8.97-6.02-5.98-9.03-14.87-9.03-26.67V35.64C0,23.85,3.01,14.95,9.03,8.97,15.05,2.99,24.01,0,35.89,0h169.92c11.96,0,20.93,2.99,26.92,8.97,5.98,5.98,8.97,14.87,8.97,26.67v149.66c0,11.72-2.99,20.59-8.97,26.61-5.98,6.02-14.95,9.03-26.92,9.03H35.89ZM34.91,205.93h171.88c6.35,0,11.25-1.69,14.71-5.07,3.46-3.38,5.19-8.36,5.19-14.95v-111.69c0-6.59-1.73-11.58-5.19-14.95-3.46-3.38-8.36-5.07-14.71-5.07H34.91c-6.43,0-11.35,1.69-14.77,5.07-3.42,3.38-5.13,8.36-5.13,14.95v111.69c0,6.59,1.71,11.58,5.13,14.95,3.42,3.38,8.34,5.07,14.77,5.07ZM55.3,138.43c-1.95,0-3.26-.32-3.91-.98-.65-.65-.98-1.91-.98-3.78v-7.08c0-1.95.32-3.23.98-3.85.65-.61,1.95-.92,3.91-.92h7.2c1.87,0,3.13.31,3.78.92.65.61.98,1.89.98,3.85v7.08c0,1.87-.33,3.13-.98,3.78s-1.91.98-3.78.98h-7.2ZM55.3,179.2c-1.95,0-3.26-.31-3.91-.92-.65-.61-.98-1.89-.98-3.85v-7.08c0-1.95.32-3.23.98-3.85.65-.61,1.95-.92,3.91-.92h7.2c1.87,0,3.13.31,3.78.92.65.61.98,1.89.98,3.85v7.08c0,1.95-.33,3.23-.98,3.85-.65.61-1.91.92-3.78.92h-7.2ZM96.68,97.66c-1.87,0-3.15-.32-3.84-.98-.69-.65-1.04-1.91-1.04-3.78v-7.2c0-1.87.35-3.11,1.04-3.72.69-.61,1.97-.92,3.84-.92h7.2c1.87,0,3.15.31,3.85.92.69.61,1.04,1.85,1.04,3.72v7.2c0,1.87-.35,3.13-1.04,3.78-.69.65-1.97.98-3.85.98h-7.2ZM96.68,138.43c-1.87,0-3.15-.32-3.84-.98-.69-.65-1.04-1.91-1.04-3.78v-7.08c0-1.95.35-3.23,1.04-3.85.69-.61,1.97-.92,3.84-.92h7.2c1.87,0,3.15.31,3.85.92.69.61,1.04,1.89,1.04,3.85v7.08c0,1.87-.35,3.13-1.04,3.78-.69.65-1.97.98-3.85.98h-7.2ZM96.68,179.2c-1.87,0-3.15-.31-3.84-.92-.69-.61-1.04-1.89-1.04-3.85v-7.08c0-1.95.35-3.23,1.04-3.85.69-.61,1.97-.92,3.84-.92h7.2c1.87,0,3.15.31,3.85.92.69.61,1.04,1.89,1.04,3.85v7.08c0,1.95-.35,3.23-1.04,3.85-.69.61-1.97.92-3.85.92h-7.2ZM138.06,97.66c-1.87,0-3.13-.32-3.78-.98-.65-.65-.98-1.91-.98-3.78v-7.2c0-1.87.32-3.11.98-3.72.65-.61,1.91-.92,3.78-.92h7.2c1.95,0,3.25.31,3.91.92.65.61.98,1.85.98,3.72v7.2c0,1.87-.33,3.13-.98,3.78s-1.95.98-3.91.98h-7.2ZM138.06,138.43c-1.87,0-3.13-.32-3.78-.98-.65-.65-.98-1.91-.98-3.78v-7.08c0-1.95.32-3.23.98-3.85.65-.61,1.91-.92,3.78-.92h7.2c1.95,0,3.25.31,3.91.92.65.61.98,1.89.98,3.85v7.08c0,1.87-.33,3.13-.98,3.78s-1.95.98-3.91.98h-7.2ZM138.06,179.2c-1.87,0-3.13-.31-3.78-.92-.65-.61-.98-1.89-.98-3.85v-7.08c0-1.95.32-3.23.98-3.85.65-.61,1.91-.92,3.78-.92h7.2c1.95,0,3.25.31,3.91.92.65.61.98,1.89.98,3.85v7.08c0,1.95-.33,3.23-.98,3.85-.65.61-1.95.92-3.91.92h-7.2ZM179.57,97.66c-1.95,0-3.26-.32-3.91-.98-.65-.65-.98-1.91-.98-3.78v-7.2c0-1.87.32-3.11.98-3.72.65-.61,1.95-.92,3.91-.92h7.08c1.95,0,3.25.31,3.91.92.65.61.98,1.85.98,3.72v7.2c0,1.87-.33,3.13-.98,3.78s-1.95.98-3.91.98h-7.08ZM179.57,138.43c-1.95,0-3.26-.32-3.91-.98-.65-.65-.98-1.91-.98-3.78v-7.08c0-1.95.32-3.23.98-3.85.65-.61,1.95-.92,3.91-.92h7.08c1.95,0,3.25.31,3.91.92.65.61.98,1.89.98,3.85v7.08c0,1.87-.33,3.13-.98,3.78s-1.95.98-3.91.98h-7.08Z"/></svg>`;
// v0.70.24 — circled tick (Save, in-field)
const IC_TICK_CIRCLE = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.4-4-4 1.7-1.7 2.3 2.3 5.5-5.5 1.7 1.7z"/></svg>`;
// v0.70.10 — "two names for one person" marker (glyph supplied by Alexander, 13 Sep)
const IC_ALERT = `<svg viewBox="0 0 255.86 231.93" fill="currentColor" aria-hidden="true"><path d="M33.33,231.93c-6.92,0-12.86-1.46-17.82-4.39-4.96-2.93-8.79-6.9-11.47-11.9-2.69-5.01-4.03-10.6-4.03-16.78s1.59-11.64,4.76-17.09L99.49,16.72c3.09-5.53,7.2-9.7,12.33-12.51S122.31,0,127.93,0s10.97,1.4,16.05,4.21c5.08,2.81,9.22,6.98,12.39,12.51l94.73,165.04c1.54,2.69,2.73,5.49,3.54,8.42.81,2.93,1.22,5.82,1.22,8.67,0,6.18-1.34,11.78-4.03,16.78-2.69,5-6.51,8.97-11.47,11.9-4.96,2.93-10.91,4.39-17.82,4.39H33.33ZM33.45,212.77h188.84c4.23,0,7.59-1.4,10.07-4.21,2.48-2.81,3.72-6.08,3.72-9.83,0-2.6-.65-5.17-1.95-7.69L139.53,26c-1.3-2.28-2.99-3.93-5.07-4.94-2.08-1.02-4.25-1.53-6.53-1.53s-4.48.51-6.59,1.53c-2.12,1.02-3.83,2.67-5.13,4.94L21.61,190.92c-.65,1.3-1.12,2.65-1.4,4.03-.29,1.38-.43,2.65-.43,3.78,0,3.74,1.22,7.02,3.66,9.83,2.44,2.81,5.78,4.21,10.01,4.21ZM127.93,189.21c-3.58,0-6.67-1.24-9.28-3.72-2.61-2.48-3.91-5.47-3.91-8.97s1.3-6.49,3.91-8.97c2.6-2.48,5.7-3.72,9.28-3.72s6.67,1.22,9.28,3.66c2.6,2.44,3.91,5.45,3.91,9.03s-1.32,6.59-3.97,9.03c-2.65,2.44-5.72,3.66-9.22,3.66ZM127.93,149.54c-6.27,0-9.48-3.21-9.64-9.64l-1.59-64.58c-.08-3.09.92-5.66,2.99-7.69,2.08-2.03,4.78-3.05,8.12-3.05s5.96,1.04,8.12,3.11c2.16,2.08,3.19,4.66,3.11,7.75l-1.83,64.45c-.08,6.43-3.17,9.64-9.28,9.64Z"/></svg>`;
// v0.70.9 — Contacts (menu glyph supplied by Alexander, 13 Sep)
const IC_MYCONTACTS = `<svg viewBox="0 0 339.97 226.07" fill="currentColor" aria-hidden="true"><path d="M24.78,226.07c-8.22,0-14.4-1.69-18.55-5.07s-6.23-8.48-6.23-15.32c0-10.09,2.4-19.79,7.2-29.11,4.8-9.32,11.45-17.62,19.96-24.9,8.5-7.28,18.35-13.04,29.54-17.27,11.19-4.23,23.17-6.35,35.95-6.35,6.75,0,13.31.59,19.65,1.77,6.35,1.18,12.47,2.93,18.37,5.25,5.9,2.32,11.49,5.19,16.78,8.61-2.77,1.95-5.29,4.01-7.57,6.16-2.28,2.16-4.35,4.46-6.23,6.9-3.99-2.36-8.16-4.31-12.51-5.86-4.35-1.54-8.91-2.73-13.67-3.54-4.76-.81-9.71-1.22-14.83-1.22-10.01,0-19.49,1.61-28.44,4.82-8.95,3.22-16.89,7.57-23.8,13.06-6.92,5.49-12.35,11.74-16.3,18.74-3.95,7-5.92,14.28-5.92,21.85,0,1.22.28,2.09.85,2.62.57.53,1.5.79,2.81.79h89.36c-.41,3.74.32,7.28,2.2,10.62,1.87,3.34,4.27,5.82,7.2,7.45H24.78ZM92.77,114.14c-8.38,0-16.03-2.26-22.95-6.77-6.92-4.52-12.45-10.56-16.6-18.13s-6.23-15.95-6.23-25.15,2.08-17.27,6.23-24.72,9.68-13.39,16.6-17.82c6.92-4.43,14.57-6.65,22.95-6.65s16.15,2.2,23.07,6.59c6.92,4.39,12.43,10.3,16.54,17.7,4.11,7.41,6.16,15.62,6.16,24.66s-2.08,17.62-6.23,25.27c-4.15,7.65-9.69,13.73-16.6,18.25-6.92,4.52-14.57,6.77-22.95,6.77ZM92.77,95.95c4.96,0,9.56-1.44,13.79-4.33,4.23-2.89,7.61-6.75,10.13-11.6,2.52-4.84,3.78-10.23,3.78-16.17s-1.24-10.99-3.72-15.62c-2.48-4.64-5.82-8.32-10.01-11.05-4.19-2.73-8.85-4.09-13.98-4.09s-9.66,1.38-13.85,4.15c-4.19,2.77-7.53,6.49-10.01,11.17-2.48,4.68-3.72,9.91-3.72,15.69s1.26,11.21,3.78,16.05c2.52,4.84,5.88,8.69,10.07,11.54,4.19,2.85,8.77,4.27,13.73,4.27ZM156.62,226.07c-19.94,0-29.91-6.51-29.91-19.53,0-7.97,2.44-16.48,7.32-25.51s11.94-17.56,21.18-25.57c9.24-8.01,20.43-14.55,33.57-19.59,13.14-5.04,27.97-7.57,44.49-7.57s31.47,2.52,44.62,7.57c13.14,5.05,24.33,11.58,33.57,19.59,9.24,8.02,16.3,16.54,21.18,25.57s7.32,17.54,7.32,25.51c0,13.02-10.01,19.53-30.03,19.53h-153.32ZM150.76,207.64h165.04c1.63,0,2.79-.24,3.48-.73s1.04-1.38,1.04-2.69c0-5.04-1.89-10.82-5.68-17.33-3.78-6.51-9.38-12.82-16.78-18.92-7.41-6.1-16.52-11.17-27.34-15.2-10.82-4.03-23.24-6.04-37.23-6.04s-26.39,2.01-37.17,6.04c-10.78,4.03-19.88,9.09-27.28,15.2-7.41,6.1-13,12.41-16.79,18.92-3.78,6.51-5.68,12.29-5.68,17.33,0,1.3.35,2.2,1.04,2.69s1.81.73,3.36.73ZM233.4,113.04c-9.69,0-18.51-2.56-26.49-7.69-7.98-5.13-14.34-12.02-19.1-20.69-4.76-8.67-7.14-18.21-7.14-28.63s2.38-19.73,7.14-28.2c4.76-8.46,11.13-15.22,19.1-20.26,7.97-5.04,16.8-7.57,26.49-7.57s18.63,2.48,26.61,7.45c7.97,4.96,14.32,11.66,19.04,20.08,4.72,8.42,7.08,17.84,7.08,28.26s-2.38,20.08-7.14,28.75c-4.76,8.67-11.13,15.58-19.1,20.75-7.98,5.17-16.81,7.75-26.49,7.75ZM233.4,94.6c6.02,0,11.54-1.73,16.54-5.19,5.01-3.46,9.01-8.14,12.02-14.04,3.01-5.9,4.52-12.43,4.52-19.59s-1.49-13.45-4.46-19.1c-2.97-5.66-6.96-10.11-11.96-13.37-5.01-3.25-10.56-4.88-16.66-4.88s-11.54,1.65-16.54,4.94c-5.01,3.3-9.01,7.79-12.02,13.49-3.01,5.7-4.52,12.08-4.52,19.17s1.53,13.67,4.58,19.53c3.05,5.86,7.08,10.5,12.08,13.92,5.01,3.42,10.48,5.13,16.42,5.13Z"/></svg>`;
const IC_INVITE = `<svg viewBox="-27.29 -30.41 327.54 327.54" fill="currentColor" aria-hidden="true"><path d="M135.13,226.07H30.03c-20.02,0-30.03-6.51-30.03-19.53,0-7.97,2.44-16.48,7.32-25.51s11.94-17.56,21.18-25.57c9.24-8.01,20.43-14.55,33.57-19.59,13.14-5.04,28.01-7.57,44.62-7.57,11.07,0,21.38,1.2,30.94,3.6,9.56,2.4,18.25,5.68,26.06,9.83-2.61,2.04-5.05,4.21-7.32,6.53-2.28,2.32-4.44,4.82-6.47,7.51-5.94-2.77-12.55-4.96-19.84-6.59-7.28-1.63-15.08-2.44-23.38-2.44-14,0-26.41,2.01-37.23,6.04-10.82,4.03-19.92,9.09-27.28,15.2s-12.96,12.41-16.78,18.92c-3.83,6.51-5.74,12.29-5.74,17.33,0,1.3.35,2.2,1.04,2.69s1.85.73,3.48.73h108.03c.08,3.17.37,6.31.85,9.4.49,3.09,1.18,6.1,2.08,9.03ZM106.57,113.04c-9.69,0-18.51-2.58-26.49-7.75-7.98-5.17-14.34-12.08-19.1-20.75-4.76-8.67-7.14-18.25-7.14-28.75s2.38-19.84,7.14-28.26c4.76-8.42,11.13-15.12,19.1-20.08,7.97-4.96,16.8-7.45,26.49-7.45s18.51,2.52,26.49,7.57c7.97,5.05,14.36,11.8,19.17,20.26,4.8,8.46,7.16,17.86,7.08,28.2,0,10.42-2.38,19.96-7.14,28.63-4.76,8.67-11.13,15.56-19.1,20.69-7.98,5.13-16.81,7.69-26.49,7.69ZM106.57,94.6c5.94,0,11.43-1.71,16.48-5.13,5.04-3.42,9.07-8.06,12.08-13.92,3.01-5.86,4.52-12.37,4.52-19.53.08-7.08-1.38-13.47-4.39-19.17-3.01-5.7-7.02-10.19-12.02-13.49-5-3.3-10.56-4.94-16.66-4.94s-11.54,1.63-16.54,4.88c-5,3.26-9.01,7.71-12.02,13.37-3.01,5.66-4.52,12.02-4.52,19.1s1.5,13.69,4.52,19.59c3.01,5.9,7.04,10.58,12.08,14.04,5.04,3.46,10.54,5.19,16.48,5.19ZM210.94,266.72c-8.46,0-16.44-1.61-23.93-4.82-7.49-3.22-14.08-7.67-19.78-13.37s-10.17-12.29-13.43-19.78c-3.26-7.49-4.88-15.5-4.88-24.05s1.63-16.54,4.88-23.99c3.25-7.45,7.73-14.04,13.43-19.78,5.7-5.74,12.29-10.21,19.78-13.43,7.49-3.21,15.46-4.82,23.93-4.82s16.56,1.61,24.05,4.82c7.49,3.22,14.08,7.67,19.78,13.37,5.7,5.7,10.15,12.29,13.37,19.78,3.21,7.49,4.82,15.5,4.82,24.05s-1.63,16.44-4.88,23.93c-3.26,7.49-7.75,14.08-13.49,19.78-5.74,5.7-12.35,10.17-19.84,13.43-7.49,3.25-15.42,4.88-23.8,4.88ZM210.94,243.53c2.28,0,4.09-.69,5.43-2.08,1.34-1.38,2.01-3.17,2.01-5.37v-23.93h23.93c2.2,0,3.99-.67,5.37-2.01,1.38-1.34,2.08-3.16,2.08-5.43s-.69-4.09-2.08-5.43c-1.38-1.34-3.17-2.01-5.37-2.01h-23.93v-23.93c0-2.2-.67-3.99-2.01-5.37-1.34-1.38-3.16-2.08-5.43-2.08s-4.09.69-5.43,2.08c-1.34,1.38-2.01,3.17-2.01,5.37v23.93h-23.93c-2.2,0-3.99.67-5.37,2.01-1.38,1.34-2.08,3.15-2.08,5.43s.69,4.09,2.08,5.43c1.38,1.34,3.17,2.01,5.37,2.01h23.93v23.93c0,2.2.67,3.99,2.01,5.37,1.34,1.38,3.15,2.08,5.43,2.08Z"/></svg>`;
const IC_SHARE = `<svg viewBox="-56.17 -27.42 329 329" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M51.03,274.17c-16.36,0-28.95-4.42-37.78-13.25-8.83-8.83-13.25-21.42-13.25-37.78v-93.99c0-16.36,4.41-28.95,13.25-37.78s21.42-13.24,37.78-13.24h24.66v19.65h-24.66c-10.01,0-17.74,2.73-23.19,8.18s-8.18,13.18-8.18,23.19v93.99c0,10.09,2.73,17.84,8.18,23.25,5.45,5.41,13.18,8.12,23.19,8.12h114.5c10.09,0,17.86-2.71,23.32-8.12,5.45-5.41,8.18-13.16,8.18-23.25v-93.99c0-10.01-2.73-17.74-8.18-23.19-5.45-5.45-13.23-8.18-23.32-8.18h-24.66v-19.65h24.66c16.44,0,29.07,4.42,37.9,13.24,8.83,8.83,13.24,21.42,13.24,37.78v93.99c0,16.36-4.42,28.95-13.24,37.78-8.83,8.83-21.47,13.25-37.9,13.25H51.03ZM66.89,57.01c-2.52,0-4.62-.81-6.29-2.44-1.67-1.63-2.5-3.66-2.5-6.1s.98-4.52,2.93-6.47L101.2,3.17c1.3-1.22,2.5-2.05,3.6-2.5,1.1-.45,2.26-.67,3.48-.67,1.3,0,2.5.23,3.6.67,1.1.45,2.26,1.28,3.48,2.5l40.28,38.82c1.87,1.95,2.81,4.11,2.81,6.47s-.85,4.48-2.56,6.1c-1.71,1.63-3.83,2.44-6.35,2.44-2.69,0-4.88-.98-6.59-2.93l-18.8-20.14-15.87-16.72-15.75,16.72-18.92,20.14c-1.71,1.95-3.95,2.93-6.71,2.93ZM108.28,178.59c-2.61,0-4.86-.92-6.78-2.75s-2.87-4.05-2.87-6.65V45.04l1.46-32.96c.16-2.2,1.02-4.11,2.56-5.74,1.54-1.63,3.42-2.44,5.62-2.44s4.19.81,5.74,2.44c1.54,1.63,2.4,3.54,2.56,5.74l1.46,32.96v124.15c0,2.61-.96,4.82-2.87,6.65s-4.21,2.75-6.9,2.75Z"/></svg>`;
const IC_LAB = `<svg viewBox="-43.93 -29.43 353.17 353.17" fill="currentColor" aria-hidden="true"><path d="M5.2,73.49c4.96-8.46,12.61-14.53,22.95-18.19,10.33-3.66,22.71-4.92,37.11-3.78,14.4,1.14,30.27,4.74,47.61,10.8,17.33,6.06,35.48,14.55,54.44,25.45,18.8,10.82,35.11,22.22,48.95,34.18,13.83,11.96,24.84,23.89,33.02,35.77,8.18,11.88,13.25,23.21,15.2,34,1.95,10.78.49,20.45-4.39,28.99-4.88,8.46-12.47,14.53-22.77,18.19s-22.64,4.92-37.05,3.78c-14.4-1.14-30.25-4.7-47.55-10.68-17.29-5.98-35.34-14.42-54.14-25.33-18.96-10.9-35.38-22.36-49.26-34.36-13.88-12-24.92-23.94-33.14-35.83C7.97,124.59,2.86,113.24.87,102.42c-2-10.82-.55-20.47,4.33-28.93ZM5.2,220.7c-4.88-8.46-6.33-18.09-4.33-28.87,1.99-10.78,7.1-22.13,15.32-34.06,8.22-11.92,19.27-23.86,33.14-35.83s30.29-23.4,49.26-34.3c18.8-10.9,36.84-19.37,54.14-25.39,17.29-6.02,33.12-9.6,47.48-10.74,14.36-1.14,26.71.14,37.05,3.85,10.33,3.7,17.94,9.79,22.83,18.25,4.88,8.46,6.35,18.09,4.39,28.87-1.95,10.78-7.02,22.12-15.2,34-8.18,11.88-19.19,23.8-33.02,35.77-13.84,11.96-30.15,23.4-48.95,34.3-18.96,10.91-37.11,19.37-54.44,25.39-17.33,6.02-33.22,9.62-47.67,10.8s-26.84-.08-37.17-3.78c-10.34-3.7-17.94-9.78-22.83-18.25ZM20.83,82.52c-3.5,5.94-4.03,13.06-1.59,21.36s7.51,17.27,15.2,26.92c7.69,9.64,17.76,19.51,30.21,29.6,12.45,10.09,26.98,19.94,43.58,29.54,16.52,9.6,32.25,17.25,47.18,22.95,14.93,5.7,28.46,9.46,40.59,11.29,12.12,1.83,22.38,1.71,30.76-.37,8.38-2.08,14.32-6.12,17.82-12.15,3.5-5.94,4.05-13.06,1.65-21.36-2.4-8.3-7.45-17.27-15.14-26.92-7.69-9.64-17.74-19.49-30.15-29.54-12.41-10.05-26.88-19.84-43.4-29.36-16.6-9.6-32.39-17.27-47.36-23.01-14.97-5.74-28.54-9.54-40.71-11.41-12.17-1.87-22.44-1.77-30.82.31-8.38,2.08-14.32,6.12-17.82,12.15ZM20.83,211.67c3.5,6.02,9.44,10.07,17.82,12.15,8.38,2.08,18.66,2.2,30.82.37,12.17-1.83,25.74-5.62,40.71-11.35,14.97-5.74,30.76-13.41,47.36-23.01,16.52-9.52,30.99-19.33,43.4-29.42,12.41-10.09,22.46-19.94,30.15-29.54,7.69-9.6,12.73-18.53,15.14-26.79,2.4-8.26,1.85-15.4-1.65-21.42-3.5-6.02-9.44-10.07-17.82-12.15-8.38-2.08-18.64-2.2-30.76-.37-12.13,1.83-25.66,5.6-40.59,11.29-14.93,5.7-30.66,13.35-47.18,22.95-16.6,9.52-31.13,19.33-43.58,29.42-12.45,10.09-22.52,19.96-30.21,29.6-7.69,9.64-12.76,18.62-15.2,26.92s-1.91,15.42,1.59,21.36ZM132.64,294.31c-9.77,0-18.82-3.56-27.16-10.68-8.34-7.12-15.62-17.21-21.85-30.27-6.23-13.06-11.05-28.6-14.47-46.63-3.42-18.02-5.13-37.94-5.13-59.75s1.71-41.69,5.13-59.63,8.24-33.43,14.47-46.45c6.22-13.02,13.51-23.09,21.85-30.21C113.82,3.56,122.88,0,132.64,0s18.94,3.56,27.28,10.68c8.34,7.12,15.6,17.19,21.79,30.21,6.18,13.02,11.01,28.5,14.47,46.45,3.46,17.94,5.19,37.82,5.19,59.63s-1.73,41.73-5.19,59.75c-3.46,18.03-8.28,33.57-14.47,46.63-6.19,13.06-13.45,23.15-21.79,30.27-8.34,7.12-17.44,10.68-27.28,10.68ZM132.64,276.37c7,0,13.49-3.13,19.47-9.4,5.98-6.27,11.21-15.14,15.69-26.61,4.47-11.47,7.97-25.12,10.5-40.95,2.52-15.83,3.78-33.3,3.78-52.43s-1.26-36.58-3.78-52.37c-2.52-15.79-6.02-29.4-10.5-40.83-4.48-11.43-9.7-20.26-15.69-26.49-5.98-6.23-12.47-9.34-19.47-9.34s-13.37,3.11-19.35,9.34c-5.98,6.23-11.21,15.06-15.69,26.49-4.48,11.43-7.98,25.05-10.5,40.83-2.52,15.79-3.78,33.25-3.78,52.37s1.26,36.6,3.78,52.43c2.52,15.83,6.02,29.48,10.5,40.95,4.47,11.47,9.71,20.34,15.69,26.61,5.98,6.27,12.43,9.4,19.35,9.4ZM132.64,173.1c-7.16,0-13.29-2.54-18.37-7.63-5.09-5.08-7.63-11.21-7.63-18.37s2.54-13.26,7.63-18.31c5.09-5.04,11.21-7.57,18.37-7.57s13.26,2.52,18.31,7.57c5.04,5.05,7.57,11.15,7.57,18.31s-2.52,13.29-7.57,18.37c-5.05,5.09-11.15,7.63-18.31,7.63Z"/></svg>`;
// v1.3.14 — the Wallet sheet's Spare Key row: Alexander's spare_key glyph (12 Oct), filled with the text colour. 1.5.61 — his
// Spare_key_new of 4 Nov (one key).
const IC_KEY = `<svg viewBox="0 0 155.88 301.3" fill="currentColor" aria-hidden="true"><path d="M78,0c10.74,0,20.83,2.01,30.27,6.04,9.44,4.03,17.72,9.6,24.84,16.72,7.12,7.12,12.7,15.38,16.72,24.78,4.03,9.4,6.04,19.47,6.04,30.21,0,16.68-5.07,31.7-15.2,45.04-10.13,13.35-24.64,23.76-43.52,31.25l32.59,32.84c1.3,1.3,1.97,2.77,2.01,4.39.04,1.63-.55,3.05-1.77,4.27l-32.96,32.84,24.05,23.68c1.22,1.3,1.85,2.77,1.89,4.39.04,1.63-.59,3.09-1.89,4.39l-38.7,38.7c-1.3,1.3-2.83,1.87-4.58,1.71-1.75-.16-3.15-.77-4.21-1.83l-20.39-20.51c-1.06-1.14-1.85-2.32-2.38-3.54-.53-1.22-.79-2.61-.79-4.15v-120.48c-10.17-3.91-19-9.5-26.49-16.78-7.49-7.28-13.29-15.75-17.39-25.39-4.11-9.64-6.17-19.92-6.17-30.82s2.01-20.94,6.04-30.33c4.03-9.4,9.6-17.66,16.72-24.78s15.38-12.67,24.78-16.66C56.95,2,67.1,0,78,0ZM78,71.53c5.62,0,10.42-1.99,14.4-5.98s5.98-8.79,5.98-14.4-2-10.52-5.98-14.47c-3.99-3.95-8.79-5.92-14.4-5.92s-10.62,1.97-14.53,5.92c-3.91,3.95-5.86,8.77-5.86,14.47s1.97,10.42,5.92,14.4c3.95,3.99,8.77,5.98,14.46,5.98Z"/></svg>`;
const IC_TRASH = `<svg viewBox="-44.95 -27.19 326.22 326.22" fill="currentColor" aria-hidden="true"><path d="M9.16,65.55c-2.44,0-4.58-.92-6.41-2.75-1.83-1.83-2.75-4.01-2.75-6.53s.92-4.58,2.75-6.41c1.83-1.83,3.97-2.75,6.41-2.75h218.14c2.44,0,4.56.9,6.35,2.69,1.79,1.79,2.69,3.95,2.69,6.47s-.88,4.7-2.62,6.53c-1.75,1.83-3.89,2.75-6.41,2.75H9.16ZM62.26,271.85c-8.79,0-15.89-2.58-21.3-7.75-5.41-5.17-8.32-12.15-8.73-20.94L23.56,63.23h19.17l8.67,177.73c.16,3.58,1.42,6.55,3.78,8.91s5.29,3.54,8.79,3.54h108.28c3.58,0,6.55-1.16,8.91-3.48,2.36-2.32,3.62-5.31,3.78-8.97l8.18-177.73h19.65l-8.54,179.81c-.41,8.79-3.34,15.79-8.79,21-5.45,5.21-12.53,7.81-21.24,7.81H62.26ZM64.58,55.79v-27.34c0-8.79,2.71-15.73,8.12-20.81,5.41-5.08,12.8-7.63,22.16-7.63h46.39c9.36,0,16.74,2.54,22.16,7.63,5.41,5.09,8.12,12.02,8.12,20.81v27.34h-19.41v-26.12c0-3.42-1.12-6.18-3.36-8.3-2.24-2.12-5.19-3.17-8.85-3.17h-43.7c-3.66,0-6.61,1.06-8.85,3.17-2.24,2.12-3.36,4.88-3.36,8.3v26.12h-19.41ZM82.03,232.54c-2.28,0-4.11-.65-5.49-1.95-1.38-1.3-2.12-3.05-2.2-5.25l-3.66-130.49c-.08-2.12.59-3.85,2.01-5.19s3.28-2.01,5.55-2.01,4.01.65,5.43,1.95c1.42,1.3,2.14,3.01,2.14,5.13l3.78,130.62c0,2.12-.69,3.85-2.08,5.19-1.38,1.34-3.22,2.01-5.49,2.01ZM118.16,232.54c-2.28,0-4.15-.67-5.62-2.01s-2.2-3.07-2.2-5.19V94.85c0-2.12.73-3.85,2.2-5.19s3.34-2.01,5.62-2.01,4.27.67,5.74,2.01,2.2,3.07,2.2,5.19v130.49c0,2.12-.73,3.85-2.2,5.19s-3.38,2.01-5.74,2.01ZM154.42,232.54c-2.36,0-4.23-.67-5.62-2.01-1.38-1.34-2.04-3.07-1.95-5.19l3.66-130.49c.08-2.2.81-3.95,2.2-5.25,1.38-1.3,3.17-1.95,5.37-1.95,2.36,0,4.23.67,5.62,2.01,1.38,1.34,2.03,3.07,1.95,5.19l-3.66,130.49c-.08,2.2-.81,3.95-2.2,5.25-1.38,1.3-3.17,1.95-5.37,1.95Z"/></svg>`;
// 1.4.67 — "Mask my balance": an eye with a slash through it.
const IC_EYE_OFF = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.6 5.1A10.9 10.9 0 0 1 12 5c5.5 0 9.3 4.6 10.4 6.4a1.1 1.1 0 0 1 0 1.2 18.6 18.6 0 0 1-2.6 3.3M6.2 6.6A18.4 18.4 0 0 0 1.6 11.4a1.1 1.1 0 0 0 0 1.2C2.7 14.4 6.5 19 12 19a10.6 10.6 0 0 0 5.3-1.4M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18"/></svg>`;
// v0.99.0 — Omega Air's own mark (the Ω globe, without the wordmark), for the More row.
// 1.5.17 — Ondas Coffee's cup (the café demo's row, beside Omega Air's).
const IC_CAFE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9h12v6a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V9z"/><path d="M16 10h1.5a2.5 2.5 0 0 1 0 5H16"/><path d="M8 6c0-1 .7-1.3.7-2M11 6c0-1 .7-1.3.7-2"/></svg>`;
const IC_OMEGA = `<svg viewBox="-16 -8 336 288" fill="currentColor" aria-hidden="true"><path d="M69.37,126.34c.24-10.09,2.22-19.65,5.95-28.66,3.72-9.01,8.8-17.06,15.23-24.15,2.82,2.76,6.44,5.3,10.86,7.62,4.42,2.31,9.51,4.28,15.28,5.9-1.56,5.71-2.79,11.88-3.7,18.52-.9,6.64-1.38,13.56-1.44,20.77h-42.18ZM90.64,184.2c-6.37-7.09-11.45-15.16-15.23-24.2-3.79-9.04-5.8-18.64-6.04-28.8h42.18c.06,7.33.54,14.35,1.44,21.04.9,6.7,2.13,12.99,3.7,18.88-5.65,1.5-10.68,3.38-15.1,5.63-4.42,2.25-8.07,4.73-10.95,7.44ZM93.35,70.55c6.07-6.13,12.96-11.21,20.68-15.23,7.72-4.02,16.03-6.73,24.92-8.11-4.27,3.12-8.23,7.71-11.9,13.74-3.67,6.04-6.73,13.26-9.19,21.68-4.99-1.44-9.52-3.17-13.61-5.18-4.09-2.01-7.72-4.31-10.91-6.89ZM138.86,210.42c-8.77-1.38-17-4.07-24.69-8.07-7.69-4-14.57-9.03-20.64-15.1,6.49-5.17,14.6-9.13,24.33-11.9,2.46,8.23,5.53,15.34,9.19,21.31,3.66,5.98,7.6,10.56,11.81,13.74ZM116.42,126.34c.12-6.73.6-13.34,1.44-19.83.84-6.49,1.98-12.59,3.42-18.3,4.2.9,8.68,1.65,13.43,2.25,4.75.6,9.7.93,14.87.99v34.88h-33.17ZM121.65,169.78c-1.56-5.83-2.81-12.03-3.74-18.61-.93-6.58-1.43-13.23-1.49-19.96h33.17v35.42c-10.22.18-19.53,1.23-27.94,3.15ZM122.46,83.8c3.06-10.39,6.99-19,11.76-25.82,4.78-6.82,9.9-10.68,15.37-11.58v40.29c-4.99-.06-9.75-.33-14.29-.81-4.54-.48-8.82-1.17-12.84-2.07ZM149.58,211.23c-5.47-.96-10.56-4.84-15.28-11.63-4.72-6.79-8.55-15.29-11.49-25.51,7.93-1.68,16.85-2.64,26.77-2.88v40.02ZM154.36,86.68v-40.29c5.47.9,10.57,4.76,15.32,11.58,4.75,6.82,8.65,15.43,11.72,25.82-4.03.9-8.29,1.59-12.8,2.07-4.51.48-9.25.75-14.24.81ZM154.36,126.34v-34.88c5.05-.06,9.94-.39,14.69-.99,4.75-.6,9.25-1.35,13.52-2.25,1.44,5.71,2.58,11.81,3.42,18.3.84,6.49,1.29,13.1,1.35,19.83h-32.99ZM154.36,166.62v-35.42h32.99c-.06,6.73-.54,13.38-1.44,19.96-.9,6.58-2.13,12.78-3.7,18.61-8.41-1.92-17.7-2.97-27.85-3.15ZM154.36,211.23v-40.02c4.93.12,9.61.42,14.06.9,4.45.48,8.68,1.14,12.71,1.98-3.06,10.22-6.94,18.71-11.63,25.51-4.69,6.79-9.73,10.66-15.14,11.63ZM185.99,82.62c-2.46-8.41-5.53-15.64-9.19-21.68-3.67-6.04-7.63-10.62-11.9-13.74,8.89,1.38,17.2,4.09,24.92,8.11,7.72,4.03,14.58,9.1,20.59,15.23-3.19,2.58-6.8,4.88-10.86,6.89-4.06,2.01-8.58,3.74-13.56,5.18ZM165,210.42c4.27-3.19,8.22-7.77,11.85-13.74,3.63-5.98,6.68-13.08,9.15-21.31,9.73,2.76,17.84,6.73,24.33,11.9-6.07,6.07-12.95,11.1-20.64,15.1-7.69,4-15.92,6.68-24.69,8.07ZM192.3,126.34c-.06-7.21-.54-14.13-1.44-20.77-.9-6.64-2.13-12.81-3.7-18.52,5.71-1.62,10.78-3.59,15.23-5.9,4.45-2.31,8.08-4.85,10.91-7.62,6.43,7.09,11.49,15.14,15.19,24.15,3.7,9.01,5.66,18.57,5.9,28.66h-42.09ZM187.17,171.13c1.56-5.89,2.79-12.18,3.7-18.88.9-6.7,1.38-13.71,1.44-21.04h42.09c-.24,10.15-2.25,19.75-6.04,28.8-3.79,9.04-8.86,17.11-15.23,24.2-2.88-2.7-6.53-5.18-10.95-7.44-4.42-2.25-9.42-4.13-15.01-5.63Z M234.27,236.26h69.01v26.46h-118.77v-21.09c37.56-21.1,69.95-62.14,69.95-113.91,0-47.18-34.27-100.49-101.4-100.49s-103.75,50.24-103.75,100.87,32.86,94.35,70.42,113.53v21.09H.48v-26.46h68.07v-1.53c-31.45-21.1-61.97-59.45-61.97-109.31C6.59,51.78,69.96,0,153.99,0s142.71,54.08,142.71,122.35c0,52.93-30.98,90.9-62.44,112.38v1.53Z"/></svg>`;
const IC_HELP = `<svg viewBox="-24.9 -24.9 298.82 298.82" fill="currentColor" aria-hidden="true"><path d="M124.51,249.02c-17.17,0-33.29-3.23-48.34-9.71-15.06-6.47-28.28-15.4-39.67-26.79-11.39-11.39-20.32-24.62-26.79-39.67C3.24,157.8,0,141.68,0,124.51s3.24-33.28,9.7-48.34c6.47-15.05,15.4-28.3,26.79-39.73,11.39-11.43,24.62-20.36,39.67-26.79C91.23,3.22,107.34,0,124.51,0s33.28,3.22,48.34,9.64c15.05,6.43,28.28,15.36,39.67,26.79,11.39,11.43,20.32,24.68,26.79,39.73,6.47,15.06,9.7,31.17,9.7,48.34s-3.24,33.29-9.7,48.34c-6.47,15.06-15.4,28.28-26.79,39.67-11.39,11.39-24.62,20.33-39.67,26.79-15.06,6.47-31.17,9.71-48.34,9.71ZM124.51,228.27c14.32,0,27.75-2.69,40.28-8.06,12.53-5.37,23.56-12.82,33.08-22.34s16.97-20.55,22.34-33.08c5.37-12.53,8.06-25.96,8.06-40.28s-2.69-27.77-8.06-40.34-12.82-23.6-22.34-33.08c-9.52-9.48-20.55-16.91-33.08-22.28-12.53-5.37-25.96-8.06-40.28-8.06s-27.75,2.69-40.28,8.06c-12.53,5.37-23.56,12.8-33.08,22.28-9.52,9.48-16.97,20.51-22.34,33.08s-8.06,26.02-8.06,40.34,2.69,27.75,8.06,40.28c5.37,12.53,12.82,23.56,22.34,33.08s20.55,16.97,33.08,22.34c12.53,5.37,25.96,8.06,40.28,8.06ZM122.07,149.78c-6.51,0-9.77-3.21-9.77-9.64v-1.59c0-6.02,1.44-10.86,4.33-14.53s6.53-7,10.93-10.01c5.37-3.74,9.36-6.94,11.96-9.58,2.6-2.64,3.91-6.04,3.91-10.19,0-4.64-1.67-8.4-5-11.29-3.34-2.89-7.81-4.33-13.43-4.33-2.69,0-5.19.45-7.51,1.34-2.32.9-4.42,2.18-6.29,3.85-1.87,1.67-3.54,3.68-5,6.04l-2.2,2.93c-1.06,1.46-2.3,2.65-3.72,3.54-1.42.9-3.15,1.34-5.19,1.34s-3.95-.71-5.74-2.14c-1.79-1.42-2.69-3.52-2.69-6.29,0-1.06.08-2.08.24-3.05.16-.98.41-1.95.73-2.93,1.71-5.62,5.88-10.56,12.51-14.83,6.63-4.27,15.2-6.41,25.7-6.41,6.92,0,13.31,1.2,19.17,3.6,5.86,2.4,10.6,5.92,14.22,10.56,3.62,4.64,5.43,10.34,5.43,17.09,0,7.32-1.91,13.1-5.74,17.33-3.83,4.23-8.67,8.3-14.53,12.21-4.31,2.85-7.49,5.51-9.52,8-2.04,2.48-3.05,5.43-3.05,8.85v1.46c0,2.36-.88,4.39-2.62,6.1-1.75,1.71-4.13,2.56-7.14,2.56ZM121.7,186.16c-3.5,0-6.55-1.24-9.16-3.72-2.61-2.48-3.91-5.47-3.91-8.97s1.28-6.49,3.84-8.97c2.56-2.48,5.63-3.72,9.22-3.72s6.67,1.22,9.28,3.66c2.6,2.44,3.91,5.45,3.91,9.03s-1.3,6.59-3.91,9.03c-2.61,2.44-5.7,3.66-9.28,3.66Z"/></svg>`;

// Balance is masked on the strip; the number is revealed only in the balance
// top-panel. No inline eye/flip reveal any more (retired in v0.18.6).

// Home (v0.18 shell): a compact top strip (M logo · masked Balance · QR), the
// Send/Receive money flow as the body, and a bottom dock (Wallet · Add · Pay ·
// Activity · More). Identity/admin (address, Circle Arc, invites, delete) lives
// in bottom sheets opened from the dock. Long-press the M logo opens a Lab tray.

// v0.52 — person-first home action glyphs (Pay ↗, Request ↙, Earn = leaf; Move
// reuses IC_ADD). Diagonal arrows stay the money-direction identity.
// v0.90.14 — Grow's glyph is the chart (Alexander's SVG), not the leaf: the
// section holds ETFs and gold once the money fund is the default form, and a
// chart says "grow" where a leaf said "earn". Same padded viewBox as Add, so
// the ring is the family's.
const IC_LEAF = `<svg viewBox="-23.90 -47.35 320.15 320.15" fill="currentColor" aria-hidden="true"><path d="M9.77,225.46c-2.61,0-4.88-.94-6.84-2.81-1.95-1.87-2.93-4.19-2.93-6.96s.98-4.99,2.93-6.9c1.95-1.91,4.23-2.87,6.84-2.87h252.69c2.69,0,5,.96,6.96,2.87,1.95,1.91,2.93,4.21,2.93,6.9s-.98,5.08-2.93,6.96c-1.95,1.87-4.27,2.81-6.96,2.81H9.77ZM11.47,174.93v-59.2c0-3.42,1.02-6.1,3.05-8.06,2.03-1.95,4.84-2.93,8.42-2.93h28.2c3.5,0,6.27.98,8.3,2.93,2.03,1.95,3.05,4.64,3.05,8.06v59.2c0,3.42-1.02,6.08-3.05,8-2.04,1.91-4.8,2.87-8.3,2.87h-28.2c-3.58,0-6.39-.96-8.42-2.87-2.04-1.91-3.05-4.58-3.05-8ZM77.64,174.93v-94.24c0-3.25,1-5.86,2.99-7.81,1.99-1.95,4.74-2.93,8.24-2.93h28.32c3.5,0,6.27.98,8.3,2.93,2.03,1.95,3.05,4.56,3.05,7.81v94.24c0,3.42-1.02,6.08-3.05,8-2.04,1.91-4.8,2.87-8.3,2.87h-28.32c-3.5,0-6.25-.96-8.24-2.87-2-1.91-2.99-4.58-2.99-8ZM143.68,174.93V45.78c0-3.42,1.02-6.08,3.05-8,2.03-1.91,4.8-2.87,8.3-2.87h28.2c3.5,0,6.29.96,8.36,2.87,2.08,1.91,3.11,4.58,3.11,8v129.15c0,3.42-1.04,6.08-3.11,8-2.07,1.91-4.86,2.87-8.36,2.87h-28.2c-3.5,0-6.27-.96-8.3-2.87-2.04-1.91-3.05-4.58-3.05-8ZM209.84,174.93V10.86c0-3.42,1-6.08,2.99-8,1.99-1.91,4.74-2.87,8.24-2.87h28.2c3.58,0,6.39.96,8.42,2.87,2.03,1.91,3.05,4.58,3.05,8v164.06c0,3.42-1.02,6.08-3.05,8-2.04,1.91-4.84,2.87-8.42,2.87h-28.2c-3.5,0-6.25-.96-8.24-2.87-1.99-1.91-2.99-4.58-2.99-8Z"/></svg>`; // 1.4.91 — Grow is the bars (see IC_WALLET); 1.4.90's squircle chart and the circled chart before it: git history


interface HomeViewDeps {
  adapter: WalletAdapter;
  address: string;
  name: WalletName | null;
  onExit: () => void;
  /** Optional: open the Send flow pre-filled (arriving from a pay link). */
  sendPrefill?: SendPrefill;
  /** 1.4.47 — entered from a payment or a take: money just moved, so the first statement read is fresh. */
  freshStatement?: boolean;
  /** 1.4.48 — this wallet's first home in this browser: the pill rests open (a first-timer doesn't see •••• as a button). */
  firstLogin?: boolean;
  /** 1.4.75 — the wallet was made a moment ago (the sign-up): nothing can be in it yet, so the pill opens on 0 before the first read. */
  justMade?: boolean;
  /** 1.4.88 — arrived from a short balance's Add money (/add): the home opens on the Add money sheet. */
  openAdd?: boolean;
  /** 1.5.47 — back from the approver's enrolment: Settings → M® card open, with "Your M® card is set." */
  openCardSet?: boolean;
}

const EXPLORER_BASE =
  import.meta.env.VITE_EXPLORER_BASE ?? "https://testnet.arcscan.app";
const APP_VERSION = `v${__APP_VERSION__}`; // from package.json via vite define (v0.70.11)

// v0.60.5 — refresh the balance when the app comes back to the foreground.
// One document listener, bound once, always pointing at the live view's
// refresh (the home re-renders; the listener must not stack).
let _liveRefresh: (() => Promise<void>) | null = null;
let _lastRefreshAt = 0;
const REFRESH_MIN_GAP_MS = 15_000;
let _visBound = false;
function bindForegroundRefresh() {
  if (_visBound) return;
  _visBound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - _lastRefreshAt < REFRESH_MIN_GAP_MS) return;
    void _liveRefresh?.();
  });
}

let noteCopyListener: ((e: Event) => void) | null = null; // v1.1.19 — Copy code / link on a Sent row

// v1.3.0 — the passkey's name (Alexander, 6 Oct: the route of least resistance).
// A passkey is named once, when the wallet is made ("M® · Lucy"); browsers
// don't agree on a way for a site to rename it (WebAuthn's Signal API is on
// some and not on others), so M doesn't try: after a change of name it says
// the passkey keeps its own and where the phone keeps it.
const PASSKEY_STEER = "Saved. Your passkey keeps the name it was made with — to rename it, open Passwords on your phone and find globalmoneyapp.com.";
// 1.5.44 — where the browser can rename the passkey itself (passkeyName.ts).
const PASSKEY_FOLLOWS = "Saved. Your passkey shows the new name too.";

/** Every state class this view puts on #app, which outlives every render (an auto-logout replaces #app's content, not
 *  #app). A home starts by clearing them all: one left over from the screen open at the logout — Swap's `swap25` was
 *  missing until 1.5.6 — hid the next home's keypad and showed the dock's ✕ alone (Alexander, 24 Oct 17:22). A class
 *  added to #app anywhere in this view goes in this list in the same change; the `menus` suite logs out from Swap,
 *  Global and Sending and in again. */
export const HOME_STATES = ["focus", "lifted", "acting", "over", "home-zero", "home-funded", "global", "swap25", "getpaid"] as const; // 1.5.19: getpaid (the lit material)

export function renderHomeView(
  root: HTMLElement,
  { adapter, address, name, onExit, sendPrefill, freshStatement, firstLogin, justMade, openAdd, openCardSet }: HomeViewDeps,
) {
  void checkFreshness(); // v1.0.13 — a login asks what is live (v1.0.15: skipped if the page-load check just ran)
  // v1.0.7 — the pill missing after a login (Chrome, 23 and 28 Sep): this view
  // toggles its state classes on #app, which outlives every render. A Send
  // card left open (focus: the pill hidden, strip and dock dimmed) or a To
  // field's lift, carried through an auto-logout, came back with the next
  // login. Every home starts clean. (1.5.6: the list is HOME_STATES, Swap's and Global's classes with the others.)
  root.classList.remove(...HOME_STATES);
  document.body.classList.remove("kb-lock");
  const display = name?.display ? titleCase(name.display) : "your wallet";
  const shareUrl = name?.url ?? "";
  let currentBalance = 0;
  let earnUsd = 0; // v0.30 — M-MMF position in USD (0 until the Teller is live)
  // v0.80 — the FX seam. v0.90: live when MFx is deployed and wired, otherwise
  // the placeholder (quotes an indicative rate, refuses to execute). Env-driven
  // like EarnSource, so placeholder↔live is a config change, not a code change.
  // Swapping it in changes no UI — the seam did its job.
  const fxSource: FxSource = import.meta.env.VITE_FX_ADDRESS
    ? new MFxSource(adapter, {
        apiBase: import.meta.env.VITE_WORKER_URL ?? "",
        address,
      })
    : makePlaceholderFxSource(() => currentBalance);
  // v0.80.41 — settings can now push as soon as they change (not just at the
  // next login), which is what "change in Chrome, open Safari" needs.
  setPusher(address);
  // v1.1.9 — after a login that landed on a different wallet than the one this
  // browser opened with, ask once which one it should open with from now on.
  setTimeout(() => askMainWallet(), 700);
  // v0.80.28 — real balances for every pocket M has a token for (USD, EUR).
  // USD stays in `currentBalance` (the nucleus and Send use it); the rest live
  // here. Read on the same trips as the USD balance.
  const pocketBalances: Record<string, number> = {};
  const pocketBalance = (code: string) =>
    code === BASE_CURRENCY ? currentBalance : (pocketBalances[code] ?? 0);
  async function refreshPockets() {
    await Promise.all(
      liveCurrencies()
        // 1.4.29 — your pockets only: eleven currencies now have tokens, and
        // each read is a round trip to Arc on every refresh.
        .filter((c) => c !== BASE_CURRENCY && enabledCurrencies().includes(c))
        .map(async (c) => {
          try {
            pocketBalances[c] = Number(await adapter.getBalance(address, c)) || 0;
          } catch {
            /* leave whatever we had */
          }
        }),
    );
  }
  const totalUsd = () => currentBalance + earnUsd;
  let srApi: SendReceiveHandle | null = null;
  // v0.90.18 — the home used to wait for the balance read (Circle's transport,
  // several seconds) before showing the keypad and dock: strip + pill, then
  // nothing. Now the LAST KNOWN state paints the funded home at once — the pill
  // is masked, so no stale figure is ever shown — and the read reconciles it.
  // (v1.3.0 — declared up here: the conversation asks `funded` as it first paints.)
  let assumeFunded = (() => { try { return Number(localStorage.getItem("m.lastTotal") ?? "0") > 0; } catch { return false; } })();
  let balanceRead = false; // v1.3.0 — the first balance read is in
  const lastWasZero = (() => { try { return localStorage.getItem("m.lastTotal") === "0"; } catch { return false; } })(); // 1.4.21
  // v1.3.0 — the welcome money (cash sync T2229Z; Alexander, 6 Oct). A wallet
  // made with an invite is funded by the Worker at once (VIP $10, standard
  // $1). createView leaves m.welcome = { a: address, at, amt? } when the
  // invite was used; the home says "Welcome. Sending $10 to your wallet now to
  // try M®." once.
  // v1.3.1 — WHEN it is said. Worker 1.3.2 answers the invite with the amount
  // it is about to send (`welcome`, cash sync T1410Z), kept as `amt`: the line
  // is then said at the first paint, while the money is on its way — as its
  // words say. Without `amt` (an older Worker) it is said when the money shows
  // in the balance, with the amount that arrived, as in 1.3.0. Either way the
  // amount is the Worker's or the chain's, never the app's guess.
  // While the money is on its way (WAIT) the balance is re-read every few
  // seconds, and the empty wallet's "Add money to get started?" is held back.
  // If nothing came (the treasury was empty, Circle was down), that line takes
  // over after WAIT; money that lands later (within KEEP) is still welcomed,
  // once. Device-only, never synced.
  const WELCOME_WAIT_MS = 90_000;
  const WELCOME_KEEP_MS = 24 * 3_600_000;
  let welcomeAmt: number | null = null;
  let welcomeTimer = 0;
  let homeEl: Element | null = null; // this view's <main>, to tell a live home from a logged-out one
  let welcomeAt = 0; // when the invite was used (the flag's `at`), for the amount told in advance
  let welcomeLanded = false;
  // 1.4.57 — a wallet made on a note's take page (noteTakeView) leaves { a, at, note: "€1.41" }: the home opens on
  // "Welcome. €1.41 is in your wallet." once. The money is there already (the take ended in it), so nothing waits.
  let welcomeNoteText: string | null = null;
  function welcomeFlag(): { at: number; amt: number | null } | null {
    try {
      const w = JSON.parse(localStorage.getItem(WELCOME_KEY) ?? "null") as { a?: string; at?: number; amt?: string | number; note?: string } | null;
      if (!w || typeof w.at !== "number") return null;
      if (Date.now() - w.at > WELCOME_KEEP_MS) { localStorage.removeItem(WELCOME_KEY); return null; }
      if ((w.a ?? "").toLowerCase() !== address.toLowerCase()) return null;
      // 1.4.64 — the whole sentence: "Welcome." for a wallet made on the take page, the amount alone for one that was already here.
      if (typeof w.note === "string" && w.note) { welcomeNoteText = `${(w as { fresh?: boolean }).fresh === false ? "" : "Welcome. "}${w.note} is in your wallet.`; localStorage.removeItem(WELCOME_KEY); return null; }
      const amt = Number(w.amt);
      return { at: w.at, amt: Number.isFinite(amt) && amt > 0 ? amt : null };
    } catch {
      return null;
    }
  }
  // 1.5.41 (Alexander, 29 Oct) — the home opens on the default pocket; the one home a note take opens, on the note's
  // pocket (fx.ts takeOpenPocket, read once), as a pocket pick would: for this home only. Supersedes 1.4.63.
  {
    const tc = takeOpenPocket(address);
    setSessionCurrency(tc && tc !== defaultCurrency() ? tc : null);
  }
  const forgetWelcome = () => { try { localStorage.removeItem(WELCOME_KEY); } catch { /* said once per view either way */ } };
  // The amount the Worker said it is sending: known before the first paint, so
  // the line can open the home. The flag goes at once — said once, in this login.
  {
    const f = welcomeFlag();
    if (f?.amt != null && Date.now() - f.at < WELCOME_WAIT_MS) {
      welcomeAmt = f.amt;
      welcomeAt = f.at;
      forgetWelcome();
    }
  }
  function welcomeMoney(): number | "wait" | null {
    if (welcomeAmt !== null) {
      // Told in advance and still nothing after WAIT: it didn't come — step aside for "Add money to get started?".
      if (welcomeAt && !welcomeLanded && Date.now() - welcomeAt >= WELCOME_WAIT_MS) return null;
      return welcomeAmt;
    }
    const f = welcomeFlag();
    return f && Date.now() - f.at < WELCOME_WAIT_MS ? "wait" : null;
  }
  /** After every balance read: has the welcome money landed? If not, look again shortly. */
  function checkWelcome() {
    if (currentBalance > 0) welcomeLanded = true;
    const f = welcomeFlag();
    const at = welcomeAt || f?.at || 0;
    if (!at) return;
    if (currentBalance > 0) {
      if (welcomeAmt === null && f) { welcomeAmt = currentBalance; forgetWelcome(); } // not told in advance: what arrived
      return;
    }
    const left = at + WELCOME_WAIT_MS - Date.now();
    window.clearTimeout(welcomeTimer);
    if (left <= 0) return;
    welcomeTimer = window.setTimeout(() => {
      if (!homeEl || !root.contains(homeEl)) return; // logged out meanwhile
      void refreshBalance();
    }, Math.min(4000, left));
  }
  // v1.1.21 — the first word of the name on the profile, for "Hi Alex."
  // 1.4.21 — from what this browser remembers first (main.ts `m.home`), so
  // "Hi Alex." is there with the keypad; the profile confirms it behind.
  const HOME_KEY = "m.home";
  let greetName = (() => {
    try {
      const j = JSON.parse(localStorage.getItem(HOME_KEY) ?? "null") as { a?: string; greet?: string } | null;
      return j?.a?.toLowerCase() === address.toLowerCase() ? (j.greet ?? "") : "";
    } catch { return ""; }
  })();
  getProfile(address)
    .then((p) => {
      const g = (p.name ?? "").trim().split(/\s+/)[0] ?? "";
      if (!g && greetName) return; // 1.4.75 — a profile read that beat the new wallet's save doesn't erase "Hi <name>."
      greetName = g;
      try {
        const j = JSON.parse(localStorage.getItem(HOME_KEY) ?? "null") as { a?: string } | null;
        if (j?.a?.toLowerCase() === address.toLowerCase()) localStorage.setItem(HOME_KEY, JSON.stringify({ ...j, greet: greetName }));
      } catch { /* ignore */ }
      srApi?.repaint();
    })
    .catch(() => {});

  // v0.30 — pick the live source when the M-MMF contract addresses are wired,
  // otherwise the placeholder (zero position, refuses moves). Env-driven, so
  // flipping placeholder↔live is a config change, not a code change.
  const mmfAddr = import.meta.env.VITE_MMF_ADDRESS;
  const tellerAddr = import.meta.env.VITE_TELLER_ADDRESS;
  const earnSource: EarnSource =
    mmfAddr && tellerAddr
      ? new TellerEarnSource(adapter, {
          mmfAddress: mmfAddr,
          tellerAddress: tellerAddr,
          usdcAddress:
            import.meta.env.VITE_USDC_ADDRESS ??
            "0x3600000000000000000000000000000000000000",
          apiBase: import.meta.env.VITE_WORKER_URL ?? "",
        })
      : placeholderEarnSource;

  root.innerHTML = `
    <main class="screen home v18">
      <header class="home-strip">
        <!-- v0.90.14 — the globe where the M was: "currencies". Same class as
             the QR glyph opposite, so the two are one size by construction. -->
        <button id="stripGlobe" class="strip-qr strip-globe" type="button" aria-label="Global: your currencies" aria-pressed="false">${IC_GLOBE}</button>
        <!-- v1.1.21 — the balance pill in the header's centre (masked at rest,
             the pocket's figure while acting) and the Get paid toggle on the
             right, lit while the home asks rather than sends. -->
        <div class="strip-center"><div class="bal21-wrap rest" id="nucPill"><button class="bal21 masked" id="nucPillBtn" type="button" aria-label="Your balance">••••</button><button class="nuc-add" id="nucAdd" type="button" aria-label="Add money" hidden>${IC_ADD}</button></div></div>
        <!-- 1.4.94 (Alexander, 24 Oct) — Scan an M® QR, top right (the Send row's scan squircle moved here); Get paid went into the dock pill's menu -->
        <button id="stripScan" class="strip-qr strip-scan" type="button" aria-label="Scan an M® QR">${IC_SCAN}</button>
      </header>

      <div class="home-body">
        <div class="hero-top" id="heroTop">
          <!-- v0.90.14 — the balance pill is the resting state: the orb is
               retired and the pill (the panel's own) sits on its centreline
               with the pocket triangle beneath and the nucleus's halo behind.
               At rest it is masked and a tap opens the Balance panel (the
               "did my money arrive?" tap, v0.60.5). While an amount is typed
               (v0.80.37) it shows the pocket's figure and a tap drops the
               pocket list; Add appears beside it when a digit was refused. -->
          <div class="hero-greet" hidden>
            <p class="greet" id="heroGreet"></p>
            <p class="greet-sub" id="heroSub">${MONEY_QUESTION}</p>
          </div>
        </div>
        <div id="zeroActions" class="zero-stack" hidden>
          <button class="zact add" data-act="add" type="button"><span class="zi">${IC_ADD}</span><span class="zl">Add to M®</span></button>
          <button class="zact sr" data-act="sr" type="button"><span class="zi">${IC_ARROWS}</span><span class="zl">Send &amp; Request</span></button>
          <button class="zact grow" data-act="grow" type="button"><span class="zi">${IC_LEAF}</span><span class="zl">Grow your money</span></button>
        </div>
        <div id="srMount" hidden></div>
        <div id="swapMount" class="sw-mount" hidden></div>
      </div>

      <nav class="dock" id="dock" aria-label="Wallet navigation" hidden>
        <div class="dock-inner">
          <!-- 1.4.94 (Alexander, 24 Oct) — the dock redrawn: Wallet in a squircle, a split pill in the middle (the mode's
               word — Send · Get paid · Invest — and a ⌄ segment; a tap anywhere opens the mode menu; with an amount it
               is green: Next | ✕), Settings in a circle. Home ($) and Grow left the dock. A lit Wallet or Settings
               lights its whole shape (no sliding #dockPill any more). The flow paints #srNext / #srX / #srGoLbl. -->
          <div class="dock-wallet">
            <button data-dock="wallet" class="tab" type="button" aria-label="Wallet"><span class="ic">${IC_WALLET}</span></button>
          </div>
          <!-- 1.5.1 (Alexander, 24 Oct) — on Swap and Sending the dock is a muted ✕ in Wallet's squircle and the slider
               (the screen's own #swSlide / #srSlide, placed over the dock's slot by CSS); Settings and the pill step aside -->
          <div class="dock-x"><button class="tab" id="dockX" type="button" aria-label="Close">${IC_X}</button></div>
          <div class="dock-go" id="dockGo">
            <button id="srNext" class="dk-go" type="button"><span id="srGoLbl">Send</span></button>
            <button id="srX" class="dk-seg" type="button" aria-label="Send, Get paid or Invest">${IC_CHEV}</button>
            <div class="dock-menu" id="dockMenu" role="menu" hidden>
              <button class="dm-row" data-mode="send" role="menuitemradio" type="button">Send<span class="dm-tick">✓</span></button>
              <button class="dm-row" data-mode="receive" role="menuitemradio" type="button">Get paid<span class="dm-tick">✓</span></button>
              <button class="dm-row" data-mode="invest" role="menuitemradio" type="button">Invest<span class="dm-tick">✓</span></button>
              <!-- 1.5.3 (Alexander, 24 Oct) — Borrow, to come: muted, the lock on the right, a tap does nothing -->
              <button class="dm-row dm-locked" type="button" role="menuitem" aria-disabled="true" aria-label="Borrow — not open yet">Borrow<span class="dm-lk">${IC_LOCK_M}</span></button>
            </div>
          </div>
          <div class="dock-set">
            <button data-dock="more" class="tab" type="button" aria-label="Settings"><span class="ic">${IC_GEAR}</span></button>
          </div>
          <div class="dock-menu-scrim" id="dockMenuScrim" hidden></div>
        </div>
      </nav>

      <div id="scrim" class="scrim" hidden></div>
      <div id="sheet" class="sheet" hidden role="dialog" aria-modal="true"></div>
      <div id="popup" class="modal" hidden role="dialog" aria-modal="true"></div>
      <div id="toast" class="toast" hidden></div>
    </main>
  `;

  const $ = <T extends HTMLElement>(sel: string) => root.querySelector<T>(sel)!;
  homeEl = root.querySelector("main.home");

  // --- transient toast ------------------------------------------------------
  let toastTimer: number | undefined;
  function toast(msg: string, kind: "info" | "alert" = "info") {
    const t = $("#toast");
    // v0.70.24 — a "Couldn't…" notice carries the alert glyph (rose on Pure, amber on Midnight)
    if (kind === "alert" || /^Couldn/.test(msg)) t.innerHTML = `<span class="toast-ic">${IC_ALERT}</span><span>${escapeHtml(msg)}</span>`;
    else t.textContent = msg;
    t.classList.toggle("alert", kind === "alert" || /^Couldn/.test(msg));
    t.removeAttribute("hidden");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => t.setAttribute("hidden", ""), 1700);
  }

  // v1.1.9 — "Use 🦊 Lucy as your main wallet here?" Both answers equal; no ✕.
  // Shown once; leaving it unanswered keeps the wallet just used (as before).
  function askMainWallet() {
    let prev: PrevWallet | null = null;
    try {
      const raw = localStorage.getItem(ASK_KEY);
      localStorage.removeItem(ASK_KEY); // asked once, whatever happens next
      prev = raw ? (JSON.parse(raw) as PrevWallet) : null;
    } catch { /* storage blocked */ }
    if (!prev || prev.address.toLowerCase() === address.toLowerCase()) return;
    if (!root.isConnected || !$("#popup").hasAttribute("hidden") || !$("#sheet").hasAttribute("hidden")) return;
    const newEm = getEmoji(display);
    const paint = (newLabel: string) => {
      const oldLabel = prev!.label || (prev!.name ? titleCase(prev!.name) : "your other wallet");
      const s = openPopup(`
        <div class="mw-ask">
          <p class="mw-q">Use ${newEm} ${escapeHtml(newLabel)}<span>as your main wallet here?</span></p>
          <p class="mw-s">M opens with your main wallet on this browser, and Face ID asks for it first.</p>
          <div class="mw-btns">
            <button class="btn ghost" type="button" data-mw="yes">Yes, use ${newEm} ${escapeHtml(newLabel)}</button>
            <button class="btn ghost" type="button" data-mw="no">No, keep ${prev!.emoji} ${escapeHtml(oldLabel)}</button>
          </div>
        </div>`);
      $("#popup").classList.add("no-x");
      s.querySelector('[data-mw="yes"]')!.addEventListener("click", () => closePopup());
      s.querySelector('[data-mw="no"]')!.addEventListener("click", () => {
        try { localStorage.setItem(REVERT_KEY, JSON.stringify(prev)); } catch { /* storage blocked */ }
        closePopup();
        toast(`Next time, M opens with ${prev!.emoji} ${oldLabel} here.`);
      });
    };
    getProfile(address).then((p) => paint(p.name || titleCase(display))).catch(() => paint(titleCase(display)));
  }

  // --- generic bottom sheet -------------------------------------------------
  // Every sheet dismisses two ways, no "Close" button: tap the ✕, or pull the
  // top bar down. Returns the .sheet-body so callers can swap content without
  // disturbing the top bar / dismiss wiring.
  let sheetCleanup: number | undefined;
  // 1.4.90 (Alexander, 23 Oct: "most users come for just one function at a time") — closing a panel opened from
  // another (Contacts from Wallet, Mode from Settings) closes both: its ✕, the pull-down or a tap outside go home.
  // (1.4.86–1.4.89 returned to the parent through a `sheetBack` the opener set.)
  function leaveSheet() {
    closeSheet();
  }
  function openSheet(inner: string): HTMLElement {
    if (sheetCleanup !== undefined) {
      clearTimeout(sheetCleanup); // cancel a prior closeSheet wipe (open-from-sheet race)
      sheetCleanup = undefined;
    }
    const sheet = $("#sheet");
    sheet.innerHTML = `
      <div class="sheet-top"></div>
      <button class="sheet-x" type="button" aria-label="Close">${IC_X}</button>
      <div class="sheet-body">${inner}</div>`;
    $("#scrim").removeAttribute("hidden");
    sheet.removeAttribute("hidden");
    requestAnimationFrame(() => sheet.classList.add("up"));
    wirePullDown(sheet, sheet.querySelector<HTMLElement>(".sheet-top")!);
    sheet
      .querySelector(".sheet-x")!
      .addEventListener("click", () => leaveSheet());
    return sheet.querySelector<HTMLElement>(".sheet-body")!;
  }
  // v0.80.21 — a sheet opened from a popup can hand the user back to it on
  // close (Add money from Swap → back to Swap). One-shot; cleared on use.
  let sheetReturn: (() => void) | null = null;
  /** `stay` (1.4.86): the dock keeps its lit tab — the panel's parent is about to open from it. */
  function closeSheet(stay = false) {
    const back = sheetReturn;
    sheetReturn = null;
    if (back) setTimeout(back, 210);
    const sheet = $("#sheet");
    sheet.style.transition = "";
    sheet.style.transform = "";
    sheet.classList.remove("up");
    $("#scrim").setAttribute("hidden", "");
    if (!stay) setActiveDock(totalUsd() > 0 ? "sr" : ""); // sheet gone → keypad home lit (funded)
    sheetCleanup = window.setTimeout(() => {
      sheet.setAttribute("hidden", "");
      sheet.innerHTML = "";
      sheetCleanup = undefined;
    }, 200);
  }
  // Drag the top bar down to dismiss; release under the threshold springs back.
  function wirePullDown(sheet: HTMLElement, handle: HTMLElement) {
    let startY: number | null = null;
    let dy = 0;
    handle.addEventListener("pointerdown", (e) => {
      startY = e.clientY;
      dy = 0;
      sheet.style.transition = "none";
      handle.setPointerCapture(e.pointerId);
    });
    handle.addEventListener("pointermove", (e) => {
      if (startY == null) return;
      dy = Math.max(0, e.clientY - startY);
      sheet.style.transform = `translateY(${dy}px)`;
    });
    const end = () => {
      if (startY == null) return;
      sheet.style.transition = "";
      if (dy > 90) {
        leaveSheet(); // 1.4.86 — back to the parent panel, if any
      } else {
        sheet.style.transform = "";
      }
      startY = null;
      dy = 0;
    };
    handle.addEventListener("pointerup", end);
    handle.addEventListener("pointercancel", end);
  }
  // --- popup (centred modal; for top-strip triggers: balance / QR / Lab) ----
  // Its own dark backdrop + a corner ✕. Dismiss = tap ✕ or tap outside the card.
  // v0.80.2 — `back` makes ✕ (and the backdrop) step back to the panel that
  // opened this one instead of dropping the user on the home screen.
  function openPopup(inner: string, back?: () => void): HTMLElement {
    const m = $("#popup");
    m.classList.remove("top-anchored"); // v0.80.5 — only the balance panel re-adds it
    m.classList.remove("swap-screen", "no-x"); root.classList.remove("swapping"); // v1.1.9
    m.style.removeProperty("--panel-top"); m.style.removeProperty("--panel-origin");
    m.innerHTML = `
      <div class="modal-card">
        <button class="modal-x" type="button" aria-label="${back ? "Back" : "Close"}">${IC_X}</button>
        <div class="pop-body">${inner}</div>
      </div>`;
    m.removeAttribute("hidden");
    const leave = () => (back ? back() : closePopup());
    m.querySelector(".modal-x")!.addEventListener("click", leave);
    m.onclick = (e) => {
      // v1.1.9 — a screen (Swap) has no backdrop to tap: only its ✕ leaves.
      if (e.target === m && !m.classList.contains("swap-screen")) leave();
    };
    return m.querySelector<HTMLElement>(".pop-body")!;
  }
  // v0.90.31 — one centre line for the balance pill. The home pill (the
  // typing position is the benchmark; the resting pill now shares it) sits
  // in the page's flow and the panel is fixed to the viewport, so the panel's
  // top is set from where the home pill actually is: the panel's pill then
  // opens exactly over it, and grows out of that point. offsetTop, not
  // getBoundingClientRect, for the panel side — the card is mid-animation.
  function alignPanelPill(panelPill: HTMLElement) {
    const m = $("#popup");
    const home = document.querySelector<HTMLElement>("#nucPillBtn");
    const card = m.querySelector<HTMLElement>(".modal-card");
    if (!home || !card) return;
    const r = home.getBoundingClientRect();
    if (r.height === 0) return; // home pill not on screen: keep the default top
    let y = panelPill.offsetHeight / 2;
    for (let el: HTMLElement | null = panelPill; el && el !== card; el = el.offsetParent as HTMLElement | null) y += el.offsetTop;
    y += card.clientTop;
    const top = Math.max(8, Math.round(r.top + r.height / 2 - y));
    m.style.setProperty("--panel-top", `${top}px`);
    m.style.setProperty("--panel-origin", `${Math.round(y)}px`);
  }
  function closePopup() {
    const m = $("#popup");
    m.classList.remove("top-anchored", "swap-screen", "no-x");
    root.classList.remove("swapping"); // v1.1.9
    m.style.removeProperty("--panel-top"); m.style.removeProperty("--panel-origin");
    m.setAttribute("hidden", "");
    m.innerHTML = "";
  }

  // Backdrop of the bottom sheets closes them.
  $("#scrim").addEventListener("click", () => leaveSheet()); // 1.4.86 — a tap outside: back to the parent panel, if any

  // --- balance -------------------------------------------------------------
  // Masked on the strip (always "••••"); the number is revealed only inside the
  // balance top-panel, on a deliberate tap.
  let balanceText = "—";

  // --- copy link + QR (shared) ---------------------------------------------
  function copyLink() {
    if (!shareUrl) return;
    navigator.clipboard?.writeText(shareUrl).then(
      () => toast("Link copied"),
      () => toast("Long-press the link to copy"),
    );
  }
  function openQr() {
    if (!shareUrl) return;
    const svg = brandedQrSvg(shareUrl, 6, 2);
    openPopup(`
      <div class="qr-title">Get paid</div>
      <div class="qr-name">${escapeHtml(display)}</div>
      <div class="qr-holder">${svg}</div>
      <div class="qr-link">${escapeHtml(shareUrl.replace(/^https?:\/\//, ""))}<button id="qrCopy" class="sh-copy req-copy" type="button" aria-label="Copy link">${IC_COPY}</button></div>
      <div class="orsep">or</div>
      <button id="qrScan" class="scanbtn" type="button"><span class="si">${IC_SCAN}</span>Scan to pay</button>
    `);
    // v0.80.41 — Copy the M link, as on the Requesting card.
    $("#qrCopy").addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(shareUrl!);
        toast("Copied");
      } catch {
        toast("Long-press the link to copy");
      }
    });
    $("#qrScan").addEventListener("click", () => {
      closePopup();
      setTimeout(startScan, 180);
    });
  }

  // Open the live scanner; a valid M code resolves the recipient and drops the
  // user straight into Send, pre-filled.
  function startScan() {
    openScanner({
      onResult: (text) => {
        const link = parseMLink(text);
        if (!link) return false; // not an M code — keep scanning
        // v1.1.24 — a scanned M® QR opens the same "Paying" page as tapping its
        // link (Alexander, 4 Oct 21:40): who, the amount and the reference
        // as the link carries them. Same origin, so the wallet stays signed in.
        try {
          // 1.5.19 — M® Checkout's QR carries ?qr=1 (the success card then says the order is on the screen
          // that was scanned); nothing else from a scanned query is passed on.
          const u = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
          location.assign(u.pathname + (link.scanned ? `?qr=1${link.site ? `&site=${encodeURIComponent(link.site)}` : ""}` : ""));
        } catch {
          void routeScannedLink(link);
        }
        return true;
      },
      onClose: () => {},
    });
  }

  async function routeScannedLink(link: {
    slug: string;
    amount?: number;
    reference?: string;
  }) {
    try {
      const r = await resolveName(link.slug);
      srApi?.openSend({
        toAddress: r.address,
        toDisplay: titleCase(r.display),
        amount: link.amount,
        reference: link.reference,
      });
    } catch {
      toast("Couldn't reach that wallet — try again.");
    }
  }
  // v1.1.21 — Get paid: the home asks instead of sending ("How much to ask
  // for?", Get paid $X → the Requesting card; Show my QR at $0). Tapped
  // again, or the dock's centre, it sends again. The Get paid screens are 1.1.22.
  // 1.4.94 (Alexander, 24 Oct) — the mode lives in the dock pill's menu (Send · Get paid · Invest); the header's
  // Get paid toggle is gone, the scan squircle stands in its place.
  let homeMode: "send" | "receive" | "invest" = "send";
  $("#stripScan").addEventListener("click", () => { if (!$("#popup").hasAttribute("hidden")) closePopup(); startScan(); });
  const dockMenuOpen = () => !$("#dockMenu").hidden;
  function setDockMenu(open: boolean) {
    $("#dockMenu").hidden = !open;
    $("#dockMenuScrim").hidden = !open;
    $("#dockGo").classList.toggle("menu-open", open);
    if (open) root.querySelectorAll<HTMLButtonElement>("#dockMenu .dm-row[data-mode]").forEach((r) => r.setAttribute("aria-checked", String(r.dataset.mode === homeMode)));
  }
  $("#dockMenuScrim").addEventListener("click", () => setDockMenu(false));
  // 1.5.1 — the dock's ✕ on Swap (leaves Swap, at any amount) and on Sending (back to the amount)
  $("#dockX").addEventListener("click", () => {
    if (root.classList.contains("swap25")) root.querySelector<HTMLButtonElement>("#swX")?.click();
    else root.querySelector<HTMLButtonElement>("#scClose")?.click();
  });
  // 1.5.1 — the dock's slot (Wallet's squircle + 12 px to the right edge), for the screens' sliders placed over it
  const placeDockSlot = () => {
    const r = $(".dock-inner").getBoundingClientRect();
    if (!r.width) return;
    const st = document.documentElement.style;
    st.setProperty("--dk-l", `${r.left}px`); st.setProperty("--dk-r", `${innerWidth - r.right}px`); st.setProperty("--dk-b", `${innerHeight - r.bottom}px`);
  };
  requestAnimationFrame(placeDockSlot);
  addEventListener("resize", placeDockSlot);
  root.querySelectorAll<HTMLButtonElement>("#dockMenu .dm-row[data-mode]").forEach((r) => r.addEventListener("click", () => { // 1.5.3 — not Borrow
    setDockMenu(false);
    const m = r.dataset.mode as "send" | "receive" | "invest";
    if (!$("#popup").hasAttribute("hidden")) closePopup();
    closeSheet();
    if (globalOn && m !== "send") setGlobal(false); // v1.1.25 — Get paid is the sending home's other face, not Global's
    srApi?.setMode(m);
  }));
  // the pill: with an amount the word is Next and the segment ✕; at rest both open the menu
  $("#srNext").addEventListener("click", () => { if ($("#srNext").dataset.has) srApi?.go(); else setDockMenu(!dockMenuOpen()); });
  $("#srX").addEventListener("click", () => { if ($("#srNext").dataset.has) srApi?.clear(); else setDockMenu(!dockMenuOpen()); });

  // --- balance --------------------------------------------------------------
  // 1.4.20 (Alexander, 16 Oct: "upon login, in Safari, sometimes the keypad
  // does not come on; logging out and in doesn't help, closing the tab does")
  // — a read that never answers. iOS Safari can leave a fetch hanging for good
  // after the tab was in the background, and Circle's transport put no clock on
  // it; with `m.lastTotal` at 0 the home waited for that answer before showing
  // anything, and a new login in the same tab met the same stuck transport.
  // Now every read is given READ_TIMEOUT_MS; a read that fails or times out is
  // retried (READ_RETRY_MS, while this home is live) and never writes the
  // last-known total — and the home no longer waits for it at all (see
  // applyHomeState: an unknown balance looks funded, the pill is masked).
  const READ_TIMEOUT_MS = 8_000;
  const READ_RETRY_MS = 5_000;
  const timed = <T>(p: Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const t = window.setTimeout(() => reject(new Error("read timed out")), READ_TIMEOUT_MS);
      p.then((v) => { window.clearTimeout(t); resolve(v); }, (e) => { window.clearTimeout(t); reject(e); });
    });
  let readRetry = 0;
  let spareChecked = false; // 1.5.45 — the Spare Key prompt's reads, once per home
  async function refreshBalance() {
    _lastRefreshAt = Date.now();
    window.clearTimeout(readRetry);
    void refreshPockets(); // in parallel — the USD read must not wait on it
    let read = true;
    try {
      const b = await timed(adapter.getBalance(address));
      currentBalance = Number(b) || 0;
      balanceText = formatUsdc(b);
    } catch {
      try {
        const w = await timed(getWallet(address));
        currentBalance = Number(w.usdcBalance ?? 0) || 0;
        balanceText = w.usdcBalance ? formatUsdc(w.usdcBalance) : "—";
      } catch {
        balanceText = "—";
        read = false;
      }
    }
    try {
      earnUsd = (await timed(earnSource.getPosition(address))).usd;
    } catch {
      earnUsd = 0;
    }
    if (read) noteMoneyMoved(); // 1.4.47 — the balance moved: the statement may be behind it
    if (!read) {
      // Unknown still: keep what the home shows, ask again shortly.
      readRetry = window.setTimeout(() => { if (homeEl && root.contains(homeEl)) void refreshBalance(); }, READ_RETRY_MS);
      applyHomeState();
      return;
    }
    assumeFunded = false; // the read is in; from here the truth decides
    balanceRead = true;
    checkWelcome();
    try { localStorage.setItem("m.lastTotal", String(totalUsd())); } catch { /* ignore */ }
    applyHomeState();
    // 1.5.45 (HL-17) — once the balance is known, today's reads for the Spare Key prompt (Arc's key list, the
    // statement's count), once a day, behind the home; the line repaints if they change it.
    if (!spareChecked) {
      spareChecked = true;
      void refreshSparePrompt(address, { readContract: (a) => adapter.read(a.address, a.abi, a.functionName, a.args) }, totalUsd, () => srApi?.repaint());
    }
  }

  // --- Send / Receive money flow (v0.17.2, unchanged) ----------------------
  let lastUse = 0;
  srApi = renderSendReceive($("#srMount"), {
    adapter,
    address,
    name,
    getAvailable: () => pocketBalance(activeCurrency()),
    getCurrency: activeCurrency,
    getTotal: totalUsd,
    // v0.90.19 — the ladder of sources: rung 3 converts a shortfall from
    // another live pocket inside the payment's own userOp.
    fx: fxSource,
    ladder: {
      pockets: () => Object.fromEntries(liveCurrencies().map((c) => [c, pocketBalance(c)])),
      defaultPocket: defaultCurrency,
    },
    onAddMoney: () => addSheet(),
    onDropComplete: () => {
      // v1.1.30 — a payment in this pocket counts towards the pockets row's
      // third pill (once per payment: the callback can fire twice for one).
      const used = activeCurrency();
      if (Date.now() - lastUse > 15000) { notePocketUse(used); lastUse = Date.now(); }
      // v0.80.32 — (these two were meant to land in .29/.30 but the edit never
      // matched this one-liner.) A payment just landed: don't serve a cached
      // statement, and "just for one transaction" is over — back to the default pocket.
      statementDirty = true;
      setSessionCurrency(null);
      void refreshBalance();
      srApi?.refreshAvailable();
    },
    onActivity: (active) => {
      root.classList.toggle("acting", active);
      paintNucPill();
    },
    onOver: (over) => {
      root.classList.toggle("over", over); // v1.1.21 — the pill turns alert; the conversation offers Add money
      paintNucPill(); // v1.3.5 — and shows the balance it passed
    },
    onCard: (on) => root.classList.toggle("focus", on), // v0.60.18 (S8) — fade strip + dock behind the card
    // v1.1.21 — the conversation greets by the name on the profile ("Hi Alex.").
    greetName: () => greetName,
    onMode: (m) => { homeMode = m; root.classList.toggle("getpaid", m === "receive"); }, // 1.5.19 — Get paid is an action screen: the lit material (home21.css)
    dockGo: { btn: $("#srNext"), x: $("#srX"), lbl: $("#srGoLbl") }, // 1.4.94
    onScan: () => startScan(),
    onPocket: (code) => pickPocket(code),
    onPocketList: (code, pick) => pocketSlotList(code, pick), // v1.1.30 — your pockets only
    onSwap: () => openSwap(activeCurrency()),
    onCheckMoney: () => activitySheet(),
    // 1.5.45 (HL-17) — the Spare Key prompt in the opening line's place (sparePrompt.ts); its phrase opens the Spare Key screen.
    sparePrompt: () => sparePromptDue(address, totalUsd()),
    onSparePromptShown: () => sparePromptShown(address),
    onSparePrompt: () => openSpareKeyScreen(),
    // v1.3.0 — the opening line: "Your money's doing well." only where there is
    // money; the welcome money of a wallet just made with an invite.
    // 1.4.21 — the opening line at once: before the first read, the last known
    // total decides (an empty wallet that was empty last time hears "Add money"
    // at once); nothing known at all = funded ("Your money's doing well.") —
    // the read corrects it within the second if not.
    funded: () => (balanceRead ? totalUsd() > 0 : assumeFunded ? true : lastWasZero ? false : true),
    welcomeMoney,
    welcomeNote: () => welcomeNoteText, // 1.4.57
    onShowQr: () => openQr(),
    onLift: (on) => root.classList.toggle("lifted", on), // v0.70.16 — To field focused: block to the top
    prefill: sendPrefill,
  });
  // 1.5.59 — the Timelock's stand-in (claude/timelock.md §8): on only where this wallet's owner set it up on this device.
  // The home reads the Worker's view, repaints the pill each second while a hold is open, and the send flow its line.
  tlStart(address);
  {
    const mine = $("#nucPillBtn"); // #app outlives every home; this home's pill doesn't
    let tick: number | undefined;
    const off = onTimelock(() => {
      if (!mine.isConnected) { off(); window.clearInterval(tick); return; }
      paintNucPill();
      if (tlPrimary() && tick === undefined) tick = window.setInterval(() => { if (!mine.isConnected) { window.clearInterval(tick); return; } paintNucPill(); }, 1000);
      if (!tlPrimary() && tick !== undefined) { window.clearInterval(tick); tick = undefined; }
    });
  }
  if (sendPrefill) {
    setTimeout(
      () => $("#srMount").scrollIntoView({ block: "start", behavior: "smooth" }),
      80,
    );
  }

  // ===================== HOME (balance-gated) ==============================
  // Zero balance → person-first onboarding: greeting · nucleus ($0.00) · three
  // colour-coded actions, no dock. Funded → the amount keypad is home, with the
  // greeting + nucleus (balance folded in) above it and the dock below.
  $("#heroGreet").textContent = greetingFor();

  // (v0.90.18's `assumeFunded` — the last known state paints the funded home
  // at once — is declared at the top since v1.3.0.)
  // v1.1.21 — the empty wallet has the same home: the conversation asks "Add
  // money to get started?" and the dock's Add is in the alert colour. (The
  // three-tile empty state is retired.)
  let flowShown = true;
  // 1.4.20 — "zero" only once a read has said so: until then the home is the
  // funded one (keypad, dock, the pill masked) and the money question waits.
  const knownZero = () => balanceRead && totalUsd() <= 0 && !assumeFunded;
  function applyHomeState() {
    const zero = knownZero();
    root.classList.toggle("home-zero", zero);
    root.classList.toggle("home-funded", !zero);
    paintNucPill(); // zero is never masked; funded rests on •••• (v0.90.14)
    $("#heroSub").hidden = !zero; // the money-question frames the empty state
    $("#zeroActions").hidden = !zero;
    $("#srMount").hidden = (zero && !flowShown) || root.classList.contains("swap25"); // v1.1.29 — never under the Swap screen
    $("#dock").hidden = zero && !flowShown;
    paintAddAlert();
    if (zero && !flowShown) {
      setActiveDock("");
    } else {
      srApi?.refreshAvailable();
      setActiveDock("sr");
    }
  }

  // From the empty state, "Send & Request" reveals the amount step (Cash, the
  // default since 1.1.12). v1.1.12 — the dock comes with it, so Add is there
  // to steer to: in the alert colour while nothing has been added.
  function showFlow() {
    flowShown = true;
    $("#zeroActions").hidden = true;
    $("#srMount").hidden = false;
    $("#dock").hidden = false;
    setActiveDock("sr");
    paintAddAlert();
    srApi?.open();
  }
  /** v1.1.12 — not funded: the dock's Add in the alert colour from the start. 1.4.85: Add left the dock, so nothing to
   *  paint; the home's "Add money to get started?" says it. Kept as a no-op for its two callers. */
  function paintAddAlert() { /* 1.4.85 — no Add in the dock */ }

  root.querySelectorAll<HTMLButtonElement>(".zact").forEach((b) => {
    b.addEventListener("click", () => {
      const act = b.dataset.act;
      if (act === "add") addSheet();
      else if (act === "sr") showFlow();
      else if (act === "grow") earnSheet();
    });
  });
  // v0.70.5 — an emoji picked before profiles existed lives only on this
  // device; push it to the server once so others see it too.
  try {
    const pick = getEmojiOverride();
    if (pick && !localStorage.getItem("m.emojiSynced")) {
      getProfile(address)
        .then((p) => (p.emoji ? null : setProfile({ address, emoji: pick })))
        .then(() => localStorage.setItem("m.emojiSynced", "1"))
        .catch(() => {});
    }
  } catch {
    /* storage blocked — cosmetic */
  }
  // v0.70.8 — pull Drops once at mount too (login): the Worker syncs them with
  // Arc on that read, so the first panel open is already fresh. v0.70.13: the
  // result is kept so the panel can paint instantly and refresh in place.
  getMyDrops(address)
    .then((m) => (dropsCache = m))
    .catch(() => {});
  // v0.70.12 — names are device-only again (no server alias book).
  // v0.70.18 — warm the profile cache for everyone in Recents/nicknames so
  // labels fall back to their own name, never a bare code.
  void warmProfiles([...readRecent().map((r) => r.address), ...listNicks().map((n) => n.address)]);

  // Tap the nucleus → reveal the balance (and the Arc link).
  // v0.80.37 — the morphed nucleus: the pocket's balance in the panel's pill.
  // v1.3.4 — the dock's centre follows the default pocket (set by press-and-hold
  // in the pockets panel or the pills' list, or arriving with the synced settings).
  // 1.4.94 — the dock's centre is the mode pill now; the $ glyph (icHome) went with the Home tab.
  function paintNucPill() {
    const btn = $("#nucPillBtn");
    const acting = root.classList.contains("acting") || root.classList.contains("global"); // v1.1.25 — the lit globe shows the pocket's balance
    $("#nucPill").classList.toggle("rest", !acting); // v0.90.14 — no triangle at rest
    if (!acting) { root.classList.remove("over"); $("#nucAdd").hidden = true; }
    const code = activeCurrency();
    // v1.3.5 (Alexander, 7 Oct) — the balance is shown only where it is asked
    // for: the Balance panel (a tap on the pill), the pockets panel, Activity —
    // and here only when an amount passes it (amber). Typing, Global and an
    // empty wallet keep the bullets; the triangle still says "a pocket list".
    // 1.4.48 (Alexander, 19 Oct) — at rest the bullets hide a balance only when there is one to hide
    // and the person has been here before: an empty wallet shows its $0.00, and the first home in this
    // browser shows the figure (a first-timer doesn't see •••• as something to tap). From the next
    // login, and once an amount passes the balance (amber), the rules above.
    // 1.4.67 (Alexander, 21 Oct) — and only when the person has asked for it: "Mask my balance" in More. Until
    // then the pill rests open, as on the first home; typing and Global keep the bullets (the pill is the
    // currency list there), and an amount past the balance still shows it in amber.
    // 1.5.3 (Alexander, 24 Oct) — with "Mask my balance" off the pill is open everywhere: typing, Globe, Swap and Sending
    // show the figure too (the pocket in hand's). With it on, as before: the bullets at rest and while acting.
    const openAtRest = balanceRead && (!maskBalance() || (!acting && (totalUsd() <= 0 || !!firstLogin)));
    const knownEmpty = !acting && !balanceRead && !!justMade; // 1.4.75 — a wallet made a moment ago holds nothing yet
    // 1.5.34 (Alexander, 29 Oct) — a chevron inside the pill, under the figure: ⌄ here (a tap opens the panel, or the list)
    const face = (t: string) => { btn.innerHTML = `<span class="b21-v">${t}</span><span class="b21-ch" aria-hidden="true">${IC_CHEV}</span>`; };
    // 1.5.59 — the Timelock (claude/timelock.md §6.1): while a hold is open the pill is its countdown — held: the hourglass
    // in the theme's alert colour, the digits neutral; released: the clock, all neutral; past its time and not due:
    // Confirming…, with no number (never a number a Mayday or a wrong word alone would show); due: Ready.
    const tlh = !acting ? tlPrimary() : null;
    if (tlh) {
      const ph = phaseOf(tlh, tlNow());
      const words = ph === "held" || ph === "released" ? countdown(tlh.goesAt - tlNow()) : ph === "ready" ? "Ready" : "Confirming…";
      btn.innerHTML = `<span class="b21-v tl"><span class="tl-g${ph === "held" ? " warn" : " rd"}" aria-hidden="true">${ph === "held" ? IC_TIMELOCK : IC_RELEASE_DELAY}</span>${words}</span><span class="b21-ch" aria-hidden="true">${IC_CHEV}</span>`;
      btn.classList.remove("masked");
      btn.setAttribute("aria-label", ph === "ready" ? "A held payment is ready" : "A held payment");
      btn.style.fontSize = "";
      placeNucAdd();
      return;
    }
    if (root.classList.contains("over") || openAtRest || knownEmpty) {
      face(code === BASE_CURRENCY ? `$${formatCents(currentBalance)}` : formatFx(code, pocketBalance(code)));
      btn.classList.remove("masked");
      btn.setAttribute("aria-label", acting ? "Choose a currency" : "Your balance");
    } else {
      face("••••"); // funded balances rest masked; tap to see
      btn.classList.add("masked");
      btn.setAttribute("aria-label", acting && !root.classList.contains("global") ? "Choose a currency" : "Your balance");
    }
    // v0.90.31 — the same fixed 190px face as the Balance panel's pill, so
    // the same rule as its fit(): 12 chars → 16px, 10–11 → 18px, else 22px.
    const n = (btn.textContent ?? "").length; // the figure alone: the chevron is an icon
    btn.style.fontSize = n > 11 ? "16px" : n > 9 ? "18px" : "";
    placeNucAdd();
  }
  // 1.4.67 — "Mask my balance" changed in another browser arrives with the synced settings (main.ts): repaint the
  // pill while this home is the live one; a home that has been replaced unhooks itself.
  {
    const pillBtn = $("#nucPillBtn");
    const onMask = () => { if (pillBtn.isConnected) paintNucPill(); else window.removeEventListener("m:mask", onMask); };
    window.addEventListener("m:mask", onMask);
  }
  // v0.90.34 — Add sits on the face's centre line, measured rather than
  // derived: its top is the face's own middle within the wrapper (offsetTop
  // ignores the wrapper's centring transform), so it can't drift with the
  // slab, the triangle's gap or the font.
  function placeNucAdd() {
    const add = document.querySelector<HTMLElement>("#nucAdd");
    const face = document.querySelector<HTMLElement>("#nucPillBtn");
    const slab = face?.parentElement;
    if (!add || !face || !slab || add.hidden) return;
    add.style.top = `${slab.offsetTop + face.offsetTop + face.offsetHeight / 2}px`;
  }
  // Tap the pill: at rest → the Balance panel (+ a fresh read from Arc);
  // while typing → the pocket list, dropped under it (the panel's component).
  $("#nucPillBtn").addEventListener("click", () => {
    // 1.5.59 — the held card, when the pill is its countdown. 1.5.60: only then — in Globe mode the pill shows the pocket's
    // balance, and its tap is the Balance panel as always (it had opened the held card: the pockets were out of reach).
    if (!root.classList.contains("acting") && !root.classList.contains("global") && tlPrimary()) { srApi?.openHold(); return; }
    // v1.1.25 — with the globe lit the pockets are on the row, so the pill is
    // the balance and its ledger only (as at rest).
    if (!root.classList.contains("acting") || root.classList.contains("global")) {
      balancePanel();
      // v0.60.5 — the "did my money arrive?" tap: pull a fresh balance from
      // Arc and patch the panel figure in place (no spinner).
      void refreshBalance().then(() => {
        const el = $("#popup").querySelector<HTMLElement>('[data-b="earn-amt"]');
        if (el && !$("#popup").hasAttribute("hidden")) {
          el.textContent = `$${formatCents(currentBalance + earnUsd)}`;
        }
      });
      return;
    }
    const open = $("#heroTop").querySelector(".pk-menu");
    if (open) { open.remove(); return; }
    const codes = enabledCurrencies();
    const cur = activeCurrency();
    const menu = document.createElement("div");
    menu.className = "pk-menu nuc-menu";
    const shown = (c: string) => (c === BASE_CURRENCY ? `$${formatCents(currentBalance)}` : formatFx(c, pocketBalance(c)));
    menu.innerHTML = codes
      .map(
        (c) => `<button class="pk-mrow ${c === cur ? "on" : ""}" type="button" data-code="${c}">
          <span class="pk-mflag ${c === defaultCurrency() ? "def" : ""}">${currency(c).flag}</span>
          <span class="pk-mname">${currency(c).name}</span>
          <span class="pk-mamt">${shown(c)}</span>
        </button>`,
      )
      .join("");
    menu.querySelectorAll<HTMLButtonElement>(".pk-mrow").forEach((r) =>
      r.addEventListener("click", () => {
        const code = r.dataset.code!;
        setSessionCurrency(code === defaultCurrency() ? null : code); // same rule as the panel
        menu.remove();
        // v0.80.38 — the session continues: the typed amount stays, the pill
        // re-reads for the new pocket, and the keypad's symbol follows. If the
        // amount no longer fits the pocket, refreshAvailable flags it (alert + Add).
        srApi?.refreshAvailable();
        paintNucPill();
      }),
    );
    $("#nucPill").insertAdjacentElement("afterend", menu);
    const outside = (e: Event) => {
      const t = e.target as Element | null;
      if (t && (t.closest(".nuc-menu") || t.closest("#nucPillBtn"))) return;
      menu.remove();
      document.removeEventListener("pointerdown", outside, { capture: true });
    };
    setTimeout(() => document.addEventListener("pointerdown", outside, { capture: true }), 0);
  });
  $("#nucAdd").addEventListener("click", () => addSheet());

  // Arriving from a pay link: open the flow straight away.
  if (sendPrefill) {
    $("#zeroActions").hidden = true;
    $("#srMount").hidden = false;
  }
  // 1.4.88 — arriving from a short balance's Add money: the sheet, once the home has painted under it.
  if (openAdd) setTimeout(() => addSheet(), 400); // closing it leaves the home
  if (openCardSet) setTimeout(() => { cardSheet(true); setActiveDock("more"); }, 400); // 1.5.47 — closing it leaves the home

  // ===================== SHEET CONTENTS ====================================

  // v0.80 — the balance panel is a carousel of CURRENCY POCKETS. The whole
  // panel body swipes (Recent transactions are per-currency, so they travel
  // with their page); dots sit at the bottom; the flag glyph top-left opens
  // the currency list. The pocket is the currency — no token symbol ever
  // reaches a surface (decisions-log, M-FX).
  function balancePanel(startAt = -1) {
    const known = balanceText && balanceText !== "—";
    let codes = enabledCurrencies();
    // v0.80.13 — the panel opens on the pocket set as default (long-press in
    // the currency list); `startAt` only overrides it when stepping back from
    // a sheet, so you return to the pocket you were on.
    const home = Math.max(0, codes.indexOf(activeCurrency())); // v0.80.34 — the session pocket while one is live
    let index = startAt < 0 ? home : Math.min(startAt, codes.length - 1);

    const shownFor = (code: string) =>
      code === BASE_CURRENCY
        ? known
          ? `$${formatCents(currentBalance + earnUsd)}`
          : "—"
        : formatFx(code, pocketBalance(code));

    // v0.80.3 — no swiping. The pill IS the pocket selector: tap it and pick
    // from the pockets you've added. Gesture conflicts with the panel's own
    // vertical scroll disappear, and the target is the biggest thing on screen.
    // The pill stays centred in the panel; the glyphs hang either side of it.
    const s = openPopup(`
      <div class="bal-lead" id="pkLead">
        <span>Your current balance is</span>
      </div>
      <div class="bal-row pk-row">
        <span class="bal-side left">
          <!-- 1.4.16 — no Swap glyph here any more (Swap is the globe's screen and the list's button); the
               slot stays, unseen, so the pill keeps its centre. -->
          <span class="bal-ic away" aria-hidden="true">${IC_SWAP_UD}</span>
        </span>
        <span class="pk-pillcol">
          <button class="bal-pill-big" id="pkPill" type="button" aria-label="Choose a currency"><span class="bp-v">${shownFor(codes[index])}</span><span class="bp-ch" aria-hidden="true"><span class="bp-dn">${IC_CHEV}</span><span class="bp-up">${IC_CHEV}</span></span></button>
        </span>
        <span class="bal-side right">
          <button class="bal-ic" data-b="add" type="button" aria-label="Add money">${IC_ADD}</button>
        </span>
      </div>
      <div id="pkBody"></div>
    `);

    // v0.80.4 — top-anchored: the panel's top edge is at the same height for
    // every pocket (an empty pocket used to float lower, being shorter).
    $("#popup").classList.add("top-anchored");
    const pill = s.querySelector<HTMLButtonElement>("#pkPill")!;
    alignPanelPill(pill);
    const bodyBox = s.querySelector<HTMLElement>("#pkBody")!;

    // v0.90.14 — the lead carries no glyph any more: currencies are the
    // strip's globe. (v0.80.14's flag-alignment code went with it.)
    // The figure shrinks rather than pushing the glyphs off their slots.
    // 1.5.34 (Alexander, 29 Oct) — the chevrons inside the pill, under the figure: ⌄⌄ while the pockets' list is shut (a
    // tap opens it), ⌃ while it shows (a tap closes it); the home's pill carries the single ⌄. (The triangle under the
    // pill, v0.80.3–1.5.33, went.)
    const figure = pill.querySelector<HTMLElement>(".bp-v")!;
    const fit = () => {
      // v0.90.16 — the column is fixed at 190px (fits `CHF99,999.00`), so only
      // the figure shrinks: 12 chars → 16px, 10–11 → 18px, else the full 22px.
      const n = figure.textContent?.length ?? 0;
      pill.style.fontSize = n > 11 ? "16px" : n > 9 ? "18px" : "";
    };

    function paint() {
      const code = codes[index];
      panelCode = code; // 1.4.47 — which pocket's statement the open panel shows
      figure.textContent = shownFor(code);
      fit();
      // v0.80.32 — every pocket page is built the same way: its own live Drops
      // (pending / claimed, filtered by the Drop's currency) and its own
      // statement (filtered by `?currency=`). The USD page used to be the only
      // one with Drops, and its statement was unfiltered — so a euro Drop
      // showed in the wrong pocket while pending and in both once released.
      bodyBox.innerHTML = `<div id="balDrops"></div>`;
      void fillDropsRows(bodyBox.querySelector<HTMLElement>("#balDrops")!, code);
    }
    paint();

    // Tap the pill → your pockets, as a list under it. 1.4.15 (Alexander, 16 Oct): the list is the
    // Globe's panel's — the same rows (hold = the default, a left swipe closes an empty pocket), the
    // hint and Swap · Add more at its foot; while it shows, the Swap glyph beside the pill and the
    // pocket's statement below step aside. A tap on a row picks that pocket and closes the list.
    // (1.1–1.4.14: a short flag · name · amount menu, no hint, no buttons.)
    const closeList = () => {
      s.querySelector(".pk-menu21")?.remove();
      bodyBox.hidden = false;
      pill.classList.remove("open");
    };
    pill.addEventListener("click", () => {
      if (s.querySelector(".pk-menu21")) { closeList(); return; }
      const menu = document.createElement("div");
      menu.className = "pk-menu21";
      menu.innerHTML = `<div class="pk-menu"></div>${POCKET_HINT}${pocketActs(["pkListSwap", "pkListAdd"])}`; // 1.4.16: the short rows in their box
      s.querySelector(".pk-row")!.insertAdjacentElement("afterend", menu);
      bodyBox.hidden = true;
      pill.classList.add("open");
      pocketRows(menu.querySelector<HTMLElement>(".pk-menu")!, {
        simple: true,
        current: () => codes[index] ?? null,
        onPick: (code) => {
          index = Math.max(0, codes.indexOf(code));
          // v0.80.34 — an active pick starts the session (opening the panel never does); picking the
          // default ends it, so no phantom lingers.
          setSessionCurrency(code === defaultCurrency() ? null : code);
          srApi?.refreshAvailable(); // keypad symbol + balance follow
          closeList();
          paint();
        },
        onChange: () => { codes = enabledCurrencies(); index = Math.max(0, codes.indexOf(activeCurrency())); figure.textContent = shownFor(codes[index]); fit(); },
      });
      menu.querySelector("#pkListSwap")!.addEventListener("click", () => swapSheet(codes[index] ?? BASE_CURRENCY, index));
      // 1.4.16 — after Add more the panel opens on the new pocket.
      menu.querySelector("#pkListAdd")!.addEventListener("click", () =>
        currencyListPopup((added) => {
          if (added) { setSessionCurrency(added === defaultCurrency() ? null : added); srApi?.refreshAvailable(); }
          balancePanel(added ? Math.max(0, enabledCurrencies().indexOf(added)) : index);
        }),
      );
    });

    s.querySelector<HTMLButtonElement>('[data-b="add"]')!.addEventListener("click", () => {
      closePopup();
      setTimeout(addSheet, 210);
    });
  }

  // v1.1.25 — Swap is a screen of the home (swapScreen.ts): the lit globe
  // stays on while it shows, and tapping the globe leaves it.
  function swapSheet(from: string, _back: number) {
    closePopup();
    openSwap(from);
  }

  // ===================== v1.1.25 — GLOBAL ==================================
  let globalOn = false;
  let swapH: SwapScreenHandle | null = null;
  function setGlobal(on: boolean) {
    globalOn = on;
    const g = $("#stripGlobe");
    g.classList.toggle("lit", on);
    g.setAttribute("aria-pressed", String(on));
    root.classList.toggle("global", on);
    if (!on) {
      closeSwap();
      setSessionCurrency(null); // back on the default pocket
    } else if (homeMode === "receive") {
      srApi?.setMode("send"); // the pockets row is the sending home's
    }
    srApi?.setGlobal(on);
    srApi?.refreshAvailable();
    paintNucPill();
  }
  /** A pocket pill (or a row of the panel or the list): the home works in it now. */
  function pickPocket(code: string) {
    setSessionCurrency(code === defaultCurrency() ? null : code);
    srApi?.refreshAvailable();
    paintNucPill();
  }
  function openSwap(from: string) {
    if (!globalOn) setGlobal(true);
    closeSheet();
    if (!$("#popup").hasAttribute("hidden")) closePopup();
    swapH?.close();
    $("#srMount").hidden = true;
    const mount = $("#swapMount");
    mount.hidden = false;
    root.classList.add("acting", "swap25");
    swapH = renderSwapScreen(mount, {
      from,
      source: fxSource,
      balanceOf: pocketBalance,
      onDone: () => { notePocketUse(activeCurrency()); closeSwap(); statementDirty = true; void refreshBalance(); },
      onClose: () => closeSwap(), // v1.1.31 — the globe stays lit: the home in Global mode
      onSource: (code) => { setSessionCurrency(code === defaultCurrency() ? null : code); paintNucPill(); },
      onOver: (over) => { root.classList.toggle("over", over); paintNucPill(); },
      onAddMoney: () => addSheet(), // 1.4.86 — a sheet over Swap; closing it leaves Swap as it was
      toast,
      explorerUrl: (tx) => adapter.explorerUrl(tx),
    });
    paintNucPill();
  }
  function closeSwap() {
    if (!swapH) return;
    swapH.close();
    swapH = null;
    $("#swapMount").hidden = true;
    $("#srMount").hidden = false;
    root.classList.remove("swap25", "over");
    srApi?.refreshAvailable();
    srApi?.repaint();
    paintNucPill();
  }

  // The currency list: the current one first (accent), every other currency
  // alphabetically by full name. A pick adds the pocket if need be and makes
  // it the one the home works in.
  // 1.4.16 (Alexander, 16 Oct) — a currency you don't hold yet asks first: a line under its row with ✕ and
  // Add (the shape of the pocket list's Keep / Close), so a stray tap opens no pocket. `back` gets the code
  // it opened, if any, so the Balance panel can stay on it.
  function currencyListPopup(back?: (added?: string) => void) {
    const now = activeCurrency();
    const rest = CURRENCIES.filter((c) => c.code !== now).sort((a, b) => a.name.localeCompare(b.name));
    const row = (code: string, name: string, on: boolean) =>
      `<button type="button" class="opt21${on ? " on" : ""}" data-code="${code}"><b>${code}</b><span>${escapeHtml(name)}</span></button>`;
    const s = openPopup(`<div class="sheet-title">Currency</div><div class="opt21-list">${row(now, currency(now).name, true)}${rest.map((c) => row(c.code, c.name, false)).join("")}</div>`, back ? () => back() : undefined);
    $("#popup").classList.add("list21");
    const go = (code: string, added: boolean) => {
      if (back) { srApi?.setGlobal(globalOn); back(added ? code : undefined); return; } // from Add more: the new pocket shows in the panel
      closePopup();
      pickPocket(code);
      srApi?.setGlobal(globalOn); // the row repaints with it
    };
    s.querySelectorAll<HTMLButtonElement>(".opt21").forEach((o) =>
      o.addEventListener("click", () => {
        const code = o.dataset.code!;
        if (enabledCurrencies().includes(code)) { go(code, false); return; }
        s.querySelector(".pk21-confirm")?.remove();
        const ask = document.createElement("div");
        ask.className = "pk21-confirm add";
        ask.innerHTML = `<span>Open a ${escapeHtml(currency(code).name)} pocket?</span><button type="button" data-keep="1" aria-label="Not now">${IC_X}</button><button type="button" class="go2" data-add="${code}">Add</button>`;
        o.insertAdjacentElement("afterend", ask);
        ask.querySelector("[data-keep]")!.addEventListener("click", () => ask.remove());
        ask.querySelector("[data-add]")!.addEventListener("click", () => { setCurrencyEnabled(code, true); go(code, true); });
      }),
    );
  }

  // v1.1.30 — pill 2's or pill 3's list: your pockets only (not the default,
  // which is pill 1), the pill's own first in the accent colour, the rest by
  // full name. A pick sets that pill until another is picked there.
  function pocketSlotList(now: string, pick: (code: string) => void) {
    const def = defaultCurrency();
    const rest = enabledCurrencies()
      .filter((c) => c !== now && c !== def)
      .map((c) => currency(c))
      .sort((a, b) => a.name.localeCompare(b.name));
    // v1.1.32 — a left swipe on a row reveals Close: an empty pocket leaves the
    // list (and the wallet's pockets); the default and USD stay.
    const row = (code: string, name: string, on: boolean) =>
      `<div class="opt21-sw" data-code="${code}"><button type="button" class="opt21${on ? " on" : ""}" data-code="${code}"><b>${code}</b><span>${escapeHtml(name)}</span></button><button type="button" class="opt21-del" data-del="${code}">Close</button></div>`;
    const s = openPopup(`<div class="sheet-title">Your pockets</div><div class="opt21-list">${row(now, currency(now).name, true)}${rest.map((c) => row(c.code, c.name, false)).join("")}</div>`);
    $("#popup").classList.add("list21");
    s.querySelectorAll<HTMLElement>(".opt21-sw").forEach((w) => {
      const btn = w.querySelector<HTMLButtonElement>(".opt21")!;
      let x0 = 0, dx = 0, swiping = false;
      w.addEventListener("pointerdown", (e) => { x0 = e.clientX; dx = 0; swiping = false; });
      w.addEventListener("pointermove", (e) => {
        if (!x0) return;
        dx = e.clientX - x0;
        if (Math.abs(dx) > 8) swiping = true;
        if (swiping) btn.style.transform = `translateX(${Math.max(-88, Math.min(0, dx + (w.classList.contains("open") ? -88 : 0)))}px)`;
      });
      const settle = () => {
        if (!x0) return;
        const open = w.classList.contains("open") ? dx < 30 : dx < -40;
        w.classList.toggle("open", open);
        btn.style.transform = open ? "translateX(-88px)" : "";
        x0 = 0;
      };
      w.addEventListener("pointerup", settle);
      w.addEventListener("pointercancel", settle);
      btn.addEventListener("click", () => {
        if (swiping) { swiping = false; return; }
        if (w.classList.contains("open")) { w.classList.remove("open"); btn.style.transform = ""; return; }
        closePopup();
        pick(btn.dataset.code!);
      });
      w.querySelector<HTMLButtonElement>(".opt21-del")!.addEventListener("click", () => {
        const code = w.dataset.code!;
        if (code === BASE_CURRENCY || code === defaultCurrency()) { toast("Your default pocket stays open."); return; }
        if (pocketBalance(code) > 0) { toast("Only an empty pocket can be closed."); return; }
        setCurrencyEnabled(code, false);
        if (activeCurrency() === code) setSessionCurrency(null);
        w.remove();
        srApi?.refreshAvailable();
        srApi?.setGlobal(globalOn);
        paintNucPill();
        if (!s.querySelector(".opt21-sw")) closePopup();
      });
    });
  }

  // The pockets panel (the globe's press-and-hold): balances, See on Arc ↗,
  // press and hold = default (an outline round the symbol), tap the symbol of
  // an empty pocket = close it after a confirm; Swap and Add a currency at the foot.
  // 1.4.15 — the pocket rows of the Globe's panel, shared with the Balance panel's list (below): a row per
  // pocket (symbol · name and "USDC on Arc" · balance with See on Arc); hold a row = the default; a left swipe
  // reveals Close (an empty pocket only; never USD or the default). `onPick`: a tap picks that pocket (the
  // Balance panel's list); without it a tap only closes an open swipe (the Globe's panel).
  // 1.4.16 (Alexander, 16 Oct) — `simple`: the Balance panel's list keeps the short rows it had before 1.4.15
  // (flag · name · amount; the default's flag underlined), with the same hold / swipe / tap underneath.
  function pocketRows(rows: HTMLElement, opts: { current?: () => string | null; onPick?: (code: string) => void; onChange: () => void; simple?: boolean }) {
    const NAMES: Record<string, string> = { USD: "USDC on Arc", EUR: "EURC on Arc" };
    const paint = (confirming: string | null) => {
      const def = defaultCurrency();
      const on = opts.current?.() ?? null;
      return enabledCurrencies()
        .map((c) => {
          const cur = currency(c);
          const live = liveCurrencies().includes(c);
          const bal = pocketBalance(c);
          // v1.1.33 — a left swipe reveals Close (as in the pills' list); the symbol is just the symbol.
          const cls = `${c === def ? " def" : ""}${c === on ? " on" : ""}`;
          if (opts.simple)
            return `<div class="pk21-sw" data-code="${c}"><div class="pk-mrow pk21-row${cls}" data-code="${c}">
            <span class="pk-mflag${c === def ? " def" : ""}" ${c === def ? 'aria-label="Opens here"' : ""}>${cur.flag}</span>
            <span class="pk-mname">${escapeHtml(cur.name)}</span>
            <span class="pk-mamt">${escapeHtml(formatFx(c, bal))}</span>
          </div><button type="button" class="opt21-del" data-close="${c}">Close</button></div>${confirming === c ? `<div class="pk21-confirm"><span>Close the ${escapeHtml(cur.name)} pocket? It's empty.</span><button type="button" data-keep="1">Keep</button><button type="button" class="go2" data-close="${c}">Close</button></div>` : ""}`;
          return `<div class="pk21-sw" data-code="${c}"><div class="pk21-row${cls}" data-code="${c}">
            <span class="sym" aria-hidden="true">${escapeHtml(cur.symbol.trim())}</span>
            <span class="nm">${escapeHtml(cur.name)}<small>${live ? NAMES[c] ?? `${c} on Arc` : "Not on Arc yet"}</small></span>
            <span class="bl">${escapeHtml(formatFx(c, bal))}${live ? `<a class="arc" href="${EXPLORER_BASE}/address/${address}?tab=tokens" target="_blank" rel="noopener">See on Arc ↗</a>` : ""}</span>
          </div><button type="button" class="opt21-del" data-close="${c}">Close</button></div>${confirming === c ? `<div class="pk21-confirm"><span>Close the ${escapeHtml(cur.name)} pocket? It's empty.</span><button type="button" data-keep="1">Keep</button><button type="button" class="go2" data-close="${c}">Close</button></div>` : ""}`;
        })
        .join("");
    };
    let confirming: string | null = null;
    const wire = () => {
      rows.innerHTML = paint(confirming);
      rows.querySelectorAll<HTMLElement>(".pk21-sw").forEach((w) => {
        const r = w.querySelector<HTMLElement>(".pk21-row")!;
        const code = w.dataset.code!;
        let held = false;
        let t: number | undefined;
        let x0 = 0, dx = 0, swiping = false;
        r.addEventListener("pointerdown", (e) => {
          if ((e.target as HTMLElement).closest("a")) return;
          held = false; swiping = false; x0 = e.clientX; dx = 0;
          t = window.setTimeout(() => {
            if (swiping) return;
            held = true;
            setDefaultCurrency(code);
            // 1.4.94 — (paintDockHome, v1.3.4, went with the Home tab)
            if (navigator.vibrate) navigator.vibrate(8);
            toast(`${currency(code).name} is your default`);
            wire();
            opts.onChange();
          }, 550);
        });
        // v1.1.33 — a left swipe reveals Close.
        r.addEventListener("pointermove", (e) => {
          if (!x0) return;
          dx = e.clientX - x0;
          if (Math.abs(dx) > 8) { swiping = true; window.clearTimeout(t); }
          if (swiping) r.style.transform = `translateX(${Math.max(-88, Math.min(0, dx + (w.classList.contains("open") ? -88 : 0)))}px)`;
        });
        const settle = () => {
          window.clearTimeout(t);
          if (!x0) return;
          const open = w.classList.contains("open") ? dx < 30 : dx < -40;
          w.classList.toggle("open", open);
          r.style.transform = open ? "translateX(-88px)" : "";
          x0 = 0;
        };
        r.addEventListener("pointerup", settle);
        r.addEventListener("pointerleave", settle);
        r.addEventListener("pointercancel", settle);
        r.addEventListener("click", (e) => {
          if (held) { held = false; return; }
          if (swiping) { swiping = false; return; }
          if ((e.target as HTMLElement).closest("a")) return;
          if (w.classList.contains("open")) { w.classList.remove("open"); r.style.transform = ""; return; }
          opts.onPick?.(code);
        });
        w.querySelector<HTMLButtonElement>(".opt21-del")!.addEventListener("click", () => {
          if (code === BASE_CURRENCY || code === defaultCurrency()) { toast("Your default pocket stays open."); w.classList.remove("open"); r.style.transform = ""; return; }
          if (pocketBalance(code) > 0) { toast("Only an empty pocket can be closed."); w.classList.remove("open"); r.style.transform = ""; return; }
          confirming = code;
          wire();
        });
      });
      rows.querySelector("[data-keep]")?.addEventListener("click", () => { confirming = null; wire(); });
      rows.querySelector<HTMLButtonElement>(".pk21-confirm [data-close]")?.addEventListener("click", (e) => {
        const code = (e.currentTarget as HTMLButtonElement).dataset.close!;
        setCurrencyEnabled(code, false);
        if (activeCurrency() === code) setSessionCurrency(null);
        confirming = null;
        wire();
        srApi?.refreshAvailable();
        srApi?.setGlobal(globalOn);
        paintNucPill();
        opts.onChange();
      });
    };
    wire();
  }
  const POCKET_HINT = `<p class="pk21-hint">Press and hold a currency to make it your default. Swipe left to close an empty pocket.</p>`;
  const pocketActs = (ids: [string, string]) =>
    `<div class="pk21-acts"><button type="button" id="${ids[0]}">${IC_SWAP_UD}Swap</button><button type="button" id="${ids[1]}">Add more</button></div>`;

  // 1.4.17 — unreferenced since the globe's hold went (see the globe's wiring); kept for a way back.
  function pocketsPanel() {
    const s = openPopup(`<div class="sheet-title">Currencies</div><div id="pk21Rows"></div>${POCKET_HINT}${pocketActs(["pk21Swap", "pk21Add"])}`);
    $("#popup").classList.add("list21");
    pocketRows(s.querySelector<HTMLElement>("#pk21Rows")!, { onChange: () => undefined });
    s.querySelector("#pk21Swap")!.addEventListener("click", () => openSwap(activeCurrency()));
    // v1.1.29 — Add more: the currency list, and back here when it closes.
    s.querySelector("#pk21Add")!.addEventListener("click", () => currencyListPopup(pocketsPanel));
  }
  void pocketsPanel;

  // v0.60.4 (S2a + S5a) — "Recent sends": the last 5 confirmed sends from this
  // device, with reference. Hidden entirely when empty. Sends only until
  // chain-sourced Activity (v0.70) brings received payments.
  // v0.70.13 — "Recent drops": your sends (device log) and your Drops (server),
  // merged into one list, newest first. Painted from cache at open, then refreshed.
  let dropsCache: { sent: DropView[]; received: DropView[] } | null = null;
  const receipt = (tx: string | null | undefined) =>
    tx ? `<a class="st-link st-rcpt" href="${adapter.explorerUrl(tx)}" target="_blank" rel="noopener">Receipt ↗</a>` : "";

  // v0.70 — Drops on the Balance panel: what you've sent that's still waiting
  // or claimed (tap → release), and what's been claimed for you (tap → the
  // confirmation again). Fetched after the popup opens so the panel never waits.
  async function fillDropsRows(box: HTMLElement, code: string = BASE_CURRENCY) {
    // v0.80.10 — exactly ONE "Loading from Circle Arc…" line, owned here: it
    // sits under whatever is cached while either read is in flight, and is
    // removed once both have landed (or failed).
    const mark = () => {
      if (!box.querySelector(".bal-refreshing"))
        box.insertAdjacentHTML("beforeend", `<p class="sheet-note bal-loading bal-refreshing">Loading from Circle Arc…</p>`);
    };
    const unmark = () => box.querySelectorAll(".bal-refreshing").forEach((el) => el.remove());
    if (dropsCache) paintDrops(box, dropsCache, code);
    else box.innerHTML = "";
    mark();
    // v0.80.8 — both reads in flight at once; each repaints its own part.
    const recent = loadRecentTx(statementDirty, code).then(() => {
      statementDirty = false;
      let r = box.querySelector<HTMLElement>("#balRecent");
      if (!r) { // cold cache and the Drops read hasn't painted (or failed)
        box.insertAdjacentHTML("afterbegin", `<div id="balRecent"></div>`);
        r = box.querySelector<HTMLElement>("#balRecent")!;
      }
      r.innerHTML = recentBlockHtml(code);
    });
    const t0 = performance.now();
    let mine: { sent: DropView[]; received: DropView[] } | null = null;
    // v0.90.26 — payments recorded at acceptance and not yet confirmed (or
    // failed): read alongside the Drops; a normal send resolves in seconds and
    // never shows here (rows younger than 60 s are skipped in paint).
    const pendP = getPendingTx(address).then((r) => r.items).catch(() => null);
    try {
      mine = await getMyDrops(address);
    } catch {
      /* keep the cache */
    }
    const pendRows = await pendP;
    if (pendRows) pendingTxCache = pendRows;
    lastTiming.drops = Math.round(performance.now() - t0);
    if (mine) {
      dropsCache = mine;
      paintDrops(box, mine, code);
      mark();
    }
    await recent;
    unmark();
  }
  let pendingTxCache: PendingTx[] = []; // v0.90.26
  function paintDrops(box: HTMLElement, mine: { sent: DropView[]; received: DropView[] }, code: string = BASE_CURRENCY) {
    // v0.70.15 — only the LIVE Drops keep their own group (they carry actions);
    // everything settled shows in "Recent transactions" from the chain below.
    // v0.80.32 — and only THIS pocket's Drops (a Drop's currency is its escrow).
    const live = (d: DropView) => (d.status === "open" || d.status === "claimed") && (d.currency ?? "USD") === code;
    const sent = mine.sent.filter(live);
    const received = mine.received.filter(live);
    const mineFor = (a: string | null | undefined, fallback: string) => (a && readNick(a)) || fallback;
    const who = (d: DropView, side: "sent" | "received") =>
      side === "sent"
        ? `${escapeHtml(mineFor(d.claimerAddress, d.label || d.claimerName || d.claimerCode || "Someone"))}${d.claimerCode ? ` <span class="ct-theirs">(${d.claimerName ? `${escapeHtml(d.claimerName)} ` : ""}${emojiFor(d.claimerCode, d.claimerEmoji)})</span>` : ""}`
        : `${escapeHtml(mineFor(d.senderAddress, d.senderName ?? "Someone"))} <span class="ct-theirs">(${emojiFor(d.senderCode, d.senderEmoji)})</span>`;
    const right = (d: DropView, side: "sent" | "received") =>
      side === "sent"
        ? d.status === "claimed"
          ? `<button class="st-link" type="button" data-act="release" data-drop="${d.id}">Release ›</button>`
          : // v0.90.21 — a bearer Drop's link needs its key, which only the
            // device that made it holds; elsewhere it can still be cancelled.
            `${!d.bearer || secretFor(d.id) ? `<button class="st-link" type="button" data-act="share" data-drop="${d.id}">Share link</button> · ` : ""}<button class="st-link muted" type="button" data-act="cancel" data-drop="${d.id}"${d.bearer ? ` data-bearer="1"` : ""} data-ccy="${escapeHtml(d.currency ?? "USD")}">Cancel</button>`
        : `<button class="st-link" type="button" data-act="confirm" data-drop="${d.id}">Confirmation ›</button>`;
    const row = (d: DropView, side: "sent" | "received") =>
      statementRow({
        who: who(d, side),
        amt: (d.currency ?? "USD") === BASE_CURRENCY ? formatUsdShort(Number(d.amount)) : formatFx(d.currency!, Number(d.amount)),
        when: formatDayNoYear(Date.parse(d.createdAt.replace(" ", "T") + "Z")),
        ref: d.ref ?? "",
        right: right(d, side),
        sign: side === "sent" ? "-" : "+",
      });
    // v0.90.21 — the rest of a first payment, held on this device:
    // "€49 · Lucy — waiting for Lucy to confirm €1". Opens the rest card.
    const heldRows = listHeld()
      .filter((h) => h.currency === code)
      .map((h) => {
        const f = (n: string) => (h.currency === BASE_CURRENCY ? formatUsdShort(Number(n)) : formatFx(h.currency, Number(n)));
        return statementRow({
          who: escapeHtml(h.label),
          amt: f(h.rest),
          when: formatDayNoYear(h.createdAt),
          // v1.0.7 — before the claim the wait is theirs; after it, the next
          // step is personal: check with them that it arrived.
          ref: (() => {
            const dv = mine.sent.find((x) => x.id === h.id);
            const claimed = dv?.status === "released";
            const who = isNewLabel(h.label) ? (claimed && dv?.claimerName ? dv.claimerName : "") : h.label;
            return claimed
              ? who ? `Check ${who} got ${f(h.first)}` : `Check the ${f(h.first)} arrived`
              : who ? `Waiting for ${who} to claim ${f(h.first)}` : `Waiting for them to claim ${f(h.first)}`;
          })(),
          right: `<button class="st-link" type="button" data-act="rest" data-drop="${escapeHtml(h.id)}">Send ›</button>`,
          sign: "-",
        });
      });
    // v0.90.26 — a send still confirming after a minute, or one that didn't go
    // through (kept a day). Tap "Try again" → the Send card, prefilled.
    const nowMs = Date.now();
    const txRows = pendingTxCache
      .filter((p) => (p.currency ?? "USD") === code)
      .filter((p) => p.status === "failed" || nowMs - Date.parse(p.at.replace(" ", "T") + "Z") > 60_000)
      .map((p) => {
        const cp = p.counterparty && /^0x[0-9a-fA-F]{40}$/.test(p.counterparty) ? p.counterparty : null;
        const who = p.kind?.startsWith("drop") ? "Drop" : p.kind?.startsWith("earn") ? (p.kind === "earn_in" ? "Earning" : "Cash") : cp ? readNick(cp) || shortAddr(cp) : "—";
        const amt = (p.currency ?? "USD") === BASE_CURRENCY ? formatUsdShort(Number(p.amount ?? 0)) : formatFx(p.currency, Number(p.amount ?? 0));
        const failed = p.status === "failed";
        return statementRow({
          who: escapeHtml(who),
          amt,
          when: formatDayNoYear(Date.parse(p.at.replace(" ", "T") + "Z")),
          ref: failed ? "Didn't go through — nothing moved" : "Confirming on Circle Arc…",
          right: failed && cp && p.kind === "send" ? `<button class="st-link" type="button" data-act="retry" data-to="${cp}" data-amt="${escapeHtml(p.amount ?? "")}">Try again ›</button>` : "",
          sign: "-",
        });
      });
    const pending = [...txRows, ...heldRows, ...sent.map((d) => row(d, "sent")), ...received.map((d) => row(d, "received"))];
    const pendingHtml = pending.length ? `<div class="txlog"><div class="tx-title pending">Pending transactions</div>${pending.join("")}</div>` : "";
    box.innerHTML =
      pendingHtml +
      `<div id="balRecent">${recentBlockHtml(code)}</div>`;
    box.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((b) => {
      b.addEventListener("click", () => {
        const id = b.dataset.drop!;
        switch (b.dataset.act) {
          case "retry": { // v0.90.26 — re-open the Send card for a failed payment
            const to = b.dataset.to!;
            closePopup();
            srApi?.openSend({ toAddress: to, toDisplay: readNick(to) || shortAddr(to), amount: Number(b.dataset.amt) || undefined });
            return;
          }
          case "rest":
            location.assign(`/c/${id}`);
            return;
          case "share":
          {
            const sd = sent.find((x) => x.id === id);
            const fig = !sd ? "" : (sd.currency ?? "USD") === BASE_CURRENCY ? formatUsdShort(Number(sd.amount)) : formatFx(sd.currency!, Number(sd.amount));
            void shareOrCopy(dropSharePayload(fig, dropUrl(location.origin, id, secretFor(id) ?? undefined))).then((r) => {
              if (r === "copied") toast("Link copied ✓");
            });
            return;
          }
          case "cancel":
            void cancelDrop(id, b);
            return;
          case "release":
            location.assign(`/c/${id}`);
            return;
          case "confirm":
            location.assign(`/d/${id}`);
            return;
        }
      });
    });
  }

  // v0.70.15 — "Recent transactions": the last five legs from the chain (this
  // month, then last month), same source and shape as Activity. Cached so the
  // panel paints instantly and refreshes in place.
  // v0.80.32 — one statement cache per pocket; every read is filtered.
  const recentTxCache: Record<string, ActivityItem[]> = {};
  // v0.80.33 — the ONE place that decides what the statement block shows for a
  // pocket: rows if there are any; the "No … yet" note for an empty non-USD
  // pocket once its read has come back; nothing otherwise. Both the Drops
  // repaint and the statement repaint call this, so they can't disagree —
  // they used to, and whichever finished last decided whether the note
  // survived (the "sometimes" in the bug report).
  function recentBlockHtml(code: string): string {
    const items = recentTxCache[code];
    if (items?.length) return recentTxHtml(items);
    if (items && code !== BASE_CURRENCY && pocketBalance(code) <= 0) {
      // v0.90.18 — only a pocket with a token on Arc can be swapped into.
      return liveCurrencies().includes(code)
        ? `<p class="sheet-note pk-empty">No ${currency(code).name} yet.<br />Swap to move money into this pocket.</p>`
        : `<p class="sheet-note pk-empty">No ${currency(code).name} yet.<br />${code} isn't supported on M yet.</p>`;
    }
    return "";
  }
  // v0.80.29 — set after a payment so the next statement read bypasses the
  // Worker's 60 s cache; cleared once it has been used.
  let statementDirty = !!freshStatement; // 1.4.47 — the home entered from a payment or a take: no cached statement
  // 1.4.47 (Alexander's video, 19 Oct) — the money from a take was in the balance pill, and in
  // Activity, but not in the Balance panel's Recent transactions: its first read came back before
  // the take's leg was in (the node's receipt a moment behind), and the Worker and the browser kept
  // that answer for 60 s. Now a balance that differs from the last one known means the statement is
  // behind it: the next read is fresh, an open panel is repainted, and one more fresh read follows a
  // few seconds later (the leg may still be on its way).
  let lastSeenTotal: number | null = (() => { try { const v = localStorage.getItem("m.lastTotal"); return v == null ? null : Number(v); } catch { return null; } })();
  let followUp = 0;
  function noteMoneyMoved() {
    const now = totalUsd();
    const before = lastSeenTotal;
    lastSeenTotal = now;
    if (before == null || !Number.isFinite(before) || Math.abs(now - before) < 0.005) return;
    statementDirty = true;
    void repaintRecent();
    window.clearTimeout(followUp);
    followUp = window.setTimeout(() => { statementDirty = true; void repaintRecent(); }, 5000);
  }
  /** Read the statement fresh and repaint the Balance panel's Recent transactions, if it is open. */
  let panelCode: string = BASE_CURRENCY;
  async function repaintRecent() {
    const code = panelCode;
    await loadRecentTx(true, code);
    statementDirty = false;
    const r = $("#popup").querySelector<HTMLElement>("#balRecent");
    if (r && !$("#popup").hasAttribute("hidden")) r.innerHTML = recentBlockHtml(code);
  }
  function recentTxHtml(items: ActivityItem[]): string {
    if (!items.length) return "";
    const rows = items
      .slice(0, 5)
      .map((it) =>
        statementRow({
          who: activityWho(it),
          amt: it.currency && it.currency !== BASE_CURRENCY
            ? formatFx(it.currency, Number(it.amount))
            : formatUsdShort(Number(it.amount)),
          when: formatDayNoYear(Date.parse(it.at)),
          ref: it.ref ?? "",
          refCell: noteRefCell(it),
          right: receipt(it.hash),
          sign: it.sign,
        }),
      )
      .join("");
    return `<div class="txlog"><div class="tx-title">Recent transactions</div>${rows}</div>`;
  }
  const recentTxInflight: Record<string, Promise<void> | undefined> = {};
  const recentTxInflightFresh: Record<string, boolean> = {}; // 1.4.47 — was the read in flight a fresh one?
  // v0.80.9 — timings for Lab, so "slower" is a number, not a feeling.
  const lastTiming: { drops?: number; recent?: number; months?: number; explorer?: number; pages?: number; worker?: string; explorerState?: string; own?: string } = {};
  function loadRecentTx(fresh = false, code: string = BASE_CURRENCY): Promise<void> {
    const inflight = recentTxInflight[code];
    // One fetch per pocket at a time. 1.4.47 — but a fresh read never rides on one that isn't:
    // the stale answer is what it is there to replace.
    if (inflight) return fresh && !recentTxInflightFresh[code] ? inflight.then(() => loadRecentTx(true, code)) : inflight;
    const now = new Date();
    const ym = (d: Date) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    const t0 = performance.now();
    const p = (async () => {
      try {
        // v0.80.10 — ask for 5: the Worker stops paging the explorer once it has them.
        const first = await getActivity(address, ym(now), 5, code, fresh);
        let items = first.items;
        lastTiming.months = 1;
        lastTiming.explorer = first.timing?.explorer_ms;
        lastTiming.pages = first.timing?.pages;
        lastTiming.worker = first.worker;
        // v0.90.22 — is the explorer answering, and how many legs came from M's own records
        lastTiming.explorerState = first.timing?.explorer;
        const o = first.timing?.own;
        lastTiming.own = o ? `${o.legs} legs, ${o.fetched} from node in ${o.rpc_ms} ms${o.error ? ` · ${o.error}` : ""}` : undefined;
        if (items.length < 5) {
          const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
          const second = await getActivity(address, ym(prev), 5 - items.length, code, fresh);
          items = items.concat(second.items);
          lastTiming.months = 2;
          lastTiming.explorer = (lastTiming.explorer ?? 0) + (second.timing?.explorer_ms ?? 0);
          lastTiming.pages = (lastTiming.pages ?? 0) + (second.timing?.pages ?? 0);
        }
        recentTxCache[code] = items.slice(0, 5);
        void refreshNoteStates(); // v1.1.19 — Sent / Taken on the M® Note rows
      } catch {
        /* keep whatever we had */
      } finally {
        lastTiming.recent = Math.round(performance.now() - t0);
        recentTxInflight[code] = undefined;
        recentTxInflightFresh[code] = false;
      }
    })();
    recentTxInflight[code] = p;
    recentTxInflightFresh[code] = fresh;
    return p;
  }
  // v0.80.8 — warm "Recent transactions" at view start (like Drops above), so
  // the panel's first open paints from cache instead of waiting on the explorer.
  // (Sits after the `let`s it touches — a call above them would hit the TDZ.)
  void loadRecentTx(statementDirty, activeCurrency());

  // v1.1.19 — M® Note rows carry their state (brief §2.4, decided 2 Oct):
  // **Sent** — nobody has taken it; Copy code / Copy link sit on the row while
  // this browser still holds the secret — or **Taken**. A note this wallet
  // took is Taken. A note sent from THIS browser reads from its record (kept
  // in line with Arc by reconcileSent); one sent from another browser is read
  // from Arc once (bySerial) and remembered for the session.
  const noteStateBySerial = new Map<string, SentState>();
  const noteLookups = new Set<string>();
  let noteReconciledAt = 0;
  function noteStateOf(it: ActivityItem): SentState | null {
    if (it.kind === "note_in") return "taken";
    if (it.kind !== "note_out" || !it.note) return null;
    const rec = listSent().find((r) => r.serial === it.note);
    if (rec) return sentState(rec);
    const known = noteStateBySerial.get(it.note);
    if (known) return known;
    lookUpNote(it.note);
    return null;
  }
  function noteRefInner(it: ActivityItem): string {
    const st = noteStateOf(it);
    if (!st) return "";
    const rec = it.kind === "note_out" ? listSent().find((r) => r.serial === it.note) : undefined;
    // v1.1.20 — the serial after the state (no "Mx": the amount is on the row),
    // and Copy link / Copy code as two actions on a Sent row (Alexander, 4 Oct).
    // A cash-link note has no code: Copy link only (its link carries the secret).
    const serial = it.note && parseSerial(it.note) ? ` · <span class="nt-serial">${escapeHtml(formatSerial(it.note))}</span>` : "";
    const btn = (what: "link" | "code", label: string) =>
      `<button class="st-link nt-rowcopy" type="button" data-ncopy="${escapeHtml(it.note!)}" data-nwhat="${what}">${label}</button>`;
    // The two copies on their own line, under the state, so the row's Receipt ↗ keeps its place.
    const copy = st === "sent" && rec?.secret
      ? `<span class="nt-acts">${rec.kind === "code" ? btn("link", "Copy link") + " · " + btn("code", "Copy code") : btn("link", "Copy link")}</span>`
      : "";
    return `<span class="nt-state ${st}">${st === "sent" ? "Sent" : "Taken"}</span>${serial}${copy}`;
  }
  /** The ref cell for an M® Note row (null for any other row). */
  function noteRefCell(it: ActivityItem): string | undefined {
    if (it.kind !== "note_out" && it.kind !== "note_in") return undefined;
    return `<span class="ar-ref" data-nref="${escapeHtml(it.note ?? "")}" data-nkind="${it.kind}">${noteRefInner(it)}</span>`;
  }
  /** Repaint every M® Note ref cell on screen (Recent and Activity) from what is known now. */
  function repaintNoteRefs() {
    document.querySelectorAll<HTMLElement>("[data-nref]").forEach((el) => {
      const kind = el.dataset.nkind as ActivityItem["kind"];
      el.innerHTML = noteRefInner({ kind, note: el.dataset.nref || undefined } as ActivityItem);
    });
  }
  function lookUpNote(serialText: string) {
    if (noteLookups.has(serialText)) return;
    const serial = parseSerial(serialText);
    if (!serial) return;
    noteLookups.add(serialText);
    void noteSource()
      .bySerial(serial)
      .then((n) => {
        if (!n) return;
        noteStateBySerial.set(serialText, looksUntaken(n) ? "sent" : "taken");
        repaintNoteRefs();
      })
      .catch(() => noteLookups.delete(serialText)); // try again on the next paint
  }
  /** Bring this browser's sent notes in line with Arc (at most every 20 s), then repaint. */
  async function refreshNoteStates() {
    if (!listSent().length || Date.now() - noteReconciledAt < 20_000) return repaintNoteRefs();
    noteReconciledAt = Date.now();
    try { await reconcileSent(noteSource()); } catch { /* a failed read changes nothing */ }
    repaintNoteRefs();
  }
  // Copy code / Copy link on a Sent row: the secret goes to the clipboard only on this tap.
  // One listener for the life of the page: the home can be mounted again (log out, log in).
  if (noteCopyListener) root.removeEventListener("click", noteCopyListener);
  noteCopyListener = (e: Event) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-ncopy]");
    if (!b) return;
    const rec = listSent().find((r) => r.serial === b.dataset.ncopy);
    if (!rec?.secret || !rec.serial) return;
    // The link of a code-locked note is the bare serial (the code is said, never sent with it);
    // a cash link carries its secret after #.
    const wantCode = b.dataset.nwhat === "code" && rec.kind === "code";
    const text = wantCode ? formatNoteCode(rec.secret) : noteLink(LINK_ORIGIN, rec.serial, rec.kind === "link" ? rec.secret : undefined);
    void navigator.clipboard.writeText(text).then(
      () => toast(wantCode ? "Code copied" : "Link copied"),
      () => toast("Couldn't copy just now", "alert"),
    );
  };
  root.addEventListener("click", noteCopyListener);

  // v0.70.15 — panel row: [date] [who] [amount] / [Ref: …] … [Receipt ↗ | action]
  function statementRow(r: { who: string; amt: string; when: string; ref: string; right: string; sign?: "+" | "-"; refCell?: string }): string {
    return `<div class="arow">
      <span class="ar-when">${r.when}</span>
      <span class="ar-who">${r.who}</span>
      <span class="ar-amt ${r.sign === "+" ? "in" : "out"}">${r.sign === "-" ? "–" : ""}${r.amt}</span>
      ${r.refCell ?? `<span class="ar-ref">${r.ref ? escapeHtml(r.ref) : ""}</span>`}
      <span class="ar-right">${r.right}</span>
    </div>`;
  }

  // v0.70.5 — cancel an unclaimed Drop from its row: one Face ID, money back.
  // Only offered while nobody has claimed; after that the 7-day expiry handles it.
  async function cancelDrop(id: string, btn: HTMLButtonElement) {
    btn.disabled = true;
    btn.textContent = "Cancelling…";
    try {
      const cfg = await getDropConfig();
      // v0.90.21 — the contract that holds THIS Drop: its currency, escrow or
      // bearer (this always used the USD escrow, so a euro Drop couldn't cancel).
      const ccy = btn.dataset.ccy ?? "USD";
      const to = (btn.dataset.bearer ? cfg.bearer?.[ccy]?.contract : cfg.contracts?.[ccy]?.contract) ?? cfg.contract!;
      const { txHash } = await adapter.execute([
        {
          to: to as `0x${string}`,
          data: encodeFunctionData({
            abi: [{ type: "function", name: "cancel", stateMutability: "nonpayable", inputs: [{ name: "id", type: "bytes32" }], outputs: [] }] as const,
            functionName: "cancel",
            args: [stringToHex(id, { size: 32 })],
          }),
        },
      ]);
      await dropCancelled(id, txHash).catch(() => {});
      // A first payment taken back takes its held rest with it.
      if (btn.dataset.bearer) {
        removeHeld(id);
        await dropRest(id, { senderAddress: address, dropped: true }).catch(() => {});
      }
      toast("Drop cancelled — money's back");
      btn.closest(".arow")?.remove();
      void refreshBalance();
    } catch (e) {
      console.warn("[M] cancel drop failed", e);
      btn.disabled = false;
      btn.textContent = "Cancel";
      toast("Couldn't cancel just now");
    }
  }

  // v0.30 — "Put your money to work": Cash ⇄ Earning (M-MMF) allocation.
  // The position source is a placeholder until MToken/MTeller are deployed;
  // the sheet itself is final.
  function earnSheet() {
    const body = openSheet(`
      <div id="earnMount"></div>
      <button id="growAdd" class="btn primary block pill" type="button">Add to M®</button>
    `);
    body.querySelector("#growAdd")!.addEventListener("click", () => {
      closeSheet();
      setTimeout(addSheet, 210);
    });
    renderEarnSheet(body.querySelector<HTMLElement>("#earnMount")!, {
      address,
      cashUsd: currentBalance,
      source: earnSource,
      toast,
      onMoved: () => void refreshBalance(),
      onDone: closeSheet,
    });
  }

  // v0.20 — "Your invites": the wallet's referral codes + conversion progress.
  // mintInvites is idempotent and re-run here so codes appear even if the
  // create-time mint failed transiently.
  async function invitesSheet() {
    const body = openSheet(`
      <h3 class="sheet-title">Your invites</h3>
      <div id="ivBody"><div class="add-progress"><span class="spinner"></span>Loading your invites…</div></div>
    `);
    const mount = body.querySelector<HTMLElement>("#ivBody")!;
    try {
      try {
        await mintInvites(address);
      } catch {
        /* non-blocking — getMyInvites may still have prior codes */
      }
      const data = await getMyInvites(address);
      renderInvites(mount, data);
    } catch {
      mount.innerHTML = `<p class="sheet-note">Couldn't load your invites. Pull down and try again.</p>`;
    }
  }

  function renderInvites(
    el: HTMLElement,
    data: {
      codes: {
        code: string;
        status: string;
        shared?: boolean;
        joinedName?: string | null;
        joinedAddr?: string | null;
      }[];
      joined: number;
      needed: number;
      unlocked: boolean;
    },
  ) {
    const pct =
      data.needed > 0 ? Math.min(100, Math.round((data.joined / data.needed) * 100)) : 0;
    const progress = data.unlocked
      ? `<div class="rprog"><div class="rlab"><span>You unlocked 5 more</span><b>${data.joined} joined</b></div><div class="rbar"><i style="width:100%"></i></div></div>`
      : `<div class="rprog"><div class="rlab"><span>Progress to your next 5</span><b>${data.joined} of ${data.needed} joined</b></div><div class="rbar"><i style="width:${pct}%"></i></div></div>`;

    const rows = data.codes
      .map((c) => {
        if (c.status === "used") {
          // The wallet that joined: show its nickname → code, with a pencil to
          // name it (keyed by its address, so it's the same contact as Recents).
          if (c.joinedAddr) {
            const shown = escapeHtml(contactLabel(c.joinedAddr, c.joinedName));
            return `<div class="ivrow">
                <span class="ivname">${shown}</span>
                <span class="ivstat joined">&#10003; Joined</span>
                <button class="iconbtn ivnick" data-nick-addr="${escapeHtml(c.joinedAddr)}" data-nick-code="${escapeHtml(c.joinedName ?? "")}" aria-label="Name this contact">${PENCIL}</button>
              </div>`;
          }
          const label = c.joinedName
            ? `<span class="ivname">${escapeHtml(c.joinedName)}</span>`
            : `<span class="ivcode used">${escapeHtml(c.code)}</span>`;
          return `<div class="ivrow">${label}<span class="ivstat joined">&#10003; Joined</span></div>`;
        }
        return `<div class="ivrow">
            <span class="ivcode">${escapeHtml(c.code)}</span>
            <span class="ivstat shared">${c.shared ? "Shared" : ""}</span>
            <span class="ivacts">
              <button class="iconbtn" data-share="${escapeHtml(c.code)}" aria-label="Share invite">${IC_SHARE}</button>
            </span>
          </div>`;
      })
      .join("");

    el.innerHTML = `
      <p class="sheet-note">Share these with people you'd vouch for. Each code makes one wallet. When ${data.needed} join, you unlock 5 more.</p>
      ${progress}
      ${rows || `<p class="sheet-note">No codes yet — pull down and reopen.</p>`}
    `;

    const linkFor = (code: string) =>
      `${LINK_ORIGIN}/invite/${encodeURIComponent(code)}`;

    el.querySelectorAll<HTMLElement>("[data-share]").forEach((b) =>
      b.addEventListener("click", async () => {
        const code = b.dataset.share!;
        const r = await shareOrCopy({
          url: linkFor(code),
          text: "Join me on M — here's your invite:", // v1.4.2 (Alexander) — a colon before the link
          title: "Your invite to M",
        });
        if (r === "shared") {
          // Only a completed native share counts. Record it and reflect it live.
          markInviteShared(code, address).catch(() => {});
          const stat = b.closest(".ivrow")?.querySelector(".ivstat");
          if (stat) stat.textContent = "Shared";
        } else if (r === "copied") {
          toast("Invite link copied");
        } else if (r === "failed") {
          toast("Couldn't share — long-press the link");
        }
      }),
    );

    el.querySelectorAll<HTMLElement>("[data-nick-addr]").forEach((b) =>
      b.addEventListener("click", () => {
        openNameEditor({
          address: b.dataset.nickAddr!,
          code: b.dataset.nickCode || undefined,
          onDone: () => renderInvites(el, data), // re-render picks up the new name
        });
      }),
    );
  }

  // 1.4.86 — the master panels, reopened as their children close (the dock lit as when they were opened).
  const toContacts = () => { void contactsSheet(); setActiveDock("wallet"); };
  function walletSheet() {
    const s = openSheet(`
      <div class="sheet-head">
        <button class="sh-av" id="whEmoji" type="button" aria-label="Change your emoji">${getEmoji(display)}</button>
        <div class="sh-id">
          <button class="sh-name sh-name-btn" id="whName" type="button" title="Tap to edit your name">Add your name</button>
          <span class="sh-subline">
            <button id="whLink" class="sh-sub addr-inline" type="button" title="Tap to switch">${name ? `mdrop.it/${escapeHtml(display)}` : "Setting up…"}</button>
            <button id="whCopy" class="sh-copy" aria-label="Copy" ${name ? "" : "disabled"}>${IC_COPY}</button>
          </span>
        </div>
      </div>
      <button class="srow" data-w="activity"><span class="si">${IC_CALENDAR}</span>Activity<span class="chev">›</span></button>
      <button class="srow" data-w="add"><span class="si">${IC_ADD}</span>Add money<span class="chev">›</span></button>
      <button class="srow" data-w="contacts"><span class="si si-wide">${IC_MYCONTACTS}</span>Contacts<span class="chev">›</span></button>
      <button class="srow" data-w="keys"><span class="si">${IC_KEY}</span>Spare Key<span class="chev">›</span></button>
      <div class="srow-split"></div>
      <button class="srow danger" data-w="delete"><span class="si">${IC_TRASH}</span>Delete wallet<span class="chev">›</span></button>
    `);
    s.classList.add("compact"); // v1.3.13 — six rows: 44 px each, so Delete wallet clears the dock on a 669 px phone (Alexander, 12 Oct)
    // 1.4.85 (Alexander, 22 Oct) — "See on Circle Arc" is Circle's logo, top right, mirroring the ✕.
    const crcl = document.createElement("a");
    crcl.className = "sheet-crcl";
    crcl.href = `${EXPLORER_BASE}/address/${encodeURIComponent(address)}`;
    crcl.target = "_blank";
    crcl.rel = "noopener";
    crcl.setAttribute("aria-label", "See on Circle Arc");
    crcl.innerHTML = IC_CIRCLE;
    s.parentElement?.append(crcl);
    // v0.70.5 — codes off the surface: the line under your name is *your M link*
    // (tap = copy). The raw address lives in Lab for the expert path.
    getProfile(address)
      .then((p) => {
        if (p.name) s.querySelector<HTMLElement>("#whName")!.textContent = p.name;
      })
      .catch(() => {});
    // v0.70.9 — the second line alternates between your M link and your
    // (truncated) address; Copy copies whichever is showing. QR lives on the
    // main screen only.
    let showAddr = false;
    const sub = s.querySelector<HTMLElement>("#whLink")!;
    const paint = () => {
      sub.textContent = showAddr ? shortAddr(address) : name ? `mdrop.it/${display}` : "Setting up…";
    };
    const flip = () => {
      showAddr = !showAddr;
      paint();
    };
    s.querySelector("#whName")!.addEventListener("click", () => {
      closeSheet(true);
      setTimeout(() => { myNameSheet(); }, 210);
    });
    sub.addEventListener("click", flip);
    const copyShown = () => {
      if (showAddr) {
        navigator.clipboard?.writeText(address).then(
          () => toast("Address copied"),
          () => toast("Couldn't copy"),
        );
      } else if (name) copyLink();
    };
    s.querySelector("#whEmoji")!.addEventListener("click", () => {
      closeSheet(true);
      setTimeout(() => { emojiPickerSheet(); }, 210);
    });
    if (name) s.querySelector("#whCopy")!.addEventListener("click", copyShown);
    s.querySelectorAll<HTMLElement>(".srow[data-w]").forEach((row) => {
      row.addEventListener("click", () => {
        const w = row.dataset.w;
        if (w === "activity") {
          closeSheet(true);
          setTimeout(() => { activitySheet(); setActiveDock("wallet"); }, 190); // 1.4.85 — Activity lives in Wallet
        } else if (w === "add") {
          closeSheet(true);
          setTimeout(() => { addSheet(); setActiveDock("wallet"); }, 190);
        } else if (w === "contacts") {
          closeSheet(true);
          setTimeout(toContacts, 190); // 1.4.85 — Contacts lives in Wallet
        } else if (w === "keys") {
          // v1.3.12 — M Spare Keys (src/sparekey/screens.ts), loaded on demand; ✕ closes.
          closeSheet();
          openSpareKeyScreen(190);
        } else if (w === "delete") {
          closeSheet(true);
          setTimeout(() => { deleteSheet(); setActiveDock("wallet"); }, 190);
        }
      });
    });
  }

  // v1.3.12 — M Spare Keys (src/sparekey/screens.ts), loaded on demand; ✕ closes. 1.5.45 — also from the home's
  // Spare Key prompt (no sheet to close first, so no wait). v1.3.15 — the centre lights, not Wallet; 1.4.90 — its ✕ closes.
  function openSpareKeyScreen(wait = 0) {
    void import("../sparekey/screens").then(
      (m) => setTimeout(() => { m.openSpareKeys({ host: openPopup(""), adapter, address, toast }); setActiveDock("sr"); }, wait),
      (e) => { recordError("sparekey/load", e); toast("Couldn't open it. Try again.", "alert"); },
    );
  }

  function addSheet() {
    // The dispensed amount is server-set (FUNDING_AMOUNT). We learn it from the
    // funding response; until then show a neutral label so copy never lies.
    const s = openSheet(`
      <h3 class="sheet-title t-center">Add money</h3>
      <p class="sheet-note">Top up your wallet to try M. One tap — it lands in a few seconds.</p>
      <div id="addBody">
        <button id="addGo" class="btn primary block">Add money</button>
      </div>
    `);
    const body = s.querySelector<HTMLElement>("#addBody")!;
    const money = (a: string) => `$${a}`;

    const settle = (html: string) => (body.innerHTML = html);
    const done = (amount: string) => {
      playDone(); // 1.5.10 — M's "done": the money is added (not the welcome money, which arrives unasked)
      settle(`<div class="add-done"><div class="tickbig">✓</div><p class="sheet-note">${money(amount)} added — enjoy M.</p></div>`);
      void refreshBalance();
      setTimeout(closeSheet, 1400);
    };

    async function poll(id: number, amount: string) {
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const f = await getFunding(id);
          if (f.status === "confirmed") return done(amount);
          if (f.status === "failed")
            return settle(`<p class="sheet-note">That didn't go through. <button id="addRetry" class="linkish">Try again</button></p>`);
        } catch {
          /* keep polling */
        }
      }
      done(amount); // still pending after ~30s — balance will catch up
    }

    body.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).id === "addRetry") addSheet();
    });

    s.querySelector("#addGo")!.addEventListener("click", async () => {
      settle(`<div class="add-progress"><span class="spinner"></span><span>Adding money… <span class="muted">confirming on Circle Arc</span></span></div>`);
      try {
        const f = await requestFunding(address, crypto.randomUUID());
        if (f.status === "confirmed") return done(f.amount);
        if (f.status === "failed")
          return settle(`<p class="sheet-note">That didn't go through. <button id="addRetry" class="linkish">Try again</button></p>`);
        await poll(f.id, f.amount);
      } catch (err) {
        const m = String((err as Error).message || "");
        if (m.includes("already_funded")) {
          settle(`<p class="sheet-note">This wallet's already been topped up. One top-up per wallet during the pilot.</p>`);
        } else if (m.includes("funding_not_enabled")) {
          settle(`<p class="sheet-note">Add money isn't switched on just yet — check back very soon.</p>`);
        } else {
          console.error(err);
          settle(`<p class="sheet-note">Couldn't reach the funding service. <button id="addRetry" class="linkish">Try again</button></p>`);
        }
      }
    });
  }

  // v0.70.12 — Activity: the statement, one month at a time, pulled from Arc
  // (via the Worker's explorer proxy) and shown with YOUR names for people.
  // Row: [DD Mmm 'YY] [name (their name)] [amount]  /  Receipt ↗  [address]
  let actMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  // Who a chain leg was with, in YOUR words first: nickname → the Drop label
  // you typed → their profile name → their M-link slug → short address.
  function activityWho(it: ActivityItem): string {
    // v1.0.6 — a Drop you made carries the name you typed for it, even when
    // this wallet already had another of your names (newest wins).
    const typed = it.dropId && it.kind === "drop_out" ? labelFor(it.dropId) : null;
    const mine = (typed && !isNewLabel(typed) ? typed : null) ?? readNick(it.counterpart);
    switch (it.kind) {
      case "fund":
        return "Add money";
      case "earn_in":
        return "Put to work";
      case "earn_out":
        return "Back to Cash";
      case "note_out":
      case "note_in":
        // v1.1.15 — a note sent / a note taken: the other side is the note
        // contract, which has no name. Say what it was (Worker 1.1.0).
        return "M® Note";
      case "swap_out":
      case "swap_in": {
        // v0.90.13 (0f) — a swap is a swap, not two rows against a contract
        // address. Both legs carry the pair, so either pocket reads the same:
        // `Swap · $5.00 → €4.45`, with this pocket's own leg as the amount.
        const sw = it.swap;
        if (!sw) return "Swap";
        const pair = `${formatFx(sw.from, Number(sw.fromAmount))} → ${formatFx(sw.to, Number(sw.toAmount))}`;
        return `Swap <span class="ct-theirs">${escapeHtml(pair)}</span>`;
      }
      default: {
        const own = mine || it.label || null;
        const theirs = it.code ? `<span class="ct-theirs">(${it.name ? `${escapeHtml(it.name)} ` : ""}${emojiFor(it.code, it.emoji)})</span>` : "";
        const isDrop = it.kind === "drop_out" || it.kind === "drop_back" || it.kind === "drop_in";
        // Nobody on the other side yet (unclaimed / taken back): say what it is, not the escrow's address.
        if (isDrop && !own && !it.name && !it.code) return it.kind === "drop_back" ? "Drop returned" : "Drop";
        const head = own ? escapeHtml(own) : it.name ? escapeHtml(it.name) : it.code ? escapeHtml(it.code) : escapeHtml(shortAddr(it.counterpart));
        const tag = it.kind === "drop_back" ? " · Drop returned" : ""; // v0.80.21 — no " · Drop" on a claimed Drop
        const bracket = !own && it.name && it.code ? `<span class="ct-theirs">(${emojiFor(it.code, it.emoji)})</span>` : theirs;
        return `${head} ${bracket}${tag}`;
      }
    }
  }
  let actCurrency = "USD"; // v0.80.39 — which pocket the statement shows
  function activitySheet() {
    actCurrency = activeCurrency();
    const s = openSheet(`
      <div class="act-head">
        <button id="actCcy" class="act-pill act-ccy" type="button" aria-label="Choose a currency"></button>
        <button id="actCal" class="act-pill" type="button" aria-label="Choose a month"></button>
      </div>
      <div class="act-bal" id="actBal"><span class="k">Current balance</span><b id="actBalV"></b></div>
      <div id="actPicker" class="act-picker" hidden></div>
      <div id="actList" class="act-list"><p class="sheet-note">Loading…</p></div>
    `);
    const picker = s.querySelector<HTMLElement>("#actPicker")!;
    const monthEl = s.querySelector<HTMLElement>("#actCal")!;
    const list = s.querySelector<HTMLElement>("#actList")!;
    const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const title = (m: string) => `${MON[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
    const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const pillTitle = (m: string) => `${MONTH_LONG[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

    function paintPicker() {
      const now = new Date();
      const year = Number(actMonth.slice(0, 4));
      const years: number[] = [];
      for (let y = now.getUTCFullYear(); y >= 2026; y--) years.push(y);
      picker.innerHTML = `
        <div class="act-year">
          <select id="actYear" aria-label="Year">${years.map((y) => `<option value="${y}" ${y === year ? "selected" : ""}>${y}</option>`).join("")}</select>
        </div>
        <div class="act-grid">${MON.map((mn, i) => {
          const key = `${year}-${String(i + 1).padStart(2, "0")}`;
          const future = new Date(Date.UTC(year, i, 1)) > now;
          return `<button class="act-m ${key === actMonth ? "on" : ""}" type="button" data-m="${key}" ${future ? "disabled" : ""}>${mn}</button>`;
        }).join("")}</div>`;
      picker.querySelector<HTMLSelectElement>("#actYear")!.addEventListener("change", (e) => {
        const y = (e.target as HTMLSelectElement).value;
        actMonth = `${y}-${actMonth.slice(5, 7)}`;
        paintPicker();
      });
      picker.querySelectorAll<HTMLButtonElement>(".act-m").forEach((b) =>
        b.addEventListener("click", () => {
          actMonth = b.dataset.m!;
          picker.hidden = true;
          void load();
        }),
      );
    }
    s.querySelector("#actCal")!.addEventListener("click", () => {
      picker.hidden = !picker.hidden;
      if (!picker.hidden) paintPicker();
    });
    // v0.80.39 — the currency pill: reads "EUR"; tap → the enabled pockets as
    // symbols; pick → the month re-reads for that pocket.
    const ccyEl = s.querySelector<HTMLElement>("#actCcy")!;
    ccyEl.addEventListener("click", () => {
      const open = s.querySelector(".act-ccy-menu");
      if (open) { open.remove(); return; }
      const menu = document.createElement("div");
      menu.className = "act-ccy-menu";
      menu.innerHTML = enabledCurrencies()
        .map((c) => `<button type="button" class="${c === actCurrency ? "on" : ""}" data-code="${c}">${currency(c).symbol.trim()}</button>`)
        .join("");
      menu.querySelectorAll<HTMLButtonElement>("button").forEach((b) =>
        b.addEventListener("click", () => {
          actCurrency = b.dataset.code!;
          menu.remove();
          void load();
        }),
      );
      s.querySelector(".act-head")!.appendChild(menu);
    });

    async function load() {
      monthEl.textContent = pillTitle(actMonth);
      ccyEl.textContent = actCurrency;
      // v1.1.27 — the top row: what this pocket holds now (Alexander, 5 Oct).
      s.querySelector<HTMLElement>("#actBalV")!.textContent = actCurrency === BASE_CURRENCY ? `$${formatCents(currentBalance)}` : formatFx(actCurrency, pocketBalance(actCurrency));
      list.innerHTML = `<p class="sheet-note">Loading…</p>`;
      let items: ActivityItem[];
      try {
        items = (await getActivity(address, actMonth, undefined, actCurrency)).items;
      } catch {
        list.innerHTML = `<p class="sheet-note">Couldn't reach Circle Arc just now. Try again in a moment.</p>`;
        return;
      }
      if (!items.length) {
        list.innerHTML = `<p class="sheet-note">Nothing in ${title(actMonth)}.</p>`;
        return;
      }
      list.innerHTML = items
        .map(
          (it) => `<div class="arow">
            <span class="ar-when">${formatDayNoYear(Date.parse(it.at))}</span>
            <span class="ar-who">${activityWho(it)}</span>
            <span class="ar-amt ${it.sign === "+" ? "in" : "out"}">${it.sign === "-" ? "–" : ""}${actCurrency === BASE_CURRENCY ? formatUsdShort(Number(it.amount)) : formatFx(actCurrency, Number(it.amount))}</span>
            ${noteRefCell(it) ?? `<span class="ar-ref">${it.ref ? escapeHtml(it.ref) : ""}</span>`}
            <span class="ar-right"><a class="st-link st-rcpt" href="${adapter.explorerUrl(it.hash)}" target="_blank" rel="noopener">Receipt ↗</a></span>
          </div>`,
        )
        .join("");
      void refreshNoteStates(); // v1.1.19
    }
    void load();
  }


  // 1.5.59 — the Timelock's stand-in (claude/timelock.md §6.8, §8). 1.5.60 (Alexander's Settings screen, 4 Nov 14:58):
  // Hold payments over [$1,000 · $5,000 · $10,000] for [12 · 24 · 48 · 72 hours]; Faster release (a switch: off is
  // "nobody", on lets a held payment go 2 hours after the Release Word — the M® Card comes with drop F); Turn off Timelock.
  // A weakening (a higher amount, a shorter hold, Faster release on, new words, off) waits the policy delay and the sheet
  // says when; anything else is at once (the Worker decides). Before it's on, the choices are kept here (tlDraft) and go
  // with Turn on Timelock — or with the words, which turn it on too.
  let tlDraft: { over: number; hold: number; fast: boolean } | null = null;
  function tlLabel(): string {
    const p = tlOnHere(address) ? tlView()?.policy : null;
    return !p || p.posture === "off" ? "Off" : "On";
  }
  function tlSay(e: unknown): string {
    const m = e instanceof Error ? e.message : "";
    return m === "words_same" || m.includes("words_same") ? "Choose two different words." : m === "words_empty" ? "Type both words." : "Couldn’t reach M. Try again.";
  }
  function timelockSheet(fresh = false) {
    if (fresh) tlDraft = null;
    const v = tlOnHere(address) ? tlView() : null;
    const pol = v?.policy && v.policy.posture !== "off" ? v.policy : null;
    const on = !!pol;
    const profile = pol?.profile ?? (tlTestFigures() ? "test" : "real");
    const opt = v?.options ?? TL_OPTIONS;
    const pend = pol ? pol.pending : null;
    const words = !!(v?.policy?.words || pend?.words);
    if (!tlDraft) tlDraft = { over: pend?.overUsd ?? pol?.overUsd ?? TL_DEFAULT.overUsd, hold: pend?.holdH ?? pol?.holdH ?? TL_DEFAULT.holdH, fast: (pend?.posture ?? pol?.posture) === "word" };
    const d = tlDraft;
    // On, what the sheet shows is what was chosen — waiting or not; the line under the heading says what waits.
    if (pol) { d.over = pend?.overUsd ?? pol.overUsd; d.hold = pend?.holdH ?? pol.holdH; if ((pend?.posture ?? pol.posture) === "word") d.fast = true; }
    const amt = (u: number) => tlAmountLabel(u, profile), hrs = (h: number) => tlHoursLabel(h, profile);
    let pendLine = "";
    if (pol && pend) {
      const parts: string[] = [];
      if (pend.posture !== pol.posture) parts.push(pend.posture === "off" ? "Turning off Timelock takes" : "Faster release takes");
      else if (pend.words) parts.push("Your new words take");
      if (pend.overUsd !== pol.overUsd) parts.push(`Holding payments over ${amt(pend.overUsd)} takes`);
      if (pend.holdH !== pol.holdH) parts.push(`Holding payments for ${hrs(pend.holdH)} takes`);
      if (pend.profile !== pol.profile) parts.push("The testing figures take");
      pendLine = `${parts.length === 1 ? parts[0] : "Your changes take"} effect at ${escapeHtml(whenTextOf(pend.at))}.`;
    }
    const sel = (id: string, label: string, list: number[], val: number, fmt: (n: number) => string) =>
      `<div class="tl-r"><span class="tl-k"><span>${label}</span></span><span class="tl-sel"><select id="${id}" aria-label="${label}">${list.map((n) => `<option value="${n}"${n === val ? " selected" : ""}>${fmt(n)}</option>`).join("")}</select></span></div>`;
    const s = openSheet(`
      <div class="tl-sheet">
        <div class="tl-head"><h3 class="sheet-title">Timelock</h3><span class="tl-shield${on ? " on" : ""}" role="img" aria-label="${on ? "On" : "Off"}">${IC_SHIELD_CHECK}</span></div>
        <p class="tl-sub">Protect your money by holding payments before they’re released.</p>
        ${pendLine ? `<p class="tl-pend" id="tlPend">${pendLine} <button class="linkish" id="tlUndo" type="button">Cancel the change</button></p>` : ""}
        ${sel("tlOver", "Hold payments over", opt.overUsd, d.over, amt)}
        <div class="tl-div"></div>
        ${sel("tlHold", "Hold payments for", opt.holdH, d.hold, hrs)}
        <div class="tl-div"></div>
        <button class="srow tl-fr" id="tlFast" type="button" role="switch" aria-checked="${d.fast}"><span class="tl-k"><span>Faster release</span><small>Release held payments after ${hrs(opt.releaseH)}.</small></span><span class="sw21" aria-hidden="true"><span class="sw21-k"></span></span></button>
        ${d.fast ? `<div class="tl-meth">
          <div class="tl-m"><span class="tl-k"><span>Release Word</span><small>${words ? "Both words are set" : "Set Release Word and Lock Word"}</small></span><button class="tl-pill" id="tlSet" type="button">${words ? "Change" : "Set"}</button></div>
          <div class="tl-m"><span class="tl-k"><span>M<sup>®</sup> Card</span><small>Add an M<sup>®</sup> Card</small></span><button class="tl-pill" type="button" disabled>Add</button></div>
        </div>
        ${words ? "" : `<p class="tl-hint">Set up at least one method to release payments early.</p>`}` : ""}
        <div class="tl-div"></div>
        ${on ? (pend?.posture === "off" ? "" : `<button class="srow tl-off" id="tlOff" type="button">Turn off Timelock<span class="chev">›</span></button>`) : `<button class="btn primary block" id="tlOn" type="button">Turn on Timelock</button>`}
        <p class="tl-msg" id="tlNote" aria-live="polite"></p>
      </div>
    `);
    const say = (x: string) => { const n = s.querySelector<HTMLElement>("#tlNote"); if (n) n.textContent = x; };
    const first = (): TlSetUp => ({ profile, overUsd: d.over, holdH: d.hold });
    const redraw = () => { if ($("#sheet").classList.contains("up") && $("#sheet").querySelector(".tl-sheet")) timelockSheet(); };
    const send = (pv: Promise<unknown>) => { say("Saving…"); void pv.then(redraw, (e) => { recordError("timelock/policy", e); say(tlSay(e)); }); };
    s.querySelector<HTMLSelectElement>("#tlOver")!.addEventListener("change", (e) => {
      const n = Number((e.target as HTMLSelectElement).value);
      if (!on) { d.over = n; return; }
      send(setChoice(address, { overUsd: n }));
    });
    s.querySelector<HTMLSelectElement>("#tlHold")!.addEventListener("change", (e) => {
      const n = Number((e.target as HTMLSelectElement).value);
      if (!on) { d.hold = n; return; }
      send(setChoice(address, { holdH: n }));
    });
    s.querySelector("#tlFast")!.addEventListener("click", () => {
      const want = !d.fast;
      d.fast = want;
      if (!on) { timelockSheet(); return; }
      if (want && !words) { timelockSheet(); return; } // the methods show; nothing to send until one is set
      const now = pend?.posture ?? pol!.posture;
      if (want === (now === "word")) { timelockSheet(); return; }
      send(setPosture(address, want ? "word" : "nobody"));
    });
    s.querySelector("#tlSet")?.addEventListener("click", () => wordsSheet());
    s.querySelector("#tlOn")?.addEventListener("click", () => send(setPosture(address, d.fast && v?.policy?.words ? "word" : "nobody", first())));
    s.querySelector("#tlOff")?.addEventListener("click", () => send(setPosture(address, "off")));
    s.querySelector("#tlUndo")?.addEventListener("click", () => { say("Saving…"); void cancelTimelockChange(address).then(() => tlRefresh()).then(() => { tlDraft = null; redraw(); }, (e) => { recordError("timelock/policy", e); say(tlSay(e)); }); });
  }
  const whenTextOf = (at: number) => { const d = new Date(at), n = new Date(tlNow()); const p2 = (x: number) => String(x).padStart(2, "0"); const t = `${p2(d.getHours())}:${p2(d.getMinutes())}`; return d.toDateString() === n.toDateString() ? t : `${t} on ${d.toLocaleDateString("en-GB", { weekday: "long" })}`; };
  /** 1.5.60 — the Release Word and the Lock Word (the codeword and the Mayday word until 1.5.59), typed in the clear
   *  (Alexander: masking adds mistakes and doesn't stop a hidden camera); capitals and spaces don't count (normalWord). */
  function wordsSheet() {
    const v = tlOnHere(address) ? tlView() : null;
    const on = !!(v?.policy && v.policy.posture !== "off");
    const f = (id: string, label: string) => `<input class="tl-field" id="${id}" type="text" autocomplete="off" autocorrect="off" autocapitalize="none" spellcheck="false" placeholder="${label}" aria-label="${label}">`;
    const s = openSheet(`
      <div class="tl-sheet">
        <h3 class="sheet-title">Release Word and Lock Word</h3>
        <p class="tl-sub">Your Release Word unlocks a payment early.<br>Your Lock Word secretly keeps it on hold.</p>
        ${f("tlW1", "Release Word")}
        ${f("tlW2", "Lock Word")}
        <p class="tl-msg" id="tlWMsg" aria-live="polite"></p>
        <button class="btn primary block" id="tlWSave" type="button">Save</button>
      </div>
    `);
    const msg = s.querySelector<HTMLElement>("#tlWMsg")!;
    const btn = s.querySelector<HTMLButtonElement>("#tlWSave")!;
    btn.addEventListener("click", () => {
      const w1 = s.querySelector<HTMLInputElement>("#tlW1")!.value, w2 = s.querySelector<HTMLInputElement>("#tlW2")!.value;
      btn.disabled = true; btn.textContent = "Saving…"; msg.textContent = "";
      const d = tlDraft ?? { over: TL_DEFAULT.overUsd, hold: TL_DEFAULT.holdH, fast: true };
      const firstUp: TlSetUp | undefined = on ? undefined : { profile: tlTestFigures() ? "test" : "real", overUsd: d.over, holdH: d.hold };
      void setWords(address, w1, w2, firstUp).then(
        () => { if (tlDraft) tlDraft.fast = true; timelockSheet(); },
        (e) => { recordError("timelock/words", e); btn.disabled = false; btn.textContent = "Save"; msg.textContent = tlSay(e); },
      );
    });
  }

  // 1.5.42 — the M® card sheet: not set → what it is and Set up (to the approver, back here with the credential);
  // set → the limit $X (a device setting until the rule lives on the account) and Remove.
  function cardSheet(justSet = false) {
    const mine = readCard()?.address.toLowerCase() === address.toLowerCase() ? readCard() : null;
    const s = openSheet(`
      <h3 class="sheet-title">M<sup>®</sup> card</h3>
      ${justSet && mine ? `<p class="card-set21" style="padding:0 20px 10px;margin:0;font-size:16px;font-weight:600;line-height:1.4;color:var(--cyan)">Your M<sup>®</sup> ${mine.kind} is set.</p>` : ""}
      <p class="sheet-note" style="padding:0 20px 12px;margin:0;font-size:14px;line-height:1.45;color:var(--muted)">${mine
        ? `Payments above the limit need your M<sup>®</sup> ${mine.kind} as well as your phone. The figures you confirm are shown by M's approver, read from the payment itself.`
        : `A second signature for larger payments, from an M<sup>®</sup> card or key you tap on the phone — a key that stays on the card, where no app can reach it.`}</p>
      ${mine ? `<label class="srow" style="justify-content:space-between"><span>Limit</span><span style="display:flex;align-items:center;gap:6px"><span class="muted">$</span><input id="cardAbove" type="number" inputmode="numeric" min="0" step="1" value="${cardAbove()}" style="width:92px;height:36px;border-radius:10px;border:1.5px solid var(--line);background:transparent;color:var(--text);font:600 16px/1 inherit;text-align:right;padding:0 10px"></span></label>
        <div class="srow-split"></div>
        <button class="srow" data-m="remove"><span class="si">${IC_CARD}</span>Remove this ${mine.kind}<span class="chev">›</span></button>`
        : `<button class="btn primary block" id="cardSetup" style="margin:8px 20px 16px">Set up my M<sup>®</sup> card</button>`}
    `);
    s.classList.add("compact");
    s.querySelector<HTMLInputElement>("#cardAbove")?.addEventListener("change", (e) => {
      const v = Math.max(0, Math.floor(Number((e.target as HTMLInputElement).value) || 0));
      setCardAbove(v); // 1.5.50 — synced with the wallet
    });
    s.querySelector("#cardSetup")?.addEventListener("click", () => {
      goToEnrol(address as `0x${string}`, cachedProfile(address)?.name?.trim() || name?.display || "My wallet", location.origin + "/");
    });
    s.querySelector('.srow[data-m="remove"]')?.addEventListener("click", () => { clearCard(); closeSheet(true); toast("Your M® card is removed."); });
  }

  function moreSheet() {
    const s = openSheet(`
      <h3 class="sheet-title">Settings</h3>
      <!-- 1.4.85 (Alexander, 22 Oct) — More is Settings: Appearance (the theme picker; the name is edited in Wallet),
           the mask, Lab, Help, Log out. Contacts and Activity live in Wallet. -->
      <button class="srow" data-m="appearance"><span class="si">${IC_APPEARANCE}</span>Mode<span class="chev chev-val">${themeLabel(getThemePref())}&nbsp;›</span></button>
      <!-- 1.4.67 (Alexander, 21 Oct) — the pill rests open; this turns the bullets on (synced, per wallet). -->
      <button class="srow srow-sw" data-m="mask" type="button" role="switch" aria-checked="${maskBalance()}"><span class="si">${IC_EYE_OFF}</span>Mask my balance<span class="sw21" aria-hidden="true"><span class="sw21-k"></span></span></button>
      <!-- 1.5.42 (Alexander, 30 Oct) — the M® card: a second signature above $X from a FIDO2 card or key, enrolled on the
           approver (its own origin; claude/multisig-card.md). Set up → the approver; Set → the limit (and remove). -->
      <button class="srow" data-m="card"><span class="si">${IC_CARD}</span>M® card<span class="chev chev-val">${readCard()?.address.toLowerCase() === address.toLowerCase() ? `above $${cardAbove().toLocaleString("en-US")}` : "Set up"}&nbsp;›</span></button>
      <!-- 1.5.59 (Alexander, 4 Nov: "Go") — the Timelock's stand-in (claude/timelock.md §6.8): who can make a held payment go
           sooner. Shown only where Lab's "Timelock: testing" is on, until the passkey signs its requests (§12 drop D). -->
      ${tlTestFigures() ? `<button class="srow" data-m="timelock"><span class="si tl-si">${IC_TIMELOCK}</span>Timelock<span class="chev chev-val">${tlLabel()}&nbsp;›</span></button>` : ""}
      <button class="srow" data-m="lab"><span class="si">${IC_LAB}</span>Lab<span class="chev">›</span></button>
      <button class="srow disabled"><span class="si">${IC_HELP}</span>Help<span class="chev soon">soon</span></button>
      <button class="srow" data-m="fly" hidden><span class="si">${IC_OMEGA}</span>Omega Air<span class="chev soon">demo</span></button>
      <button class="srow" data-m="cafe" hidden><span class="si">${IC_CAFE}</span>Ondas Coffee<span class="chev soon">demo</span></button>
      <div class="srow-split"></div>
      <button class="srow" data-m="logout"><span class="si">${IC_LOGOUT}</span>Log out<span class="chev">›</span></button>
    `);
    s.classList.add("compact"); // 1.4.85 — Wallet's 44 px rows
    s.querySelector('.srow[data-m="appearance"]')!.addEventListener("click", () => {
      closeSheet(true);
      setTimeout(() => { appearanceSheet(); setActiveDock("more"); }, 210);
    });
    // 1.4.67 — the switch flips in place (the sheet stays open); the pill behind it follows at once.
    const maskRow = s.querySelector<HTMLButtonElement>('.srow[data-m="mask"]')!;
    maskRow.addEventListener("click", () => {
      const on = !maskBalance();
      setMaskBalance(on);
      maskRow.setAttribute("aria-checked", String(on));
      paintNucPill();
    });
    s.querySelector('.srow[data-m="card"]')!.addEventListener("click", () => {
      closeSheet(true);
      setTimeout(() => cardSheet(), 210);
    });
    s.querySelector('.srow[data-m="timelock"]')?.addEventListener("click", () => {
      closeSheet(true);
      setTimeout(() => timelockSheet(true), 210);
    });
    s.querySelector('.srow[data-m="lab"]')!.addEventListener("click", () => {
      closeSheet(true);
      setTimeout(() => labSheet(() => { closePopup(); setActiveDock(totalUsd() > 0 ? "sr" : ""); }), 210); // 1.4.90 — Lab's ✕ closes, home lit (1.4.86–1.4.89: back to Settings)
    });
    s.querySelector('.srow[data-m="logout"]')!.addEventListener("click", () => {
      closeSheet();
      setTimeout(onExit, 210);
    });
    // v0.99.0 — "Pay with M®": the Omega Air checkout demo. The row exists only
    // while the Worker says the demo is on (cached 5 min), so the beta build
    // ships with it absent until demo day. Tap → a gate token for this wallet →
    // the airline's site, same tab.
    const fly = s.querySelector<HTMLButtonElement>('.srow[data-m="fly"]')!;
    void demoOn().then((on) => { if (on) fly.removeAttribute("hidden"); });
    // 1.5.17 — Ondas Coffee (app/public/cafe/) has no gate (Worker 1.4.16): the row shows whenever the Worker names the
    // café, and opens it in a new tab like the airline. On a computer the café shows M's QR to pay with the phone.
    const cafe = s.querySelector<HTMLButtonElement>('.srow[data-m="cafe"]')!;
    void getDemoConfig().then((c) => { if (c.cafe) cafe.removeAttribute("hidden"); }).catch(() => {});
    cafe.addEventListener("click", () => { if (!window.open("/cafe/", "_blank")) location.href = "/cafe/"; });
    // v0.99.5 — a new tab, so the wallet stays open behind the shop (Recent
    // transactions). iOS only allows a new tab from inside the tap itself, so
    // the tab is opened blank at once and pointed at the site when the token
    // arrives; if the browser refused the tab, the same tab is used.
    fly.addEventListener("click", async () => {
      fly.disabled = true;
      const tab = window.open("", "_blank");
      try {
        const { url } = await demoToken(address);
        if (tab) tab.location.href = url;
        else location.href = url;
      } catch (e) {
        if (tab) tab.close();
        recordError("demo/token", e);
        toast("Couldn't open the demo. Try again.", "alert");
      } finally {
        fly.disabled = false;
      }
    });
  }

  let demoOnCache: { at: number; on: boolean } | null = null;
  async function demoOn(): Promise<boolean> {
    if (demoOnCache && Date.now() - demoOnCache.at < 5 * 60_000) return demoOnCache.on;
    const on = await getDemoConfig().then((c) => !!c.enabled).catch(() => false);
    demoOnCache = { at: Date.now(), on };
    return on;
  }

  // v0.70.10 — edit your own name (the one others see) right from the wallet sheet.
  function myNameSheet() {
    const s = openSheet(`
      <h3 class="sheet-title">Your name on M®</h3>
      <input id="myName" class="nick-input" type="text" placeholder="Optional — e.g. Bob Whitfield" maxlength="40" autocomplete="name" />
      <p class="sheet-note">Shown to people you pay and who pay you. Never public, never searchable.</p>
      <button id="myNameSave" class="btn primary block">Save</button>
    `);
    const input = s.querySelector<HTMLInputElement>("#myName")!;
    let was: string | null = null; // the name as the sheet opened
    getProfile(address).then((p) => (input.value = was = p.name ?? "")).catch(() => {});
    setTimeout(() => input.focus(), 80);
    let steered = false;
    const back = () => closeSheet(); // 1.4.90 — done: home (1.4.86–1.4.89: back to Wallet)
    const save = () => {
      if (steered) return back(); // the button now reads Done
      const v = input.value.trim();
      // v1.3.0 — a changed name: the passkey keeps its own, so the sheet stays to
      // say where to change it (the note says "Saved", so no toast over the button).
      const steer = !!v && !!was && v !== was;
      setProfile({ address, name: v || null })
        .then(async () => {
          // 1.5.44 — the passkey follows the new name where the browser lets M tell it.
          const followed = v ? await tellPasskeyName(address, v) : false;
          if (!steer) return toast(v ? "Name saved" : "Name removed");
          steered = true; // only once it is saved: a failed save leaves Save to try again
          was = v;
          s.querySelector<HTMLElement>(".sheet-note")!.textContent = followed ? PASSKEY_FOLLOWS : PASSKEY_STEER;
          s.querySelector<HTMLElement>("#myNameSave")!.textContent = "Done";
        })
        .catch(() => toast("Couldn't save the name"));
      if (steer) { input.blur(); return; }
      back();
    };
    s.querySelector("#myNameSave")!.addEventListener("click", save);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") save();
    });
  }

  // v0.70.10 — a person with two of your names (a nickname AND a Drop label):
  // settle on one master name, which is then both the nickname and the alias.
  function resolveNamesSheet(target: string, names: string[]) {
    const combined = names.join(" ");
    const s = openSheet(`
      <h3 class="sheet-title">Two names, one person</h3>
      <p class="sheet-note">You've called them ${names.map((n) => `“${escapeHtml(n)}”`).join(" and ")}. Keep one, or join them — then there's a single name to type.</p>
      <div class="rn-opts">
        ${names.map((n) => `<button class="btn ghost pill rn-opt" type="button" data-v="${escapeHtml(n)}">Keep “${escapeHtml(n)}”</button>`).join("")}
        <button class="btn ghost pill rn-opt" type="button" data-v="${escapeHtml(combined)}">Join: “${escapeHtml(combined)}”</button>
      </div>
      <div class="sr-to rn-field"><span class="sr-to-field">
        <input id="rnName" class="rn-in" type="text" maxlength="40" value="${escapeHtml(names[0])}" autocapitalize="words" />
        <button id="rnSave" class="sr-to-next" type="button" aria-label="Save">${IC_TICK_CIRCLE}</button>
      </span></div>
    `);
    const input = s.querySelector<HTMLInputElement>("#rnName")!;
    s.querySelectorAll<HTMLButtonElement>(".rn-opt").forEach((b) =>
      b.addEventListener("click", () => {
        input.value = b.dataset.v ?? "";
        input.focus();
      }),
    );
    const save = () => {
      const v = input.value.trim().slice(0, 40);
      if (!v) return;
      setNick(target, v);
      clearAlso(target); // one master name, on this device
      closeSheet();
      toast(`Now just “${v}”`);
      setTimeout(contactsSheet, 210);
    };
    s.querySelector("#rnSave")!.addEventListener("click", save);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") save();
    });
  }

  // v0.70.8 — Contacts: your book. Your name for each person (nickname, or the
  // label you used for their Drop), then their profile in brackets. Merged from
  // device nicknames, server-side Drop aliases and Recents; tap = edit the name.
  async function contactsSheet() {
    const s = openSheet(`
      <h3 class="sheet-title">Contacts</h3>
      <!-- 1.4.85 (Alexander, 22 Oct) — Invite friends is the first row of Contacts (it was in Wallet). -->
      <button class="srow ct-invite" data-c="invite" type="button"><span class="si">${IC_INVITE}</span>Invite friends<span class="chev">›</span></button>
      <div class="srow-split"></div>
      <p class="sheet-note">Your names for people. Only you see them — they live on this device.</p>
      <div id="ctList" class="ct-list"><p class="sheet-note">Loading…</p></div>
    `);
    s.querySelector('.srow[data-c="invite"]')!.addEventListener("click", () => {
      closeSheet(true);
      setTimeout(() => { void invitesSheet(); setActiveDock("wallet"); }, 190);
    });
    const list = s.querySelector<HTMLElement>("#ctList")!;
    // v0.70.12 — device-only: nicknames + Recents (+ "also" names from Drops).
    // Their public profile (name + emoji) is looked up per row.
    type Row = { address: string; mine: string; also: string[]; code: string | null; name: string | null; emoji: string | null };
    const byAddr = new Map<string, Row>();
    for (const r of readRecent()) {
      byAddr.set(r.address.toLowerCase(), { address: r.address, mine: "", also: [], code: r.display ?? null, name: null, emoji: null });
    }
    for (const n of listNicks()) {
      const k = n.address.toLowerCase();
      const e = byAddr.get(k);
      if (e) e.mine = n.name;
      else byAddr.set(k, { address: n.address, mine: n.name, also: [], code: null, name: null, emoji: null });
    }
    for (const e of byAddr.values()) e.also = alsoNames(e.address).filter((x) => x.toLowerCase() !== e.mine.toLowerCase());
    const rows = [...byAddr.values()].filter((r) => r.address.toLowerCase() !== address.toLowerCase());
    if (!rows.length) {
      list.innerHTML = `<p class="sheet-note">Nobody yet. The first Drop you release, or a nickname you give, lands here.</p>`;
      return;
    }
    await Promise.all(
      rows.map(async (r) => {
        try {
          const p = await getProfile(r.address);
          rememberProfile(r.address, p); // v0.70.18 — feeds contactLabel() everywhere
          r.code = p.code ?? r.code;
          r.name = p.name;
          r.emoji = p.emoji;
        } catch {
          const c = cachedProfile(r.address);
          if (c) {
            r.code = c.code ?? r.code;
            r.name = c.name;
            r.emoji = c.emoji;
          }
        }
      }),
    );
    rows.sort((a, b) => (a.mine || a.name || a.code || "").localeCompare(b.mine || b.name || b.code || ""));
    list.innerHTML = rows
      .map((r) => {
        const theirs = r.code ? `<span class="ct-theirs">(${r.name ? `${escapeHtml(r.name)} ` : ""}${emojiFor(r.code, r.emoji)})</span>` : "";
        const mine = r.mine
          ? escapeHtml(r.mine)
          : r.name
            ? `<span class="ct-unnamed">${escapeHtml(r.name)}</span>`
            : r.code
              ? `<span class="ct-unnamed">${escapeHtml(r.code)}</span>`
              : `<span class="ct-unnamed">${escapeHtml(shortAddr(r.address))}</span>`;
        const conflict = r.also.length > 0 && !!r.mine;
        const also = conflict ? `<span class="ct-also">also ${r.also.map(escapeHtml).join(", ")}</span>` : "";
        const shownTheirs = !r.mine && r.name && r.code ? `<span class="ct-theirs">(${emojiFor(r.code, r.emoji)})</span>` : theirs;
        return `<button class="ct-row" type="button" data-a="${escapeHtml(r.address)}" data-c="${escapeHtml(r.code ?? "")}" data-names="${escapeHtml(JSON.stringify(conflict ? [r.mine, ...r.also] : []))}">
          <span class="ct-who">${mine} ${shownTheirs}${also}</span>
          <span class="${conflict ? "ct-alert" : "ct-pencil"}">${conflict ? IC_ALERT : PENCIL}</span>
        </button>`;
      })
      .join("");
    list.querySelectorAll<HTMLButtonElement>(".ct-row").forEach((b) => {
      b.addEventListener("click", () => {
        let names: string[] = [];
        try {
          names = JSON.parse(b.dataset.names || "[]");
        } catch {
          names = [];
        }
        closeSheet();
        if (names.length > 1) {
          setTimeout(() => resolveNamesSheet(b.dataset.a!, names), 210);
          return;
        }
        setTimeout(
          () =>
            openNameEditor({
              address: b.dataset.a!,
              code: b.dataset.c || null,
              onDone: () => setTimeout(contactsSheet, 210),
              onClose: () => setTimeout(contactsSheet, 210),
            }),
          210,
        );
      });
    });
  }

  function emojiPickerSheet() {
    const chosen = getEmoji(display);
    const cells = EMOJI_SET.map(
      (e) =>
        `<button class="emoji-cell${e === chosen ? " on" : ""}" data-e="${e}" type="button">${e}</button>`,
    ).join("");
    const s = openSheet(`
      <h3 class="sheet-title">Your emoji</h3>
      <p class="sheet-note">A friendly mark for this wallet — the people you pay see it too.</p>
      <div class="emoji-grid">${cells}</div>
    `);
    s.querySelector(".emoji-cell.on")?.scrollIntoView({ block: "center" }); // v0.60.18 — 60 cells; start on the current one
    s.querySelectorAll<HTMLButtonElement>(".emoji-cell").forEach((cell) => {
      cell.addEventListener("click", () => {
        setEmoji(cell.dataset.e!);
        setProfile({ address, emoji: cell.dataset.e! }).catch(() => {}); // v0.70 — others see it too
        closeSheet();
        setTimeout(walletSheet, 210);
      });
    });
  }

  // 1.4.85 — the Settings list's Appearance row: the theme picker alone (the name is edited from Wallet's header).
  function appearanceSheet() {
    const MOON = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/></svg>`;
    const SUN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4"/></svg>`;
    const AUTO = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none"/></svg>`;
    const cur = getThemePref();
    const s = openSheet(`
      <h3 class="sheet-title">Mode</h3>
      <div class="set-row set-col">
        <div class="thseg" data-on="${cur}" role="group" aria-label="Mode">
          <span class="thseg-thumb"></span>
          <button class="thseg-opt" data-theme="light" type="button">${SUN}Pure</button>
          <button class="thseg-opt" data-theme="dark" type="button">${MOON}Midnight</button>
          <button class="thseg-opt" data-theme="auto" type="button">${AUTO}Auto</button>
        </div>
      </div>
      <p class="sheet-note">Choose how M looks. Auto follows your phone's light/dark setting. Saved on this device.</p>
    `);
    const seg = s.querySelector<HTMLElement>(".thseg")!;
    seg.querySelectorAll<HTMLButtonElement>(".thseg-opt").forEach((btn) => {
      btn.addEventListener("click", () => {
        const t = btn.dataset.theme as ThemePref;
        setTheme(t);
        seg.dataset.on = t;
      });
    });
  }

  // v0.90.19 (0h) — the M-FX ops readout, in Lab, for the master wallet only.
  // Buffer positions are M's operational data: public on-chain, but not ours
  // to volunteer — so the block renders only when the logged-in wallet is the
  // designated ops wallet. Nothing here is secret, so no auth beyond that.
  const OPS_WALLETS = ["0xf7ac04b819ae2659125c00902384d9fa30ece3de"];
  const isOpsWallet = () => OPS_WALLETS.includes(address.toLowerCase());
  async function paintOps(el: HTMLElement) {
    const api = import.meta.env.VITE_WORKER_URL ?? "";
    const row = (k: string, v: string) => `<div class="ld-row"><span>${escapeHtml(k)}</span><code>${escapeHtml(v)}</code></div>`;
    try {
      const [cfg, usage, rate] = await Promise.all([
        fetch(`${api}/fx/config`).then((r) => r.json()) as Promise<{
          configured: boolean; enabled: boolean; contract: string | null; currencies: string[];
          maxSwaps: number; maxAmount: number; spreadBps: number; rateSource: string;
          buffers: Record<string, string> | null; paused: boolean | null; bufferError?: string;
        }>,
        fetch(`${api}/fx/usage/${address}`).then((r) => r.json()) as Promise<{ used: number; limit: number; remaining: number | null }>,
        fetch(`${api}/fx/rate?from=USD&to=EUR`).then((r) => r.json()).catch(() => null) as Promise<{ rate?: number; source?: string; refRate?: number } | null>,
      ]);
      const buf = cfg.buffers
        ? Object.entries(cfg.buffers).map(([c, v]) => formatFx(c, Number(v))).join(" · ")
        : `— (${cfg.bufferError ?? "unreadable"})`;
      el.innerHTML =
        row("Buffer", buf) +
        row("MFx", cfg.contract ? shortAddr(cfg.contract) : "not configured") +
        row("State", `${cfg.enabled ? "on" : "OFF"} · ${cfg.paused === null ? "?" : cfg.paused ? "PAUSED" : "open"} · ${cfg.rateSource}`) +
        row("Rate", rate?.rate ? `$1 = €${rate.rate.toFixed(4)} (ref ${rate.refRate?.toFixed(4) ?? "–"}, ${rate.source ?? "?"})` : "–") +
        row("Limits", `${cfg.maxAmount > 0 ? "$" + cfg.maxAmount + " per swap" : "no cap"} · ${cfg.maxSwaps > 0 ? cfg.maxSwaps + " per wallet" : "unlimited"} · ${cfg.spreadBps} bp`) +
        row("This wallet", `${usage.used} used${usage.remaining == null ? "" : ` · ${usage.remaining} left`}`);
    } catch (e) {
      el.innerHTML = row("Ops", `couldn't read: ${(e as Error).message}`);
    }
  }

  function labSheet(back?: () => void) {
    const s = openPopup(`
      <h3 class="sheet-title">Lab <span class="tag">experimental</span></h3>
      <p class="sheet-note">A back room for developers and testers.</p>
      <div class="lab-diag">
        <div class="ld-row"><span>Address</span><code>${escapeHtml(shortAddr(address))}</code></div>
        <div class="ld-row"><span>Network</span><code>Circle Arc · testnet</code></div>
        <div class="ld-row"><span>App</span><code>${APP_VERSION}${liveVersion() ? ` · live ${escapeHtml(liveVersion()!)}` : ""}</code></div>
        <div class="ld-row"><span>Worker</span><code>${lastTiming.worker ?? "– (deploy?)"}</code></div>
        <div class="ld-row"><span>Balance panel</span><code>drops ${lastTiming.drops ?? "–"} ms · recent ${lastTiming.recent ?? "–"} ms (explorer ${lastTiming.explorer ?? "–"} ms, ${lastTiming.pages ?? "–"} pg, ${lastTiming.months ?? "–"} mo)</code></div>
        <div class="ld-row"><span>Statement</span><code>explorer ${lastTiming.explorerState ?? "–"} · own ${lastTiming.own ?? "–"}</code></div>
        ${lastMeasures().map((m) => `<div class="ld-row"><span>${escapeHtml(m.scope)}</span><code>${escapeHtml(m.lines[0] ?? "")} · ${escapeHtml(m.at.slice(11, 19))}</code></div>`).join("")}
      </div>
      <button class="btn ghost block" id="labSpare" style="margin-bottom:16px">Spare key (drill)</button>
      <button class="srow srow-sw" id="labTl" type="button" role="switch" aria-checked="${tlTestFigures()}"><span class="si tl-si">${IC_TIMELOCK}</span>Timelock: testing<span class="sw21" aria-hidden="true"><span class="sw21-k"></span></span></button>
      <p class="sheet-note small" style="margin:4px 0 14px">Shows Settings → Timelock here, with the testing figures: an hour is a minute and a dollar a cent (payments over $10 held for 48 minutes, 2 minutes after the Release Word). For testing only until the passkey signs the Timelock's requests.</p>
      ${
        lastErrors().length
          ? `<div class="sheet-title" style="margin-top:14px">Last errors</div>
             <div class="lab-errs">${lastErrors()
               .map(
                 (d) =>
                   `<div class="lab-err"><b>${escapeHtml(d.scope)}</b> <span class="muted">${escapeHtml(d.at.slice(11, 19))}</span>${d.lines
                     .map((l, i) => `<div class="lab-errl">${"↳ ".repeat(Math.min(i, 3))}${escapeHtml(l)}</div>`)
                     .join("")}</div>`,
               )
               .join("")}</div>`
          : `<p class="sheet-note small" style="margin-top:12px">No errors recorded this session.</p>`
      }
      ${isOpsWallet() ? `<div class="sheet-title" style="margin-top:14px">Ops · M-FX</div><div class="lab-diag" id="labOps"><div class="ld-row"><span>Buffer</span><code>reading…</code></div></div>` : ""}
    `, back); // 1.4.86 — from Settings: its ✕ goes back there
    const ops = s.querySelector<HTMLElement>("#labOps");
    if (ops) void paintOps(ops);
    // 1.5.59 — the Timelock's testing figures: this device's choice for a set-up; a wallet already set up asks the Worker
    const tlSw = s.querySelector<HTMLButtonElement>("#labTl")!;
    tlSw.addEventListener("click", () => {
      const on = !tlTestFigures();
      setTlTestFigures(on);
      tlSw.setAttribute("aria-checked", String(on));
      const p = tlOnHere(address) ? tlView()?.policy : null;
      if (p && p.posture !== "off") void setTimelockPolicy({ address, profile: on ? "test" : "real" }).then(() => tlRefresh(), (e) => recordError("timelock/profile", e)); // 1.5.60: the profile alone
    });
    // v1.3.10 — the spare key drill (src/sparekey/lab.ts), loaded on demand; ✕ comes back to Lab.
    s.querySelector("#labSpare")!.addEventListener("click", () => {
      void import("../sparekey/lab").then(
        (m) => m.openSpareKeyLab({ host: openPopup("", labSheet), adapter, address, toast }),
        (e) => { recordError("sparekey/load", e); toast("Couldn't open it. Try again.", "alert"); },
      );
    });
  }

  function deleteSheet() {
    const balWarn =
      currentBalance > 0
        ? `<p class="warn">This wallet holds ${formatUsdc(String(currentBalance))} USD. Deleting does <b>not</b> move money — send your balance out first.</p>`
        : "";
    const s = openSheet(`
      <h3 class="sheet-title">Delete this wallet?</h3>
      ${balWarn}
      <p class="sheet-note">M forgets this wallet's name and history. Your funds stay on-chain and the passkey stays on your device — remove that in Settings to delete it for good.</p>
      <button id="delGo" class="btn danger block">Delete with Face ID</button>
      <p id="delStatus" class="status"></p>
    `);
    s.querySelector("#delGo")!.addEventListener("click", async () => {
      const dstat = s.querySelector<HTMLElement>("#delStatus")!;
      dstat.textContent = "Confirm with Face ID…";
      try {
        const conn = await adapter.createOrConnect("login");
        if (conn.address.toLowerCase() !== address.toLowerCase()) {
          dstat.textContent = "That passkey unlocks a different wallet.";
          return;
        }
        await deleteWallet(address);
        clearFlag();
        clearCredential();
        s.innerHTML = `
          <h3 class="sheet-title">Removed from M.</h3>
          <p class="sheet-note">To fully delete this wallet, remove its passkey:</p>
          <p class="steps">iPhone: <b>Settings → Passwords → globalmoneyapp.com</b><br/>Mac: <b>Settings → Passwords → globalmoneyapp.com</b></p>
          <button id="delDone" class="btn primary block">Done</button>`;
        s.querySelector("#delDone")!.addEventListener("click", onExit);
      } catch (e) {
        console.error(e);
        dstat.textContent = "Couldn't confirm. Please try again.";
      }
    });
  }

  // ===================== DOCK WIRING =======================================
  // Five equal glyphs (Wallet · Add · Send&Request · Grow · More), shown only
  // once funded. Sheets rise behind the bar; the tapped icon lights (filled +
  // indigo + larger) while its sheet is open. Send&Request is the keypad home,
  // so it's lit at rest; tapping a lit sheet icon closes it; tapping another
  // swaps straight across. Home (greeting/nucleus) is the M-logo tap.
  const DOCK_GLYPH: Record<string, { out: string; fill?: string }> = {
    wallet: { out: IC_WALLET },
    more: { out: IC_GEAR }, // 1.4.85 — Settings; 1.4.94 — Home ($) and Grow left the dock
  };
  let activeDock = "";
  function setActiveDock(key: string) {
    activeDock = key;
    root
      .querySelectorAll<HTMLButtonElement>(".dock [data-dock]")
      .forEach((btn) => {
        const k = btn.dataset.dock!;
        const on = k === key;
        btn.classList.toggle("on", on);
        const g = DOCK_GLYPH[k];
        if (g) {
          const holder = btn.querySelector<HTMLElement>(".ic") ?? btn;
          holder.innerHTML = g.out; // active = same outline glyph, indigo
        }
      });
    // 1.4.94 — no sliding highlight (v1.0.16–1.4.93, #dockPill): a lit tab lights its whole shape (home21.css, :has(.on))
  }

  root.querySelectorAll<HTMLButtonElement>(".dock [data-dock]").forEach((tab) => {
    tab.addEventListener("click", () => {
      const which = tab.dataset.dock!;
      // v1.1.7 — the dock is navigation: it leaves whatever popup is open
      // (Swap, the Balance panel, QR, Lab). The dock sits above popups (65 vs
      // 50), so it got the tap, but the popup stayed on top of the result.
      if (!$("#popup").hasAttribute("hidden")) closePopup();
      setDockMenu(false);
      // Tapping the already-lit sheet icon closes it (closeSheet resets to sr). 1.4.94 — the centre (Home) and Grow left the dock.
      if (which === activeDock) {
        closeSheet();
        return;
      }
      if (which === "wallet") walletSheet();
      else if (which === "more") moreSheet();
      setActiveDock(which); // light the tapped icon; its sheet hangs from it
    });
  });

  // ===================== M LOGO: tap → Activity, hold → Earn (v0.60.2 S1) ==
  // Lab moved to the More sheet; the hold now opens the "Put your money to
  // work" slider so Cash⇄Earning is one gesture from the home screen.
  // v0.70.11 — the logo is tap-only (Activity). The hold moved to the Grow dock
  // glyph: press & hold opens Cash ⇄ Earning; a plain tap does nothing (the
  // pocket isn't live for everyone yet).
  // v0.90.14 — the strip's globe opens the currencies list (the one that was
  // behind the flag). Activity lives in More and, from this drop, in Wallet.
  // v1.1.25 — the globe is a toggle, lit when on: the money pills become your
  // pockets (Swap at the end of the row).
  // 1.4.17 (Alexander, 16 Oct) — the globe is a plain toggle: its press-and-hold (the Currencies panel,
  // v1.1.25–1.4.16) is gone; the pockets live under the Balance pill now (1.4.15). `pocketsPanel()` stays
  // in the file, unreferenced, in case it is wanted back.
  {
    const g = $<HTMLButtonElement>("#stripGlobe");
    g.addEventListener("contextmenu", (e) => e.preventDefault());
    g.addEventListener("click", () => {
      if (!$("#popup").hasAttribute("hidden")) closePopup();
      setGlobal(!globalOn);
    });
  }

  // 1.4.94 — Grow's press-and-hold (v0.70.11–1.4.93) went with the Grow tab; Invest is the pill menu's placeholder.

  _liveRefresh = refreshBalance;
  bindForegroundRefresh();
  // v1.0.6 — the pill hidden after coming back to the tab (Chrome, 23 and 28
  // Sep). "lifted" (the To field's focus lift — it collapses the pill's slot)
  // and the body's keyboard lock are cleared on blur; iOS Chrome can drop a
  // field's focus while the tab is in the background without firing blur, so
  // both stuck. Reconcile them with the real focus whenever the page returns.
  const unstick = () => {
    const a = document.activeElement as HTMLElement | null;
    if (a?.id !== "srTo") root.classList.remove("lifted");
    if (!(a instanceof HTMLInputElement)) document.body.classList.remove("kb-lock");
  };
  document.addEventListener("visibilitychange", () => { if (!document.hidden) unstick(); });
  window.addEventListener("pageshow", unstick);
  window.addEventListener("focus", unstick);
  applyHomeState(); // 1.4.20 — the home paints at once, whatever the last read said; the read reconciles
  refreshBalance();
}

// ---- helpers -------------------------------------------------------------
function shortAddr(a: string) {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}
function formatUsdc(v: string) {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
function escapeHtml(s: string) {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );
}
